import { AttachmentService } from 'src/app/services/attachment.service';
import { Component, OnInit, ChangeDetectorRef, OnDestroy, AfterViewInit, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ITreatmentPlanDetailed, ITreatmentPlanPhoto, ITreatmentPlanProduct, IProductPhoto } from 'src/app/models/treatment-plan.model';
import { TreatmentPlanService } from 'src/app/services/treatment-plan.service';
import { PatientsService } from 'src/app/services/patients.service';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { debounceTime, delay, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';
import { faCross, faExclamationTriangle, faPlus, faQuestion, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthService } from 'src/app/services/auth.service';
import { CanComponentDeactivate } from 'src/app/guards/unsaved-changes.guard';
import { PhotoCaptureService, MultiplePhotoCaptureResult, PhotoCaptureResult } from './photo-capture.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IncidentService } from 'src/app/services/incident.service';
import { IIncidentAnswer, IIncidentReport, IIncidentSection } from 'src/app/models/incident.model';
import { ProductService, IApplicationRegionCategory } from 'src/app/services/product.service';
import { environment } from 'src/environments/environment';
import { QrLinkService } from 'src/app/services/qr-link.service';
import { unwrapError } from 'src/app/utils/log-error';

type FrontendPhotoType = 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST';
type BackendPhotoType = 'PRE_TREATMENT' | 'POST_TREATMENT' | 'MARKING' | 'IMMEDIATE_POST_TREATMENT';

function assertNeverPhotoType(type: never): never {
  throw new Error(`Unhandled photo type: ${type}`);
}

@Component({
  selector: 'app-treatment-plan',
  templateUrl: './treatment-plan.component.html',
  styleUrls: ['./treatment-plan.component.sass']
})
export class TreatmentPlanComponent implements OnInit, OnDestroy, CanComponentDeactivate, AfterViewInit {
  treatmentPlanId: string = '';
  treatmentPlan!: ITreatmentPlanDetailed;
  loading = true;
  saving = false;
  showSavingIndicator = false;
  sendingEmail = false; // Controle de envio de PDF por email
  downloadingPdf = false; // Controle de download do PDF (gerado no backend)

  // Modal de fases exibido durante o encerramento do plano
  showPublishingModal = false;
  publishingPhase: 'saving' | 'closing' | 'generating' | 'completed' = 'saving';

  // Indica se o paciente é modelo (usado na tela e na impressão)
  isModelPatient: boolean = false;

  faAdd = faPlus;
  faTrash = faTrash;
  faQuestion = faQuestion;
  faExclamationTriangle = faExclamationTriangle;

  treatmentPlanForm!: FormGroup;

  private photoSyncTimeout: any = null;
  private photoSyncDebounceTime = 3000; // 3 segundos de espera
  private pendingPhotoSyncs = new Set<FrontendPhotoType>();

  /** Converte tipo frontend de foto para tipo backend */
  private toBackendPhotoType(type: FrontendPhotoType): BackendPhotoType {
    switch (type) {
      case 'PRE': return 'PRE_TREATMENT';
      case 'MARKING': return 'MARKING';
      case 'IMMEDIATE_POST': return 'IMMEDIATE_POST_TREATMENT';
      case 'POST': return 'POST_TREATMENT';
      default: return assertNeverPhotoType(type);
    }
  }

  /** Verifica se um tipo de foto pertence ao tipo frontend indicado */
  private isPhotoOfType(photoType: string, frontendType: FrontendPhotoType): boolean {
    return photoType === frontendType || photoType === this.toBackendPhotoType(frontendType);
  }

  basePhotoUrl: string = 'https://plan-trat-imgs-bucket.s3.us-east-1.amazonaws.com/';

  // Controle de validação de campos
  touchedFields: {
    manufacturer: boolean;
    application_region: boolean;
    application_date: boolean;
    application_regions: Array<{
      id: string;
      region: boolean;
    }>;
  } = {
      manufacturer: false,
      application_region: false,
      application_date: false,
      application_regions: []
    };

  // Controle de abas
  activeTab: 'info' | 'products' | 'photos' | 'intercorrencias' = 'info';

  // Controle de intercorrências
  hasClinicQuestions = false;
  incidentSections: IIncidentSection[] = [];
  intercorrencias: IIncidentReport[] = [];
  loadingIntercorrencias = false;
  savingIncident = false;
  finalizingIncident = false;
  showIncidentForm = false;
  editingIncidentId: string | null = null;
  editingIncidentStatus: 'ACTIVE' | 'CLOSED' | 'DELETED' = 'ACTIVE';
  editingIncidentAnswers: IIncidentAnswer[] = [];
  editingIncidentAttachments: any[] = [];
  incidentFixedValues: Record<string, string> = {};
  incidentDynamicOptions: Record<string, string[]> = {};
  incidentRegionSelectorConfig: Record<string, any> = {};

  // Controle de upload de fotos
  selectedPhotoType: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST' = 'PRE';
  photoDescription: string = ''; // Valor padrão vazio, não utilizado pelo backend
  selectedPhotoFile: File | null = null;
  uploadingPhoto = false;
  processingHeic = false; // Loading para conversão HEIC

  // Controle de produtos
  newProduct: ITreatmentPlanProduct = {
    manufacturer: '',
    application_date: '',
    application_regions: [],
    product_name: '',
  };
  addingProduct = false;
  updatingProduct = false;
  editingProductId: string | null = null;

  // Controle de upload de fotos de produtos
  selectedProductId: string | null = null;
  selectedProductPhotoType: 'bottle' | 'box' | 'label' | null = null;
  selectedProductPhotoFile: File | null = null;
  uploadingProductPhoto = false;
  processingProductHeic = false; // Loading para conversão HEIC de produtos
  previewImageUrl: string | SafeUrl | null = null;

  // NOVO: Controle de loading individual para cada foto de produto
  uploadingProductPhotos = new Map<string, boolean>(); // productId -> boolean
  uploadingProductPhotoTypes = new Map<string, Set<'bottle' | 'box' | 'label'>>(); // productId -> Set<photoType>

  // NOVO: Métodos para controlar o loading individual de fotos
  setProductPhotoUploading(productId: string, photoType: 'bottle' | 'box' | 'label', isUploading: boolean): void {
    if (isUploading) {
      // Inicializa o Set se não existir
      if (!this.uploadingProductPhotoTypes.has(productId)) {
        this.uploadingProductPhotoTypes.set(productId, new Set());
      }
      this.uploadingProductPhotoTypes.get(productId)!.add(photoType);
      this.uploadingProductPhotos.set(productId, true);
    } else {
      // Remove o tipo específico
      const photoTypes = this.uploadingProductPhotoTypes.get(productId);
      if (photoTypes) {
        photoTypes.delete(photoType);
        // Se não há mais tipos sendo enviados, remove o produto da lista
        if (photoTypes.size === 0) {
          this.uploadingProductPhotoTypes.delete(productId);
          this.uploadingProductPhotos.delete(productId);
        }
      }
    }
    this.cdr.detectChanges();
  }

  // NOVO: Verifica se uma foto específica está sendo enviada
  isProductPhotoUploading(productId: string, photoType: 'bottle' | 'box' | 'label'): boolean {
    const photoTypes = this.uploadingProductPhotoTypes.get(productId);
    return photoTypes ? photoTypes.has(photoType) : false;
  }

  // NOVO: Verifica se há alguma foto sendo enviada
  hasAnyProductPhotoUploading(): boolean {
    return this.uploadingProductPhotos.size > 0;
  }

  // NOVO: Rastrear IDs de fotos de tratamento sendo enviadas (usaremos IDs temporários)
  uploadingTreatmentPhotoIds = new Set<string>();

  // NOVO: Rastrear IDs de fotos de tratamento sendo removidas
  deletingPhotoIds = new Set<string>();

  // NOVO: Rastrear IDs de fotos sendo processadas (conversão HEIC do backend)
  processingBackendPhotoIds = new Set<string>();

  // NOVO: Função auxiliar para verificar o estado de loading de uma foto de tratamento
  public isTreatmentPhotoLoading(photoId: string): boolean {
    return this.uploadingTreatmentPhotoIds.has(photoId) || this.deletingPhotoIds.has(photoId) || this.processingBackendPhotoIds.has(photoId);
  }

  // QR Code para upload de fotos
  qrCodeUrl: string = '';

  /** Loading isolado do modal de QR — independente do `loading` global

   *  da tela de detalhes (que esconde o painel inteiro). Sem isso, abrir

   *  o modal pra gerar QR ocultava a página de detalhes atrás dele. */

  qrCodeLoading: boolean = false;
  showQrCode = false;
  qrCodeConfirmed = false;
  qrCodeType: 'treatment' | 'product' | 'treatment_photos' = 'treatment';
  selectedQrCodeProductId: string | null = null;
  selectedQrCodePhotoType: 'bottle' | 'box' | 'label' | null = null;
  qrCodeExpirationTime: Date | null = null;
  qrCodePhotosFinishedCheckbox: boolean = false;
  isDesktopDevice: boolean = false;

  // PDF do Plano de Tratamento e da Intercorrência são gerados no backend.
  // `today` mantido para uso em outros contextos do template.
  today = new Date();

  // Controle do modal de encerramento
  showCloseTreatmentModal = false;
  confirmCloseTreatment = false;

  // Controle do modal de visualização de foto
  selectedViewPhoto: ITreatmentPlanPhoto | null = null;

  // Controle do modal de visualização de foto de produto
  selectedProductViewPhoto: string | null = null;
  selectedProductViewPhotoType: 'bottle' | 'box' | 'label' | null = null;
  selectedProductViewPhotoProduct: ITreatmentPlanProduct | null = null;

  // Controle do modal de confirmação de remoção
  showRemoveConfirmModal = false;
  removeConfirmLoading = false;
  // Include 'product_photo' so v2 template can reuse the same modal without type errors
  removeConfirmType: 'product' | 'photo' | 'product_photo' = 'product';
  removeConfirmId: string = '';
  removeConfirmName: string = '';

  // Controle de mensagens
  showMessage = false;
  messageType: 'success' | 'error' = 'success';
  messageText: string = '';
  messageTimeout: any = null;

  // Simplificar controle de câmera - manter apenas o mínimo necessário
  hasCamera = false;
  isCameraActive = false;
  cameraStream: MediaStream | null = null;
  rawImageUrl: string | null = null;

  // Controle de upload
  showUploadOptions = false;
  currentUploadType: 'treatment' | 'product' = 'product';

  // Controle de upload múltiplo
  isMultipleUpload = false;
  multipleUploadProgress = 0;
  currentUploadFileName = '';
  totalFilesToUpload = 0;
  currentFileIndex = 0;
  uploadedFiles: PhotoCaptureResult[] = [];
  showMultipleUploadProgress = false;

  // Propriedades para armazenar arquivos de imagem temporários
  tempProductPhotos: { [productId: string]: { [type: string]: File } } = {};

  // Exposição do console para uso no template
  console = console;

  // Mapeamento de fabricantes para produtos
  manufacturerProductMap: { [key: string]: string[] } = {};

  // Lista de fabricantes disponíveis
  manufacturersList: string[] = [];

  // Controle dos dropdowns abertos
  openedManufacturerDropdowns: string[] = [];
  openedProductDropdowns: string[] = [];
  openedRegionDropdowns: string[] = [];
  openedUnitDropdowns: string[] = [];

  // Lista de unidades de medida disponíveis
  unitsList: string[] = ['ml', 'mg', 'g', 'UI', 'cc', 'unidades'];

  // Produtos disponíveis para cada produto no formulário
  availableProducts: string[][] = [];

  // Variável para armazenar o QR Code gerado
  qrCodeData: string = '';
  errorMessage: string = '';

  // Controle de seleção de regiões
  selectedRegions: string[][] = [];

  // Filtro de busca de regiões por produto (checkbox multi-select)
  regionSearchFilters: { [productIndex: number]: string } = {};

  // Controle de abertura do painel de regiões por produto
  regionPanelOpen: { [productIndex: number]: boolean } = {};

  // Controle de abertura do modal de regiões por produto (v2)
  regionModalOpen: { [productIndex: number]: boolean } = {};
  regionModalProductIndex: number = -1;

  // Controle de input de região customizada por produto
  /**
   * Rascunho do input "região personalizada" enquanto o usuário digita.
   *
   * <p>Indexado por `productIndex` para casar com o `*ngFor` do template
   * (`customRegionInput[i]`). Ao remover/reordenar produtos chamar
   * {@link #reindexProductLocalState} para evitar chaves órfãs apontando para
   * produtos deletados.
   */
  customRegionInput: { [productIndex: number]: string } = {};

  showNewProductForm = false; // Variável para controlar a exibição do formulário de novo produto

  // Adicionar uma nova propriedade à classe
  canCloseTreatmentPlan: boolean = false;

  // Propriedades para o tooltip do botão de encerrar
  showCloseTooltip: boolean = false;
  closeTooltipErrors: string[] = [];
  tooltipPosition: any = {};

  // ViewChild references para posicionamento do tooltip
  @ViewChild('closeButton') closeButton!: ElementRef;
  @ViewChild('closeTooltip') closeTooltip!: ElementRef;

  // Variável para controlar a visibilidade da mensagem de ajuda
  showHelpInfoVisible: boolean = false;

  // Controle de alterações não salvas
  hasUnsavedChanges: boolean = false;

  // Referência à função de limpeza do evento beforeunload
  private beforeUnloadListener: ((event: BeforeUnloadEvent) => void) | null = null;

  // Sistema de gerenciamento de saves
  private saveTimeout: any = null;
  private saveDebounceTime = 3000; // 3 segundos
  private pendingSave = false;
  showSaveIndicator = false;
  saveIndicatorText = '';
  private saveQueue: string[] = []; // Queue para rastrear tipos de save

  // Detectar se é dispositivo móvel para ajustar QR Code
  isMobile: boolean = false;

  // Subscriptions para limpeza
  private subscriptions: any[] = [];

  // ADICIONADO: Cache para otimização de métodos de fotos
  private prePhotosCache: ITreatmentPlanPhoto[] = [];
  private postPhotosCache: ITreatmentPlanPhoto[] = [];
  private markingPhotosCache: ITreatmentPlanPhoto[] = [];
  private immediatePostPhotosCache: ITreatmentPlanPhoto[] = [];
  private photoCacheInvalidated: boolean = true;
  // Flags individuais de cache por tipo de foto (corrige bug onde apenas o primeiro getter recalculava)
  private preCacheInvalidated: boolean = true;
  private postCacheInvalidated: boolean = true;
  private markingCacheInvalidated: boolean = true;
  private immediatePostCacheInvalidated: boolean = true;
  private formChangeDetectionSetup: boolean = false;

  /**
   * Subject de destruição — emitido em `ngOnDestroy` para unsubscribe automático
   * via `takeUntil(this.destroy$)` em qualquer observable do componente.
   * Defesa em profundidade junto com o array `this.subscriptions`: se alguma
   * subscribe nova esquecer o push no array, takeUntil ainda garante cleanup.
   */
  private readonly destroy$ = new Subject<void>();

  // Flag para evitar auto-save durante inicialização do componente
  private isInitializing: boolean = true;

  // Dados de regiões carregados da API
  applicationRegionCategories: IApplicationRegionCategory[] = [];
  private facialRegionsFromApi: string[] = [];
  private corporalRegionsFromApi: string[] = [];
  private regionsLoaded = false;

  // Elemento atualmente destacado com erro de validação
  private currentHighlightedElement: HTMLElement | null = null;

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private treatmentPlanService: TreatmentPlanService,
    private patientsService: PatientsService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private photoCaptureService: PhotoCaptureService,
    protected store: Store<{ warningPopup: IError }>,
    private incidentService: IncidentService,
    private attachmentService: AttachmentService,
    private productService: ProductService,
    private qrLinkService: QrLinkService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.treatmentPlanId = id;
    }

    // Detectar se é dispositivo móvel
    this.isMobile = this.isMobileDevice();

    // Inicializar o mapeamento entre fabricantes e produtos
    this.initManufacturerProductMap();

    // CORREÇÃO 1: Subscription única para queryParams com cleanup adequado
    const queryParamsSub = this.route.queryParams.subscribe(params => {
      if (params['tab'] && ['info', 'products', 'photos', 'intercorrencias'].includes(params['tab'])) {
        this.activeTab = params['tab'] as 'info' | 'products' | 'photos' | 'intercorrencias';
        if (this.activeTab === 'intercorrencias') {
          this.loadIntercorrencias();
        }
      }
    });
    this.subscriptions.push(queryParamsSub);

    if (this.treatmentPlanId) {
      // Carregar regiões da API ANTES do plano para evitar race condition
      // que classificaria regiões padrão como "Outra" (região personalizada)
      this.loadApplicationRegionsThenPlan();
    } else {
      this.loadApplicationRegions();
      this.loading = false;
      this.router.navigate(['/main/treatment-plans']);
    }

    // Inicializar o estado de "tocado" dos campos do formulário
    this.initFormTouchedState();
    this.checkCameraAvailability();

    // Verificar se é dispositivo desktop
    this.isDesktopDevice = this.isDesktopBrowser();

    this.initializeProductControls();
  }

  ngOnDestroy(): void {

    // Limpar todos os recursos quando o componente for destruído

    // Restaurar overflow do body caso o modal de regiões esteja aberto
    document.body.style.overflow = '';

    // Remover o listener de beforeunload
    this.removeBeforeUnloadListener();

    // Remover listener de keydown para F5 se existir
    try {
      document.removeEventListener('keydown', this.handleKeyDown);
    } catch (e) {
    }

    // CRÍTICO: Parar e limpar recursos da câmera
    this.stopCamera();
    this.isCameraActive = false;

    // CRÍTICO: Limpar todas as URLs de blob para evitar vazamento de memória
    this.cleanupBlobUrls();

    // Limpar quaisquer intervalos ou timers
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }

    // CRÍTICO: Limpar objetos temporários que podem conter referências a arquivos grandes
    this.tempProductPhotos = {};
    this.manufacturerProductMap = {};
    this.availableProducts = [];

    // CORREÇÃO 9: Limpar cache de fotos
    this.invalidatePhotoCache();

    // Limpar referências a arquivos de imagem
    this.selectedPhotoFile = null;
    this.selectedProductPhotoFile = null;

    // CORREÇÃO 10: Limpar todas as subscriptions de forma mais robusta
    this.subscriptions.forEach((sub, index) => {
      if (sub && typeof sub.unsubscribe === 'function') {
        try {
          sub.unsubscribe();
        } catch (e) {
          console.warn(`Erro ao remover subscription ${index}:`, e);
        }
      }
    });
    this.subscriptions = [];

    // CORREÇÃO 11: Resetar flags de configuração
    this.formChangeDetectionSetup = false;

    // Sinaliza para qualquer observable subscrito via takeUntil(this.destroy$) que
    // o componente foi destruído — unsubscribe automático sem depender do array.
    this.destroy$.next();
    this.destroy$.complete();

    // CRÍTICO: Limpar referências do tratmentPlan para permitir GC adequado
    if (this.treatmentPlan && this.treatmentPlan.photos) {
      this.treatmentPlan.photos.forEach(photo => {
        if (photo.photo_url && photo.photo_url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(photo.photo_url);
          } catch (e) {
            console.warn('Erro ao revogar URL de foto:', e);
          }
        }
      });
    }

    // Cancelar save pendente
    this.cancelPendingSave();

    // NOVO: Limpar estado de loading de fotos
    this.uploadingProductPhotos.clear();
    this.uploadingProductPhotoTypes.clear();

    // Limpar referências do FormArray para permitir GC
    if (this.treatmentPlanForm) {
      this.treatmentPlanForm.reset();
    }

  }

  /**
   * NOVO MÉTODO: Limpa sistematicamente todas as URLs de blob para evitar vazamento de memória
   */
  private cleanupBlobUrls(): void {

    // Limpar URL de preview atual
    if (this.rawImageUrl && this.rawImageUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(this.rawImageUrl);
      } catch (e) {
        console.warn('Erro ao revogar rawImageUrl:', e);
      }
      this.rawImageUrl = null;
    }

    // Limpar preview sanitizado
    this.previewImageUrl = null;

    // Limpar URLs de fotos do modelo de dados
    if (this.treatmentPlan) {
      // Limpar fotos de tratamento
      if (this.treatmentPlan.photos) {
        this.treatmentPlan.photos.forEach(photo => {
          if (photo.photo_url && photo.photo_url.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(photo.photo_url);
            } catch (e) {
              console.warn('Erro ao revogar URL de foto de tratamento:', e);
            }
          }
        });
      }

      // Limpar fotos de produtos
      if (this.treatmentPlan.products) {
        this.treatmentPlan.products.forEach(product => {
          if (product.photos) {
            product.photos.forEach(photo => {
              if (photo.photo_url && photo.photo_url.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(photo.photo_url);
                } catch (e) {
                  console.warn('Erro ao revogar URL de foto de produto:', e);
                }
              }
            });
          }
        });
      }
    }

  }

  ngAfterViewInit(): void {
    // Configurar detector imediatamente - sem timeout
    this.setupBeforeUnloadListener();

    // Adicionar também um listener de evento de tecla para detectar F5
    document.addEventListener('keydown', this.handleKeyDown);

  }

  // Método para configurar a detecção de alterações no formulário
  setupFormChangeDetection(): void {
    if (this.formChangeDetectionSetup) {
      return;
    }

    // Configurar listener imediatamente - sem timeout
    if (this.treatmentPlanForm) {
      // ORDEM: marcar a flag ANTES do subscribe para que, caso a criação da subscription
      // lance exceção, uma chamada posterior a setupFormChangeDetection NÃO crie um segundo
      // subscribe (vazamento). Se o subscribe falhar mesmo, resetamos no catch.
      this.formChangeDetectionSetup = true;
      try {
        // `takeUntil(this.destroy$)` garante cleanup mesmo se esquecermos de dar
        // push em this.subscriptions — redundância intencional.
        const formChangeSub = this.treatmentPlanForm.valueChanges.pipe(
          takeUntil(this.destroy$),
          debounceTime(500), // Aumentar debounce para 500ms para reduzir chamadas
          distinctUntilChanged()
        ).subscribe(() => {
          // Limpar cache de validação quando o formulário muda
          this.clearValidationCache();

          // CORREÇÃO 4: Invalidar cache de fotos quando há mudanças
          this.invalidatePhotoCache();

          // Ignorar mudanças de formulário durante inicialização (evita save espúrio
          // causado por diretivas de template como ngx-mask formatando campos)
          if (this.isInitializing) {
            return;
          }

          if (!this.hasUnsavedChanges) {
            this.hasUnsavedChanges = true;
            // Solicitar save com debounce quando há mudanças no formulário
            this.requestSave('form_change', false);
          }
          // Atualizar o estado do botão de encerramento quando o formulário mudar
          this.updateCanCloseTreatmentPlan();
        });
        this.subscriptions.push(formChangeSub);
      } catch (err) {
        // Se falhou em criar a subscription, revertemos a flag para permitir retry
        this.formChangeDetectionSetup = false;
        console.error('Falha ao configurar detecção de mudanças do formulário:', err);
      }
    }
  }

  // ADICIONADO: Método para invalidar cache de fotos
  private invalidatePhotoCache(): void {
    this.photoCacheInvalidated = true;
    this.preCacheInvalidated = true;
    this.postCacheInvalidated = true;
    this.markingCacheInvalidated = true;
    this.immediatePostCacheInvalidated = true;
    this.prePhotosCache = [];
    this.postPhotosCache = [];
    this.markingPhotosCache = [];
    this.immediatePostPhotosCache = [];
  }

  // Método para atualizar o estado do botão de encerramento - versão simples
  updateCanCloseTreatmentPlan(): void {
    if (this.treatmentPlan && this.treatmentPlan.status === 'ACTIVE') {
      const newCanClose = this.checkCanCloseTreatmentPlan();
      if (this.canCloseTreatmentPlan !== newCanClose) {
        this.canCloseTreatmentPlan = newCanClose;
        // Apenas marcar para check, sem timeout
        this.cdr.markForCheck();
      }
    }
  }

  // Método para limpar o cache de validação - chamado após salvamento
  private clearValidationCache(): void {
    this.lastValidationHash = '';
    this.lastValidationResult = false;
    // Invalidar cache de fotos para forçar atualização
    this.photoCacheInvalidated = true;
    this.preCacheInvalidated = true;
    this.postCacheInvalidated = true;
    this.markingCacheInvalidated = true;
    this.immediatePostCacheInvalidated = true;
    this.prePhotosCache = [];
    this.postPhotosCache = [];
    this.markingPhotosCache = [];
    this.immediatePostPhotosCache = [];
  }

  async loadTreatmentPlan(): Promise<void> {
    this.loading = true;

    // Guardar a aba atual para manter a mesma selecionada após carregar
    const currentTab = this.activeTab;

    this.treatmentPlanService.getTreatmentPlanById(this.treatmentPlanId)
      .then(async (response: any) => {
        // Mapear os dados recebidos do backend para o formato esperado pelo frontend
        this.treatmentPlan = {
          id: response.id,
          client_id: response.client_id,
          observations: response.observations,
          status: response.status,
          created_at: response.created_at,
          updated_at: response.updated_at,
          published_at: response.published_at,
          patient_name: response.patient_name,
          patient_age: response.patient_age,
          image_authorization: response.authorize_image_use,
          contracted_procedures: response.contracted_procedures,
          responsible_professional: response.responsible_professional,
          products: response.products || [],
          photos: response.photos || [],
          // Extrair procedimentos da string separada por vírgulas
          procedures: response.contracted_procedures,
          // Extrair informações do profissional responsável
          ...this.extractProfessionalInfo(response.responsible_professional),
          // Converter o valor total para formato número
          total_value: this.parseStringCurrencyToNumber(response.total_value)
        };

        // Processar produtos e fotos para compatibilidade com o front-end
        await this.processApiDataForFrontend();

        // Ler status de paciente modelo direto do DTO
        this.isModelPatient = !!response.is_model_patient || !!(this.treatmentPlan as any).is_model_patient;

        this.initForm();
        this.initializeProductControls(); // Inicializa os controles para produtos existentes
        this.hideNewProductForm(); // Esconde o formulário de novo produto ao carregar
        this.loading = false;

        // Resetar indicador de alterações não salvas
        this.hasUnsavedChanges = false;

        // Atualizar o estado do botão de encerramento após carregar os dados
        this.updateCanCloseTreatmentPlan();

        // Manter a aba atual se não houver parâmetro específico na URL
        this.activeTab = currentTab;

        // Desativar flag de inicialização após um curto delay para ignorar
        // valueChanges espúrios vindos de diretivas de template (ex: ngx-mask)
        setTimeout(() => {
          this.isInitializing = false;
        }, 1000);

        // Informação de paciente modelo já vem do backend; sem chamada extra
      })
      .catch((error: Error) => {
        console.error('Erro ao carregar o plano de tratamento:', error);
        this.loading = false;
        this.isInitializing = false;
      });
  }

  // Removido: Carregamento extra de is_model_patient (agora vem no DTO)


  /**
   * Extrai informações do profissional de uma string formatada
   * Exemplo: "Camila Andrade | Cirurgião Dentista | CRO | SP 123456"
   */
  extractProfessionalInfo(professionalString: string): any {
    if (!professionalString) {
      return {
        professional_name: '',
        professional_occupation: '',
        professional_council: '',
        professional_registration: ''
      };
    }

    const parts = professionalString.split('|').map(part => part.trim());

    return {
      professional_name: parts[0] || '',
      professional_occupation: parts[1] || '',
      professional_council: parts[2] || '',
      professional_registration: parts[3] || ''
    };
  }

  /**
   * Converte uma string de moeda em número, aceitando tanto formato brasileiro
   * ("3.400,00") quanto formato internacional ("3,400.00" ou "3400.00").
   *
   * <p>ANTES da correção, a função assumia sempre formato BR — então um valor
   * vindo do backend como "1234.56" (sem separador de milhar) era tratado como
   * "1234,56" com ponto de milhar, resultando em 1234 (os decimais sumiam).
   * Esse era o mesmo vetor do bug de "comer um décimo" na Medida Total Aplicada.
   *
   * <p>Heurística: o separador decimal é o último `,` ou `.` encontrado. Caracteres
   * `,` / `.` anteriores a ele são separadores de milhar e podem ser descartados.
   *
   * Exemplos: "3.400,00" → 3400 · "3,400.00" → 3400 · "1234.56" → 1234.56
   *           "0,5"      → 0.5  · "10,50"    → 10.5 · "R$ 1.234,56" → 1234.56
   */
  parseStringCurrencyToNumber(currencyString: string | number | null | undefined): number {
    if (currencyString === null || currencyString === undefined) return 0;
    if (typeof currencyString === 'number') return isFinite(currencyString) ? currencyString : 0;

    // Remover símbolos de moeda e espaços
    const cleaned = String(currencyString).replace(/[R$\s]/g, '');
    if (!cleaned) return 0;

    // Descobrir onde está o separador decimal (última vírgula ou último ponto).
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const lastDecimal = Math.max(lastComma, lastDot);

    let normalized: string;
    if (lastDecimal === -1) {
      // Sem qualquer separador — é um inteiro simples.
      normalized = cleaned;
    } else {
      // Tudo que vem antes do último separador são separadores de milhar → remove.
      const intPart = cleaned.substring(0, lastDecimal).replace(/[.,]/g, '');
      const decPart = cleaned.substring(lastDecimal + 1);
      normalized = decPart.length > 0 ? `${intPart}.${decPart}` : intPart;
    }

    const parsed = parseFloat(normalized);
    return isFinite(parsed) ? parsed : 0;
  }

  /**
   * Processa os dados da API para o formato esperado pelo frontend
   */
  async processApiDataForFrontend(): Promise<void> {
    if (!this.treatmentPlan) return;

    // Converter fotos da API para o formato do frontend
    if (this.treatmentPlan.photos && this.treatmentPlan.photos.length > 0) {
      // Adicionar IDs ao set de loading antes de processar
      this.treatmentPlan.photos.forEach(photo => {
        if (photo.id && photo.photo && !this.photoCaptureService.isUrl(photo.photo)) {
          this.processingBackendPhotoIds.add(photo.id);
        }
      });
      this.cdr.detectChanges();

      // Processar fotos em paralelo para melhor performance.
      // Cache de sessão: URLs convertidas (base64 HEIC → JPEG data-uri) ficam gravadas em
      // sessionStorage indexadas por photo.id. Assim, se o usuário navegar para outra tela
      // e voltar, não paga o custo de CPU de reconverter a imagem HEIC de novo.
      // O cache é limitado à sessão do navegador (sessionStorage) para não acumular lixo
      // entre logins e não impactar a privacidade em dispositivos compartilhados.
      const processedPhotos = await Promise.all(
        this.treatmentPlan.photos.map(async photo => {
          let photoUrl = photo.photo_url;

          // Se tiver base64, processar para detectar e converter HEIC
          if (photo.photo) {
            try {
              if (this.photoCaptureService.isUrl(photo.photo)) {
                photoUrl = photo.photo;
              } else {
                // Tenta primeiro o cache de sessão (evita reconversão em navegação)
                const cached = this.readPhotoUrlCache(photo.id);
                if (cached) {
                  photoUrl = cached;
                } else {
                  // Converte HEIC para JPEG se necessário
                  photoUrl = await this.photoCaptureService.convertBase64HeicToJpeg(photo.photo);
                  // Salva em cache após conversão bem-sucedida
                  if (photo.id && photoUrl) {
                    this.writePhotoUrlCache(photo.id, photoUrl);
                  }
                }
              }
            } catch (error) {
              console.error('Erro ao processar foto:', error);
              // Em caso de erro real, tentamos renderizar como JPEG mesmo —
              // se o conteúdo não for compatível, o <img> mostra broken icon
              // (preferível a deixar tudo travado sem imagem alguma).
              photoUrl = `data:image/jpeg;base64,${photo.photo}`;
            } finally {
              // Remover do set de loading após processar
              if (photo.id) {
                this.processingBackendPhotoIds.delete(photo.id);
              }
            }
          }

          // IMPORTANTE: ao fazer o spread com `photo_url: photoUrl`, estamos persistindo
          // no treatmentPlan.photos. Assim, se outra parte do código recarregar
          // `processApiDataForFrontend()` sem nova resposta do backend, o photo_url já
          // estará preenchido e a conversão não roda de novo.
          return {
            ...photo,
            // Converter PRE_TREATMENT para PRE e POST_TREATMENT para POST se necessário
            type: photo.type === 'PRE_TREATMENT' ? 'PRE' :
              photo.type === 'POST_TREATMENT' ? 'POST' :
              photo.type === 'IMMEDIATE_POST_TREATMENT' ? 'IMMEDIATE_POST' : photo.type,
            // Usar URL processada
            photo_url: photoUrl,
            // Data de criação agora vem do backend; se ausente, permanece undefined para ocultar no front
            created_at: photo.created_at
          };
        })
      );

      this.treatmentPlan.photos = processedPhotos;

      // CORREÇÃO 12: Invalidar cache quando as fotos são processadas
      this.invalidatePhotoCache();
    }

    // Converter produtos da API para o formato do frontend
    if (this.treatmentPlan.products && this.treatmentPlan.products.length > 0) {
      // Processar produtos com fotos em paralelo
      const processedProducts = await Promise.all(
        this.treatmentPlan.products.map(async product => {
          // Extrair fotos específicas por tipo
          let bottle_photo_url = null;
          let expiration_photo_url = null;
          let box_photo_url = null;
          let label_photo_url = null;

          if (product.photos && product.photos.length > 0) {
            // Processar fotos do produto em paralelo
            const processedPhotos = await Promise.all(
              product.photos.map(async photo => {
                let photoUrl = photo.photo;
                if (!this.photoCaptureService.isUrl(photo.photo)) {
                  if (await this.photoCaptureService.detectHeicFromBase64(photo.photo)) {
                    photoUrl = await this.photoCaptureService.convertBase64HeicToJpeg(photo.photo);
                  }
                  else {
                    photoUrl = `data:image/jpeg;base64,${photo.photo}`;
                  }
                }

                return { ...photo, processedUrl: photoUrl, photo: photoUrl };
              })
            );

            // Detectar esquema de photo_order (legado x novo)
            const maxPhotoOrder = Math.max(
              ...product.photos.map((p: IProductPhoto) => (p.photo_order !== undefined && p.photo_order !== null) ? p.photo_order : -1)
            );
            const usesLegacyPhotoOrder = maxPhotoOrder >= 3;

            // Atribuir URLs processadas por tipo
            processedPhotos.forEach(photo => {
              const order = photo.photo_order;
              // Classificar foto pelo photo_order, tratando legado explicitamente
              if (usesLegacyPhotoOrder) {
                // Esquema legado: 0=Frasco, 1=Validade, 2=Caixa, 3=Etiqueta
                switch (order) {
                  case 0: bottle_photo_url = photo.processedUrl; break;     // Frasco
                  case 1: expiration_photo_url = photo.processedUrl; break; // Validade (legado)
                  case 2: box_photo_url = photo.processedUrl; break;        // Caixa
                  case 3: label_photo_url = photo.processedUrl; break;      // Etiqueta
                }
              } else {
                // Novo esquema (sem foto de validade): 0=Frasco, 1=Caixa, 2=Etiqueta
                switch (order) {
                  case 0: bottle_photo_url = photo.processedUrl; break; // Frasco
                  case 1: box_photo_url = photo.processedUrl; break;    // Caixa
                  case 2: label_photo_url = photo.processedUrl; break;  // Etiqueta
                }
              }
            });
          }

          // Processar nome do produto
          // No backend o campo 'product' é mapeado para 'product_name' via @JsonProperty
          const product_name = product.product_name || product.product || '';

          const sanitizedApplicationDate = this.normalizeIsoDate(product.application_date) || '';

          // Convertendo application_regions para string (para compatibilidade com interface)
          const application_region = product.application_regions ?
            (product.application_regions.length > 0 ? product.application_regions.map(r => r.application_site).join(', ') : '') :
            product.application_region;

          // Criar objeto de produto para o frontend
          return {
            ...product,
            product_name,
            application_date: sanitizedApplicationDate,
            bottle_photo_url,
            expiration_photo_url,
            box_photo_url,
            label_photo_url,
            application_region,
          };
        })
      );

      this.treatmentPlan.products = processedProducts;
    }
  }

  /**
   * Lê uma photo_url convertida do sessionStorage (cache de sessão).
   * Retorna null se não houver cache — caller deve então converter e chamar
   * `writePhotoUrlCache` depois.
   *
   * Usamos sessionStorage (não localStorage) para:
   *  - Expirar ao fechar aba (reduz risco de cache obsoleto)
   *  - Não persistir entre usuários em dispositivos compartilhados
   *  - Capacidade (~5MB) adequada para dezenas de fotos convertidas
   */
  private readPhotoUrlCache(photoId: string | undefined): string | null {
    if (!photoId) return null;
    try {
      return sessionStorage.getItem(`tp:photo-url:${photoId}`);
    } catch {
      // sessionStorage pode lançar em navegadores com storage desabilitado (modo privado antigo, etc.)
      return null;
    }
  }

  private writePhotoUrlCache(photoId: string, url: string): void {
    try {
      sessionStorage.setItem(`tp:photo-url:${photoId}`, url);
    } catch (e) {
      // Quota exceeded é possível com muitas fotos grandes. Ignoramos silenciosamente
      // — o pior caso é reconverter na próxima navegação, não é fatal.
      console.debug('[PHOTO CACHE] sessionStorage cheio, ignorando:', e);
    }
  }

  initForm(): void {
    if (!this.treatmentPlan) {
      console.error('Tentativa de inicializar formulário sem dados do tratamento');
      return;
    }

    // Determinar o nome do paciente a exibir
    const patientNameDisplay = this.treatmentPlan.patient_name || (this.isModelPatient ? 'Paciente Modelo' : '');

    // Inicialização do formulário - log removido
    this.treatmentPlanForm = this.fb.group({
      patient_name: [{ value: patientNameDisplay, disabled: true }],
      professional_name: [{ value: this.treatmentPlan.responsible_professional || '', disabled: true }],
      image_authorization: [this.treatmentPlan.image_authorization || false],
      observations: [this.treatmentPlan.observations || ''],
      patient_age: [this.treatmentPlan.patient_age || '', Validators.required],
      products: this.fb.array([])
    });

    // Inicializar produtos existentes
    if (this.treatmentPlan.products && this.treatmentPlan.products.length > 0) {
      this.treatmentPlan.products.forEach(product => {
        this.addExistingProduct(product);
      });
    }

    // Formulário inicializado - log removido

    // Desabilitar o formulário se o tratamento estiver encerrado
    if (this.treatmentPlan.status === 'CLOSED') {
      this.treatmentPlanForm.disable();
    } else {
      // Garantir que apenas os campos editáveis estejam habilitados
      this.treatmentPlanForm.get('patient_name')?.disable();
      this.treatmentPlanForm.get('professional_name')?.disable();
      this.treatmentPlanForm.get('image_authorization')?.enable();
      this.treatmentPlanForm.get('observations')?.enable();
    }

    // Iniciar sem alterações não salvas ANTES de configurar a detecção
    this.hasUnsavedChanges = false;

    if (!this.formChangeDetectionSetup) {
      this.setupFormChangeDetection();
    }

    // Atualizar estado do botão de encerramento
    this.updateCanCloseTreatmentPlan();
  }

  updateForm(): void {
    if (this.treatmentPlanForm) {
      this.treatmentPlanForm.patchValue({
        patient_name: this.treatmentPlan.patient_name,
        professional_name: this.treatmentPlan.responsible_professional,
        image_authorization: this.treatmentPlan.image_authorization,
        observations: this.treatmentPlan.observations || '',
        patient_age: this.treatmentPlan.patient_age || ''
      }, { emitEvent: false });

      // Atualizar os produtos no FormArray com os IDs corretos
      const productsArray = this.productsArray;

      // Limpar e reconstruir o FormArray de produtos
      while (productsArray.length > 0) {
        productsArray.removeAt(0);
      }

      // Recriar os produtos com os IDs atualizados
      if (this.treatmentPlan.products && this.treatmentPlan.products.length > 0) {
        this.treatmentPlan.products.forEach(product => {
          this.addExistingProduct(product);
        });
      }

      // Desabilitar o formulário se o tratamento estiver encerrado
      if (this.treatmentPlan.status === 'CLOSED') {
        this.treatmentPlanForm.disable();
      } else {
        // Garantir que apenas os campos editáveis estejam habilitados
        this.treatmentPlanForm.get('patient_name')?.disable();
        this.treatmentPlanForm.get('professional_name')?.disable();
        this.treatmentPlanForm.get('image_authorization')?.enable();
        this.treatmentPlanForm.get('observations')?.enable();
        this.treatmentPlanForm.get('patient_age')?.enable();
      }
    }
  }

  changeTab(tab: 'info' | 'products' | 'photos' | 'intercorrencias'): void {

    // SALVAR O TRATAMENTO ANTES DE TROCAR DE ABA (força save imediato)
    this.requestSave('tab_change', false);
    this.hasUnsavedChanges = false;

    // MELHORIA: Limpar recursos da aba anterior antes de trocar
    this.cleanupTabResources();

    this.activeTab = tab;

    // Atualizar a URL com o parâmetro da aba atual sem recarregar a página
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge', // manter outros parâmetros da URL
    });

    // Se mudar para a aba de produtos, implementar salvamento automático apenas se há produtos válidos
    if (tab === 'products') {

      // Verificar se há produtos com dados válidos não salvos
      const validUnsavedProducts = this.treatmentPlan.products.filter(p =>
        (!p.id || p.id === '') &&
        p.manufacturer &&
        p.product_name &&
        p.manufacturer.trim() &&
        p.product_name.trim()
      );

      if (validUnsavedProducts.length > 0) {
        this.autoSaveProductsOnTabChange().catch(error => {
          console.error('Erro no auto-save ao entrar na aba de produtos:', error);
        });
      }

      // Removido: Criação automática de produto quando não há produtos
      // A aba produtos agora pode ficar vazia até o usuário adicionar um produto manualmente
    }

    // Se mudar para a aba de fotos, garantir que os dados estejam atualizados
    if (tab === 'photos') {
      // Usar atualização leve imediata - SEM TIMEOUT
      this.cdr.markForCheck();
    }

    // Se mudar para a aba de intercorrências, carregar dados
    if (tab === 'intercorrencias') {
      this.loadIntercorrencias();
    }
  }

  /**
   * NOVO MÉTODO: Limpa recursos da aba atual antes de navegar para outra
   */
  private cleanupTabResources(): void {

    // Parar câmera se estiver ativa ao trocar de aba
    if (this.isCameraActive) {
      this.stopCamera();
      this.isCameraActive = false;
    }

    // Fechar modais abertos
    this.showUploadOptions = false;
    this.showQrCode = false;

    // Limpar previews de imagem
    this.cleanupCurrentImagePreview();

    // Resetar estados de upload
    this.uploadingPhoto = false;
    this.uploadingProductPhoto = false;
  }

  // Método para marcar todos os campos como "tocados" - ULTRA OTIMIZADO
  markAllFieldsAsTouched(): void {
    // Marcar todo o formulário de uma vez - muito mais eficiente
    this.treatmentPlanForm.markAllAsTouched();
  }


  /**
   * Reinicia os campos de região do formulário - OTIMIZADO
   */
  resetProductFormFields() {
    // Remover timeout e DOM queries - usar apenas FormArray reset
    if (this.productsArray.length > 0) {
      const lastProductIndex = this.productsArray.length - 1;
      const regionsArray = this.getRegionsArray(lastProductIndex);
      if (regionsArray) {
        regionsArray.clear(); // Remove todas as regiões (checkbox multi-select não precisa de região vazia)
      }
    }
  }

  /**
   * Remover produto (excluir do banco + lista local)
   */
  async removeProduct(productId: string): Promise<void> {

    // Cache do produto para evitar múltiplas buscas
    const productToRemove = this.treatmentPlan.products.find(p => p.id === productId);
    if (!productToRemove) {
      console.warn('Produto não encontrado:', productId);
      return;
    }

    // Confirmar exclusão
    const confirmed = confirm(`Tem certeza que deseja excluir este produto?`);
    if (!confirmed) return;

    this.loading = true;

    try {

      // Encontrar índice no FormArray
      const productIndex = this.productsArray.controls.findIndex(
        control => control.get('id')?.value === productId
      );

      if (productIndex >= 0) {
        // Mostrar loading no produto específico
        const productFormGroup = this.productsArray.at(productIndex);
        productFormGroup.disable();

        // 1. Excluir do banco
        await this.treatmentPlanService.deleteProductDirect(productId);

        // 2. Remover da lista local
        this.treatmentPlan.products = this.treatmentPlan.products.filter(p => p.id !== productId);
        this.productsArray.removeAt(productIndex);

        // 2.b — Reindexar estados que usam `productIndex` como chave para que
        // não fique "lixo" apontando para um produto deletado. Antes, deletar
        // o produto de índice 2 deixava `customRegionInput[2]` com a string
        // antiga; adicionar novo produto naquela posição (ou qualquer outra
        // que, após o shift do FormArray, ocupasse o índice 2) herdava o
        // texto residual. Reindexa aqui em vez de deletar seletivamente
        // para manter a consistência com o novo shape do array.
        this.reindexProductLocalState(productIndex);

        // 3. Atualizar estado
        this.hasUnsavedChanges = true;
        this.updateCanCloseTreatmentPlan();

        this.showSuccess('Produto excluído com sucesso');

        // Marcar para atualização na próxima detecção de mudanças
        this.cdr.markForCheck();
      } else {
        console.error('Produto não encontrado no FormArray:', productId);
      }

    } catch (error) {
      console.error('❌ Erro ao excluir produto:', error);
    } finally {
      this.loading = false;
    }
  }

  async confirmRemoveProduct(): Promise<void> {
    if (!this.removeConfirmId) {
      console.warn('ID de remoção não definido');
      return;
    }

    const productId = this.removeConfirmId;

    this.loading = true;
    this.removeConfirmLoading = true;

    try {
      // Encontrar o índice do produto no FormArray
      const productIndex = this.productsArray.controls.findIndex(
        control => control.get('id')?.value === productId
      );

      if (productIndex >= 0) {
        // 1. Excluir do banco ANTES de remover localmente
        await this.treatmentPlanService.deleteProductDirect(productId);

        // 2. Remover do FormArray
        this.productsArray.removeAt(productIndex);

        // 3. Atualizar o modelo local
        this.treatmentPlan.products = this.treatmentPlan.products.filter(p => p.id !== productId);

        // 4. Marcar alterações e atualizar estado
        this.hasUnsavedChanges = true;
        this.updateCanCloseTreatmentPlan();

        // Mostrar sucesso e fechar modal
        this.showSuccess('Produto removido com sucesso');
        this.showRemoveConfirmModal = false;

        // Marcar para atualização na próxima detecção de mudanças
        this.cdr.markForCheck();
      } else {
        console.error('Produto não encontrado no FormArray:', productId);
      }
    } catch (error) {
      console.error('Erro ao remover produto:', error);
      this.showError('Erro ao remover produto. Tente novamente.');
    } finally {
      this.loading = false;
      this.removeConfirmLoading = false;
      // Sempre limpar os dados de confirmação
      this.removeConfirmId = '';
      this.removeConfirmName = '';
    }
  }

  // Métodos para upload de fotos de produtos
  selectProductForPhotoUpload(productId: string, photoType: 'bottle' | 'box' | 'label'): void {
    // Redirecionar para o novo método que abre diretamente as opções de upload
    this.initPhotoCapture(productId, photoType);
  }

  cancelProductPhotoUpload(): void {
    // Liberar recursos URL se houver
    if (this.rawImageUrl) {
      try {
        URL.revokeObjectURL(this.rawImageUrl);
      } catch (e) {
        console.error('Erro ao revogar URL:', e);
      }
    }

    // Limpar todas as variáveis
    this.selectedProductId = null;
    this.selectedProductPhotoType = null;
    this.selectedProductPhotoFile = null;
    this.previewImageUrl = null;
    this.rawImageUrl = null;

    // Parar a câmera se estiver ativa
    this.stopCamera();

    // Fechar o modal de opções
    this.showUploadOptions = false;

    // Usar atualização leve da interface
    this.cdr.markForCheck();
  }

  getPhotoTypeLabel(): string {
    switch (this.selectedProductPhotoType) {
      case 'bottle':
        return 'Foto do Frasco';
      case 'box':
        return 'Foto da Caixa do Produto';
      case 'label':
        return 'Foto da Etiqueta';
      default:
        return 'Foto do Produto';
    }
  }


  uploadPhoto(skipSave: boolean = false): void {
    // Log removido para reduzir ruído

    if (!this.selectedPhotoFile) {
      return;
    }

    this.uploadingPhoto = true;

    // Criar ID temporária para a foto
    const tempId = 'temp_' + Date.now();

    // Converter a foto para base64 antes de armazenar
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64Content = e.target?.result as string;

      // Converter o tipo para o formato que o backend espera
      const backendType = this.toBackendPhotoType(this.selectedPhotoType);

      // Garantir que o array de fotos existe
      if (!this.treatmentPlan.photos) {
        this.treatmentPlan.photos = [];
      }

      // Criar objeto temporário de foto com todos os campos necessários
      const newPhoto: ITreatmentPlanPhoto = {
        id: tempId,
        photo_url: base64Content,
        description: '', // Usando string vazia como descrição padrão
        type: backendType as any, // Tipo para o backend
        created_at: new Date().toISOString(),
        photo_order: (this.treatmentPlan.photos.length + 1), // Usar um valor sequencial dentro do range permitido
        photo: base64Content.split(',')[1] || base64Content // Armazenar apenas a parte base64 sem o prefixo
      };


      // Adicionar a foto ao array apenas uma vez
      this.treatmentPlan.photos.push(newPhoto);

      // Limpar cache de validação pois os dados mudaram
      this.clearValidationCache();

      // Marcar que há alterações não salvas
      this.hasUnsavedChanges = true;

      // Fechar o modal de upload e limpar os formulários
      this.showUploadOptions = false;
      this.clearPhotoPreview();
      this.uploadingPhoto = false;

      // Atualizar estado do botão de encerramento
      this.updateCanCloseTreatmentPlan();

      // Usar atualização leve da UI para evitar travamento
      this.cdr.markForCheck();

      this.showSuccess('Foto adicionada com sucesso!');

      // Só salvar se não foi instruído para pular o save (para uploads múltiplos)
      if (!skipSave) {
        this.syncTreatmentPhotos(this.selectedPhotoType, 'photo_upload');
      }
    };

    reader.onerror = () => {
      console.error('Erro ao ler arquivo para converter em base64');
      this.uploadingPhoto = false;
    };

    // Iniciar a leitura do arquivo como URL de dados
    reader.readAsDataURL(this.selectedPhotoFile);
  }

  async removePhoto(photoId: string): Promise<void> {
    if (!photoId) return;

    this.removeConfirmLoading = true;

    // INÍCIO DA MODIFICAÇÃO
    this.deletingPhotoIds.add(photoId); // Adiciona o ID para ativar o loading
    this.cdr.detectChanges(); // Força a atualização da UI para mostrar o spinner
    // FIM DA MODIFICAÇÃO

    this.uploadingPhoto = true;

    try {
      // 1. Espera a confirmação do backend
      await this.treatmentPlanService.deletePhoto(this.treatmentPlanId, photoId);

      // Criamos uma nova lista de fotos sem a foto removida
      const newPhotos = this.treatmentPlan.photos.filter(p => p.id !== photoId);
      // Criamos um NOVO objeto treatmentPlan com a nova lista de fotos
      this.treatmentPlan = { ...this.treatmentPlan, photos: newPhotos };

      this.invalidatePhotoCache();
      this.cdr.detectChanges();
      this.showSuccess('Foto do tratamento removida com sucesso!');

    } catch (error) {
      console.error('Erro ao remover foto do tratamento:', error);
    } finally {
      // INÍCIO DA MODIFICAÇÃO
      this.deletingPhotoIds.delete(photoId); // Remove o ID para desativar o loading
      // FIM DA MODIFICAÇÃO

      // Desativa o loading e fecha o modal
      this.uploadingPhoto = false;
      this.removeConfirmLoading = false;
      this.showRemoveConfirmModal = false;
    }
  }

  confirmRemovePhoto(): void {
    const photoId = this.removeConfirmId;
    this.removePhoto(photoId); // Chama a nova função refatorada
  }

  cancelRemoveConfirm(): void {
    this.showRemoveConfirmModal = false;
    this.removeConfirmLoading = false;
    this.removeConfirmId = '';
    this.removeConfirmName = '';
  }

  /**
   * QR Code sempre disponível (produto sempre tem ID real)
   */
  canShowQrCode(productId: string): boolean {
    return !!(productId && productId.trim() !== '');
  }

  // Método para QR Code
  async toggleQrCode(type: 'treatment' | 'product' | 'treatment_photos' = 'treatment', productId?: string, photoType?: 'bottle' | 'box' | 'label'): Promise<void> {
    this.qrCodeType = type;

    // Mostrar o QR code com um spinner de carregamento
    this.showQrCode = true;
    this.qrCodeConfirmed = false;
    this.isDesktopDevice = this.isDesktopBrowser();
    this.errorMessage = '';
    this.qrCodePhotosFinishedCheckbox = false;

    // Antes de gerar o QR, garantir que os dados preenchidos estejam salvos para evitar perda ao sair para fotos
    try {
      if (this.hasUnsavedChanges) {
        await this.forceSave('before_open_qr', true);
      }
    } catch (e) {
      console.warn('Falha ao salvar antes de abrir o QR Code. Continuando.', e);
    }

    // Configurar tipo adicional específico para o QR code
    if (type === 'product' && productId) {
      this.selectedQrCodeProductId = productId;
      this.selectedQrCodePhotoType = photoType || null;
    } else if (type === 'treatment_photos') {
      // Não é necessário configurar nada adicional aqui
    }

    this.qrCodeConfirmed = true;
    this.qrCodeLoading = true;

    // Limpar estado de geração anterior — sem isso o modal flashava o QR
    // antigo brevemente enquanto o novo carregava.
    this.qrCodeUrl = '';
    this.qrCodeData = '';
    this.errorMessage = '';

    console.info('[QR-GEN] Iniciando geração de QR Code', {
      type,
      treatmentPlanId: this.treatmentPlan?.id,
      productId: this.selectedQrCodeProductId || undefined,
      photoTypeProduct: this.selectedQrCodePhotoType || undefined,
      photoTypeTreatment: this.selectedPhotoType || undefined,
      clinicId: this.treatmentPlan?.clinic_id || undefined,
    });

    try {
      // URL pública gerada pelo backend via short-link. Antes embutíamos o JWT
      // do criador na query (~700 chars) e o WhatsApp truncava, fazendo o
      // paciente abrir URL incompleta com `&token=` vazio. Agora a URL final
      // tem ~70 chars e é linkada inteira pelo WhatsApp/iMessage/etc.
      const baseUrl = window.location.origin;
      let qrData = '';

      // Capturar módulos e módulo selecionado pra repassar ao backend.
      let modulesParam = '';
      let selectedModuleId: string | undefined;
      try {
        const modules = this.authService.getModules?.() || [];
        if (modules && modules.length > 0) {
          modulesParam = btoa(JSON.stringify(modules));
        }
        const selectedModule = (this.authService as any).getSelectedModuleSync
          ? (this.authService as any).getSelectedModuleSync()
          : (this.authService.getSelectedModule ? this.authService.getSelectedModule() : null);
        if (selectedModule && selectedModule.id) {
          selectedModuleId = selectedModule.id;
        }
      } catch (e) {
        // silencioso
      }

      // Chama backend conforme o tipo. Backend persiste params, retorna token
      // curto (~22 chars), gera HMAC token de 30 min na hora da resolução.
      if (type === 'product' && this.selectedQrCodeProductId) {
        const resp = await firstValueFrom(this.qrLinkService.createProductPhotos({
          treatmentPlanId: this.treatmentPlan.id,
          productId: this.selectedQrCodeProductId,
          photoType: this.selectedQrCodePhotoType || undefined,
          modules: modulesParam || undefined,
          moduleId: selectedModuleId,
          clinicId: this.treatmentPlan.clinic_id || undefined,
        }));
        qrData = `${baseUrl}/upload-photos?qr=${resp.token}`;

      } else if (type === 'treatment_photos') {
        const resp = await firstValueFrom(this.qrLinkService.createTreatmentPhotos({
          treatmentPlanId: this.treatmentPlan.id,
          photoType: this.selectedPhotoType || undefined,
          modules: modulesParam || undefined,
          moduleId: selectedModuleId,
          clinicId: this.treatmentPlan.clinic_id || undefined,
        }));
        qrData = `${baseUrl}/upload-treatment-photos?qr=${resp.token}`;

      } else {
        console.warn('Tipo de QR Code não implementado:', type);
        this.errorMessage = 'Tipo de QR Code não suportado.';
        this.qrCodeLoading = false;
        return;
      }

      // Atribui URL final ao template. O *ngIf="!qrCodeLoading && qrCodeUrl"
      // garante que o <qrcode> só renderiza após esse ponto, sem flash.
      this.qrCodeData = qrData;
      this.qrCodeUrl = qrData;
      this.qrCodeLoading = false;

      console.info('[QR-GEN] QR Code gerado com sucesso', {
        type,
        urlLength: qrData.length,
        // NÃO logar qrData inteiro — paciente acessa via essa URL e ela
        // chega a ter o token curto. Token vaza no log = vaza acesso.
      });
    } catch (error) {
      const details = unwrapError(error);
      console.error('[QR-GEN] Erro ao gerar QR Code', { type, ...details });
      // Mensagem amigável vem do servidor quando disponível, fallback genérico.
      this.errorMessage = details.serverMessage || 'Ocorreu um erro ao gerar o QR Code. Tente novamente.';
      this.qrCodeLoading = false;
    }
  }

  /**
   * Limpa e otimiza o token para reduzir o tamanho da URL
   */
  private cleanAndOptimizeToken(token: string): string {
    if (!token) return token;

    // Remover espaços em branco
    let cleanToken = token.trim();

    // Se o token for muito longo (> 100 caracteres), tentar encurtar
    if (cleanToken.length > 100) {
      // Pegar apenas os primeiros 50 e últimos 30 caracteres para manter unicidade
      cleanToken = cleanToken.substring(0, 50) + cleanToken.substring(cleanToken.length - 30);
    }

    return cleanToken;
  }

  /**
   * Copia o link do QR Code para a área de transferência
   */
  copyQrCodeLink(): void {
    if (!this.qrCodeUrl) {
      return;
    }

    // Usar a API moderna de clipboard se disponível
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.qrCodeUrl)
        .then(() => {
          this.store.dispatch(setWarningText('Link copiado para a área de transferência!', 200));
        })
        .catch(err => {
          console.error('Erro ao copiar link:', err);
          this.fallbackCopyToClipboard(this.qrCodeUrl);
        });
    } else {
      // Fallback para navegadores mais antigos
      this.fallbackCopyToClipboard(this.qrCodeUrl);
    }
  }

  /**
   * Método fallback para copiar texto em navegadores mais antigos
   */
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.store.dispatch(setWarningText('Link copiado para a área de transferência!', 200));
      } else {
      }
    } catch (err) {
      console.error('Erro no fallback de cópia:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  /**
   * Abre o link do QR Code em uma nova aba/janela
   */
  openQrCodeLink(): void {
    if (!this.qrCodeUrl) {
      return;
    }

    try {
      window.open(this.qrCodeUrl, '_blank', 'noopener,noreferrer');
      this.showSuccess('Link aberto em nova aba');
    } catch (error) {
      console.error('Erro ao abrir link:', error);
    }
  }

  /**
   * Retorna uma versão encurtada da URL para exibição
   */
  getDisplayableQrCodeUrl(): string {
    if (!this.qrCodeUrl) return '';

    // Se a URL for muito longa, mostrar apenas parte dela
    if (this.qrCodeUrl.length > 60) {
      const start = this.qrCodeUrl.substring(0, 30);
      const end = this.qrCodeUrl.substring(this.qrCodeUrl.length - 25);
      return `${start}...${end}`;
    }

    return this.qrCodeUrl;
  }

  refreshPhotos(): void {
    // Indicar que estamos carregando
    // Manter a aba atual ao recarregar
    const currentTab = this.activeTab;
    const currentUrl = window.location.href;

    // Remover o listener de beforeunload antes de recarregar
    this.removeBeforeUnloadListener();

    // Verificar se já existe um parâmetro tab na URL
    if (currentUrl.includes('tab=')) {
      window.location.reload();
    } else {
      // Adicionar o parâmetro tab à URL atual antes de recarregar
      const separator = currentUrl.includes('?') ? '&' : '?';
      window.location.href = `${currentUrl}${separator}tab=${currentTab}`;
    }
  }

  getQrCodeTitle(): string {
    if (this.qrCodeType === 'product') {
      let photoTypeLabel = '';

      switch (this.selectedQrCodePhotoType) {
        case 'bottle':
          photoTypeLabel = 'do Frasco';
          break;
        case 'box':
          photoTypeLabel = 'da Caixa';
          break;
        case 'label':
          photoTypeLabel = 'da Etiqueta';
          break;
        default:
          photoTypeLabel = 'do Produto';
      }

      return `QR Code para Upload de Foto ${photoTypeLabel}`;
    } else if (this.qrCodeType === 'treatment_photos') {
      // Título mais específico baseado no tipo de foto selecionado
      const photoTypeLabels: Record<string, string> = {
        'PRE': 'Pré-Tratamento',
        'POST': 'Pós-Tratamento',
        'MARKING': 'Marcação',
        'IMMEDIATE_POST': 'Pós-Imediato'
      };
      const label = photoTypeLabels[this.selectedPhotoType];
      if (label) {
        return `QR Code para Upload de Fotos ${label}`;
      } else {
        return 'QR Code para Upload de Fotos de Tratamento';
      }
    } else if (this.qrCodeType === 'treatment') {
      return 'QR Code para Upload de Fotos de Tratamento';
    } else {
      return 'QR Code para Upload de Fotos';
    }
  }


  // Método para encerrar tratamento

  // Método para encerrar tratamento
  closeTreatmentPlan(): void {
    // Se já verificamos que pode encerrar, mostre o modal diretamente
    if (this.canCloseTreatmentPlan) {
      this.showCloseTreatmentModal = true;
    } else {
      // Se não pode encerrar, navegar até o campo que falta e destacá-lo
      this.validateAndNavigateToMissingField();
    }
  }

  /**
   * Valida os campos necessários para encerramento e navega até o primeiro campo inválido,
   * destacando-o visualmente e mostrando uma mensagem informativa.
   */
  private validateAndNavigateToMissingField(): void {

    // Limpar destaque anterior antes de re-validar
    this.clearCurrentHighlight();

    // Marcar todos os campos como tocados para exibir erros
    this.markAllFieldsAsTouched();

    let firstInvalidElement: HTMLElement | null = null;
    let firstInvalidTab: 'info' | 'products' | 'photos' | null = null;
    let errorMessage: string = '';

    // Verificar aba de informações gerais - Idade do paciente
    const patientAge = this.treatmentPlanForm.get('patient_age');
    if (!patientAge?.value || patientAge.value <= 0) {
      firstInvalidTab = 'info';
      errorMessage = 'Para encerrar o plano, informe a idade do paciente.';
      const element = document.getElementById('patient-age-field');
      if (element) firstInvalidElement = element;
    }

    // Verificar aba de produtos (se ainda não encontrou erro)
    if (!firstInvalidTab && this.productsArray.length > 0) {
      const productsArrayValue = this.productsArray.value;

      for (let i = 0; i < productsArrayValue.length; i++) {
        const product = productsArrayValue[i];
        const productName = product.product_name || `Produto ${i + 1}`;
        const productId = product.id;

        // Verificar campos básicos do produto
        if (!product.application_date) {
          firstInvalidTab = 'products';
          errorMessage = `Para encerrar o plano, informe a data de aplicação do ${productName}.`;
          const element = document.getElementById(`product-${i}-application_date-main`);
          if (element) firstInvalidElement = element;
          break;
        }

        if (!product.manufacturer) {
          firstInvalidTab = 'products';
          errorMessage = `Para encerrar o plano, selecione o fabricante do ${productName}.`;
          const element = document.getElementById(`dropdown-button-manufacturer-${i}`);
          if (element) firstInvalidElement = element;
          break;
        }

        if (!product.product_name) {
          firstInvalidTab = 'products';
          errorMessage = `Para encerrar o plano, selecione o nome do ${productName}.`;
          const element = document.getElementById(`dropdown-button-product-${i}`);
          if (element) firstInvalidElement = element;
          break;
        }

        // Verificar regiões de aplicação
        const regions = product.application_regions || [];
        if (regions.length === 0) {
          firstInvalidTab = 'products';
          errorMessage = `Para encerrar o plano, adicione pelo menos uma região de aplicação para o ${productName}.`;
          const element = document.getElementById(`product-${i}-regions-section`);
          if (element) firstInvalidElement = element as HTMLElement;
          break;
        }

        // Verificar cada região
        let regionError = false;
        for (let j = 0; j < regions.length; j++) {
          const region = regions[j];
          if (!region.region) {
            firstInvalidTab = 'products';
            errorMessage = `Para encerrar o plano, selecione a região de aplicação ${j + 1} do ${productName}.`;
            const element = document.getElementById(`dropdown-button-region-${i}-${j}`) || 
                           document.getElementById(`product-${i}-region-${j}`);
            if (element) firstInvalidElement = element as HTMLElement;
            regionError = true;
            break;
          }
          if (region.region === 'Outra' && !region.custom_region) {
            firstInvalidTab = 'products';
            errorMessage = `Para encerrar o plano, especifique a região personalizada ${j + 1} do ${productName}.`;
            const element = document.getElementById(`product-${i}-custom-region-${j}`);
            if (element) firstInvalidElement = element as HTMLElement;
            regionError = true;
            break;
          }
        }
        if (regionError) break;

        // Verificar fotos do produto
        const productData = this.treatmentPlan.products?.find(p => p.id === productId);
        if (productData) {
          const hasAnyPhoto = this.hasProductPhoto(productData, 'bottle') ||
                             this.hasProductPhoto(productData, 'box') ||
                             this.hasProductPhoto(productData, 'label');
          
          if (!hasAnyPhoto) {
            firstInvalidTab = 'products';
            errorMessage = `Para encerrar o plano, adicione pelo menos uma foto do ${productName}.`;
            const element = document.getElementById(`product-${i}-photos-section`);
            if (element) firstInvalidElement = element as HTMLElement;
            break;
          }
        }
      }
    }

    // Se encontrou um erro, navegar e destacar
    if (firstInvalidTab && errorMessage) {

      // Mudar para a aba correta
      if (this.activeTab !== firstInvalidTab) {
        this.changeTab(firstInvalidTab);
      }

      // Aguardar a renderização da aba e então rolar/destacar o elemento
      setTimeout(() => {
        this.scrollToAndHighlightElement(firstInvalidElement, errorMessage);
      }, 300);
    }
  }

  /**
   * Remove destaque visual do elemento atualmente destacado
   */
  private clearCurrentHighlight(): void {
    if (this.currentHighlightedElement) {
      const highlightClasses = ['ring-2', 'ring-red-500', 'ring-offset-2'];
      this.currentHighlightedElement.classList.remove(...highlightClasses);
      this.currentHighlightedElement = null;
    }
  }

  /**
   * Rola até o elemento especificado e adiciona destaque visual
   */
  private scrollToAndHighlightElement(element: HTMLElement | null, errorMessage: string): void {
    if (!element) {
      console.warn('Elemento não encontrado para destacar:', errorMessage);
      return;
    }

    // Limpar destaque anterior
    this.clearCurrentHighlight();

    // Rolar até o elemento
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });

    // Adicionar classes de destaque (borda fixa, sem animação)
    const highlightClasses = ['ring-2', 'ring-red-500', 'ring-offset-2'];
    element.classList.add(...highlightClasses);

    // Rastrear elemento destacado para limpeza posterior
    this.currentHighlightedElement = element;

    // Tentar dar foco ao elemento se for um input
    if (element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
      setTimeout(() => {
        element.focus();
      }, 100);
    }
  }

  // Método para validar todos os campos antes do encerramento
  validateAllFieldsForClosing(): boolean {

    // Limpar destaque anterior antes de re-validar
    this.clearCurrentHighlight();

    let isValid = true;
    let firstInvalidElement: HTMLElement | null = null;
    let firstInvalidTab: 'info' | 'products' | 'photos' | null = null;
    const validationErrors: string[] = [];

    // Marcar todos os campos como tocados para exibir erros
    this.markAllFieldsAsTouched();

    // Verificar aba de informações gerais
    const patientAge = this.treatmentPlanForm.get('patient_age');
    if (!patientAge?.value) {
      isValid = false;
      validationErrors.push('Idade do paciente é obrigatória');
      if (!firstInvalidTab) {
        firstInvalidTab = 'info';
        const element = document.getElementById('patient-age-field');
        if (element && !firstInvalidElement) firstInvalidElement = element;
      }
    }

    // Verificar aba de produtos - ALTERADO: produtos não são mais obrigatórios
    // O plano pode ser encerrado sem produtos
    if (this.productsArray.length > 0) {
      // Se há produtos, todos devem estar válidos
      // Validar cada produto
      for (let i = 0; i < this.productsArray.length; i++) {
        const productGroup = this.getProductFormGroup(i);
        const productId = productGroup.get('id')?.value;
        const productName = productGroup.get('product_name')?.value || `Produto ${i + 1}`;

        // Validar campos do produto
        const fieldsToValidate = [
          { field: 'manufacturer', label: 'Fabricante' },
          { field: 'product_name', label: 'Nome do produto' },
          { field: 'application_date', label: 'Data de aplicação' },
        ];
        for (const { field, label } of fieldsToValidate) {
          if (!productGroup.get(field)?.value) {
            isValid = false;
            validationErrors.push(`${productName}: ${label} é obrigatório`);
            if (!firstInvalidTab) {
              firstInvalidTab = 'products';
              const element = document.getElementById(`product-${i}-${field}`);
              if (element && !firstInvalidElement) firstInvalidElement = element;
            }
          }
        }

        // Validar regiões de aplicação
        const regionsArray = this.getRegionsArray(i);
        if (regionsArray.length === 0) {
          isValid = false;
          validationErrors.push(`${productName}: É necessário adicionar pelo menos uma região de aplicação`);
          if (!firstInvalidTab) {
            firstInvalidTab = 'products';
            const element = document.getElementById(`product-${i}-regions-section`);
            if (element && !firstInvalidElement) firstInvalidElement = element;
          }
        } else {
          for (let j = 0; j < regionsArray.length; j++) {
            const regionGroup = this.getRegionFormGroup(i, j);
            const region = regionGroup.get('region')?.value;

            if (!region) {
              isValid = false;
              validationErrors.push(`${productName}: Região ${j + 1} não foi selecionada`);
              if (!firstInvalidTab) {
                firstInvalidTab = 'products';
                const element = document.getElementById(`product-${i}-region-${j}`);
                if (element && !firstInvalidElement) firstInvalidElement = element;
              }
            }

            // Verificar se precisa de região personalizada.
            // IMPORTANTE: usa `.trim()` para rejeitar strings com apenas espaços.
            // Antes, um valor "   " passava pelo check `!value` e o plano era encerrado
            // com uma região personalizada vazia persistida no banco.
            if (region === 'Outra') {
              const customRegionValue = String(regionGroup.get('custom_region')?.value ?? '').trim();
              if (!customRegionValue) {
                isValid = false;
                validationErrors.push(`${productName}: Especifique a região personalizada`);
                if (!firstInvalidTab) {
                  firstInvalidTab = 'products';
                  const element = document.getElementById(`product-${i}-custom-region-${j}`);
                  if (element && !firstInvalidElement) firstInvalidElement = element;
                }
              }
            }
          }
        }

        // Validar fotos do produto - pelo menos uma foto é obrigatória por produto
        const product = this.treatmentPlan.products.find(p => p.id === productId);
        if (product) {
          const fotoTipos = [
            { tipo: 'bottle', nome: 'Frasco' },
            { tipo: 'box', nome: 'Caixa' },
            { tipo: 'label', nome: 'Etiqueta' }
          ];

          // Verificar se há pelo menos uma foto
          const hasAnyPhoto = fotoTipos.some(foto =>
            this.hasProductPhoto(product, foto.tipo as 'bottle' | 'box' | 'label')
          );

          if (!hasAnyPhoto) {
            isValid = false;
            validationErrors.push(`${productName}: É necessário adicionar pelo menos uma foto do produto`);
            if (!firstInvalidTab) {
              firstInvalidTab = 'products';
              const element = document.getElementById(`product-${i}-photos-section`);
              if (element && !firstInvalidElement) firstInvalidElement = element;
            }
          }
        }
      }
    }

    // Mudar para a aba que contém o primeiro erro
    if (firstInvalidTab) {
      this.changeTab(firstInvalidTab);

      // Rolar até o primeiro elemento inválido após a mudança de aba ter sido completada
      setTimeout(() => {
        this.scrollToAndHighlightElement(firstInvalidElement, validationErrors[0] || '');
      }, 500);
    }

    return isValid;
  }

  async confirmCloseTreatmentPlan(): Promise<void> {
    this.showCloseTreatmentModal = false;
    this.confirmCloseTreatment = false;

    // Garantir que todos os campos estão validados
    if (!this.validateAllFieldsForClosing()) {
      return;
    }

    // Abrir o modal de fases e iniciar o fluxo
    this.publishingPhase = 'saving';
    this.showPublishingModal = true;
    this.cdr.detectChanges();

    let apiDone = false;
    let apiError: any = null;

    // Dispara toda a cadeia de APIs em paralelo ao progresso visual das fases
    const apiFlow = (async () => {
      try {
        await this.saveTreatmentPlan(true);

        const updatedPlan = await this.treatmentPlanService.closeTreatmentPlan(this.treatmentPlanId);
        this.treatmentPlan = {
          ...this.treatmentPlan,
          ...updatedPlan,
          patient_name: updatedPlan.patient_name || this.treatmentPlan.patient_name
        };
        this.cdr.detectChanges();
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
          await this.triggerSendPdfByEmail(true, true);
        } catch (emailError) {
          // Falha de email não bloqueia o fluxo - o usuário pode reenviar manualmente
          console.error('Erro ao enviar PDF por email após publicação:', emailError);
        }

        apiDone = true;
      } catch (error) {
        apiError = error;
      }
    })();

    // Aguarda pelo tempo da fase OU pela conclusão/erro da API (o que vier primeiro)
    const waitPhaseOrApi = (duration: number) =>
      Promise.race([
        new Promise(resolve => setTimeout(resolve, duration)),
        apiFlow
      ]);

    // Fase 1: Salvando informações
    await waitPhaseOrApi(5000);
    if (apiError) {
      this.handlePublishingError(apiError);
      return;
    }
    if (apiDone) {
      this.publishingPhase = 'completed';
      this.cdr.detectChanges();
      return;
    }

    // Fase 2: Encerrando o Plano
    this.publishingPhase = 'closing';
    this.cdr.detectChanges();
    await waitPhaseOrApi(5000);
    if (apiError) {
      this.handlePublishingError(apiError);
      return;
    }
    if (apiDone) {
      this.publishingPhase = 'completed';
      this.cdr.detectChanges();
      return;
    }

    // Fase 3: Gerando e enviando documento para o Paciente — permanece até a API terminar
    this.publishingPhase = 'generating';
    this.cdr.detectChanges();
    await apiFlow;
    if (apiError) {
      this.handlePublishingError(apiError);
      return;
    }

    this.publishingPhase = 'completed';
    this.cdr.detectChanges();
  }

  isPublishingPhaseComplete(phase: 'saving' | 'closing' | 'generating'): boolean {
    const order = ['saving', 'closing', 'generating', 'completed'];
    return order.indexOf(this.publishingPhase) > order.indexOf(phase);
  }

  isPublishingPhaseActive(phase: 'saving' | 'closing' | 'generating'): boolean {
    return this.publishingPhase === phase;
  }

  private handlePublishingError(error: any): void {
    this.showPublishingModal = false;
    console.error('Erro ao encerrar tratamento:', error);
    this.showError('Erro ao encerrar tratamento: ' + (error?.error?.message || error?.message || 'Erro desconhecido'));
  }

  // Fechado pelo botão "Entendi" quando o fluxo completa com sucesso
  closePublishingModal(): void {
    this.showPublishingModal = false;
    this.removeBeforeUnloadListener();
    window.location.reload();
  }

  /**
   * Dispara o envio do PDF por email. O PDF é gerado no backend a partir dos dados
   * já persistidos — o frontend só dispara a request com os destinatários desejados.
   */
  async triggerSendPdfByEmail(sendToPatient: boolean = true, sendToClinic: boolean = true): Promise<void> {
    const fileName = this.formatFileName(this.treatmentPlan?.patient_name);
    const response = await this.treatmentPlanService.sendPdfByEmail(this.treatmentPlanId, {
      pdfFileName: fileName,
      sendToPatient,
      sendToClinic
    });

    if (!response?.success) {
      throw new Error(response?.message || 'Erro ao enviar PDF por email');
    }

    if (Array.isArray(response.sent_to) && response.sent_to.length) {
      console.info(`PDF enviado por email para: ${response.sent_to.join(', ')}`);
    }
  }

  /**
   * Baixa o PDF do plano de tratamento. O PDF é gerado no backend via Thymeleaf +
   * openhtmltopdf a partir dos dados persistidos (não depende do DOM da tela).
   */
  async downloadPdf(): Promise<void> {
    if (this.downloadingPdf) {
      return;
    }
    this.downloadingPdf = true;
    this.cdr.detectChanges();
    try {
      const { blob, fileName } = await this.treatmentPlanService.downloadPdf(this.treatmentPlanId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || this.formatFileName(this.treatmentPlan?.patient_name);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF do plano de tratamento:', error);
      this.messageType = 'error';
      this.messageText = 'Não foi possível baixar o PDF. Tente novamente.';
      this.showMessage = true;
    } finally {
      this.downloadingPdf = false;
      this.cdr.detectChanges();
    }
  }

  // Acionador manual do envio de PDF por email para planos encerrados
  async sendPdfByEmail(): Promise<void> {
    if (!this.treatmentPlan || this.treatmentPlan.status !== 'CLOSED' || this.sendingEmail) {
      return;
    }
    this.sendingEmail = true;
    this.cdr.detectChanges();
    try {
      // Reenviar apenas para o paciente
      await this.triggerSendPdfByEmail(true, false);
      this.showSuccess('PDF enviado por email com sucesso!');
    } catch (error) {
      // exibir mensagem de erro usando o mesmo sistema de mensagens
      this.messageType = 'error';
      this.messageText = 'Falha ao enviar o PDF por email. Tente novamente.';
      this.showMessage = true;
    } finally {
      this.sendingEmail = false;
      this.cdr.detectChanges();
    }
  }

  // Método para voltar para a lista
  goBack(): void {
    const navigateBack = () => {
      this.router.navigate(['/main/v2/treatment-plans']);
    };

    if (this.hasUnsavedChanges) {
      if (confirm('Há alterações não salvas. Se sair agora, essas alterações serão perdidas. Deseja continuar?')) {
        this.removeBeforeUnloadListener();
        navigateBack();
      }
    } else {
      navigateBack();
    }
  }

  // Método para calcular a idade a partir da data de nascimento
  calculateAge(birthDate: string): number {
    if (!birthDate) return 0;

    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  // Método para formatar moeda
  formatCurrency(value: number | undefined): string {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  // Método para formatar data
  formatDate(date: string | undefined): string {
    if (!date) return '';

    const d = new Date(date);
    return d.toLocaleDateString('pt-BR');
  }

  // Método para formatar nome do arquivo PDF
  formatFileName(patientName: string | undefined): string {
    if (!patientName) return 'plano_tratamento.pdf';

    // Remover acentos e caracteres especiais
    const normalizedName = patientName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Substituir espaços por underscore e remover caracteres inválidos para nome de arquivo
    const formattedName = normalizedName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

    return `${formattedName}_plano_tratamento.pdf`;
  }

  // Métodos para exibir mensagens
  showSuccess(message: string): void {
    this.messageType = 'success';
    this.messageText = message;
    this.showMessage = true;

    // Limpar qualquer timeout existente
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    // Esconder a mensagem após 3 segundos
    this.messageTimeout = setTimeout(() => {
      this.showMessage = false;
    }, 3000);
  }

  showError(message: string): void {
    // Usar o popup de aviso global (vermelho no canto) para mensagens de erro
    try {
      this.store.dispatch(setWarningText(message, 0));
    } catch (e) {
      // Fallback: manter o comportamento local se não for possível acessar a store
      console.warn('Falha ao disparar popup global de erro, usando fallback local', e);
      this.messageType = 'error';
      this.messageText = message;
      this.showMessage = true;

      // Limpar qualquer timeout existente
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
      }

      // Esconder a mensagem após 5 segundos
      this.messageTimeout = setTimeout(() => {
        this.showMessage = false;
      }, 5000);
    }
  }

  closeMessage(): void {
    this.showMessage = false;
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }

  // Métodos para filtrar fotos por tipo - COM CACHE OTIMIZADO
  getPreTreatmentPhotos(): ITreatmentPlanPhoto[] {
    // Usar flag individual para não interferir nos outros getters
    if (!this.preCacheInvalidated) {
      return this.prePhotosCache;
    }

    if (!this.treatmentPlan || !Array.isArray(this.treatmentPlan.photos)) {
      this.prePhotosCache = [];
      this.preCacheInvalidated = false;
      return this.prePhotosCache;
    }

    const photos = this.treatmentPlan.photos.filter(p => p.type === 'PRE' || p.type === 'PRE_TREATMENT');

    // Garantir que todas as fotos tenham URL válida para exibição
    this.prePhotosCache = photos.map(photo => ({
      ...photo,
      photo_url: photo.photo_url || (photo.photo ? this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}` : '')
    }));

    this.preCacheInvalidated = false;
    return this.prePhotosCache;
  }

  getPostTreatmentPhotos(): ITreatmentPlanPhoto[] {
    // Usar flag individual para não interferir nos outros getters
    if (!this.postCacheInvalidated) {
      return this.postPhotosCache;
    }

    if (!this.treatmentPlan || !Array.isArray(this.treatmentPlan.photos)) {
      this.postPhotosCache = [];
      this.postCacheInvalidated = false;
      return this.postPhotosCache;
    }

    const photos = this.treatmentPlan.photos.filter(p => p.type === 'POST' || p.type === 'POST_TREATMENT');

    // Garantir que todas as fotos tenham URL válida para exibição
    this.postPhotosCache = photos.map(photo => ({
      ...photo,
      photo_url: photo.photo_url || (photo.photo ? this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}` : '')
    }));

    this.postCacheInvalidated = false;
    return this.postPhotosCache;
  }

  getMarkingPhotos(): ITreatmentPlanPhoto[] {
    // Usar flag individual para não interferir nos outros getters
    if (!this.markingCacheInvalidated) {
      return this.markingPhotosCache;
    }

    if (!this.treatmentPlan || !Array.isArray(this.treatmentPlan.photos)) {
      this.markingPhotosCache = [];
      this.markingCacheInvalidated = false;
      return this.markingPhotosCache;
    }

    const photos = this.treatmentPlan.photos.filter(p => p.type === 'MARKING');

    this.markingPhotosCache = photos.map(photo => ({
      ...photo,
      photo_url: photo.photo_url || (photo.photo ? this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}` : '')
    }));

    this.markingCacheInvalidated = false;
    return this.markingPhotosCache;
  }

  getImmediatePostPhotos(): ITreatmentPlanPhoto[] {
    // Usar flag individual para não interferir nos outros getters
    if (!this.immediatePostCacheInvalidated) {
      return this.immediatePostPhotosCache;
    }

    if (!this.treatmentPlan || !Array.isArray(this.treatmentPlan.photos)) {
      this.immediatePostPhotosCache = [];
      this.immediatePostCacheInvalidated = false;
      return this.immediatePostPhotosCache;
    }

    const photos = this.treatmentPlan.photos.filter(p => p.type === 'IMMEDIATE_POST' || p.type === 'IMMEDIATE_POST_TREATMENT');

    this.immediatePostPhotosCache = photos.map(photo => ({
      ...photo,
      photo_url: photo.photo_url || (photo.photo ? this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}` : '')
    }));

    this.immediatePostCacheInvalidated = false;

    return this.immediatePostPhotosCache;
  }

  // Método para obter a data atual formatada para inputs de data (formato ISO)
  getCurrentDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Formato YYYY-MM-DD para inputs
  }

  // (removido) Handlers de limitação por evento substituídos por máscara de entrada (ngx-mask)

  private normalizeIsoDate(dateValue: string | null | undefined): string | null {
    if (!dateValue) return null;

    const trimmed = dateValue.toString().trim();
    if (!trimmed) return null;

    // 1) Já está em ISO YYYY-MM-DD
    const directIsoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (directIsoMatch) {
      return `${directIsoMatch[1]}-${directIsoMatch[2]}-${directIsoMatch[3]}`;
    }

    // 2) Formato BR DD/MM/YYYY
    const brMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brMatch) {
      const dd = brMatch[1], mm = brMatch[2], yyyy = brMatch[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    // 3) Fallback: tentar Date parser (pode aceitar outros formatos válidos)
    const parsedDate = new Date(trimmed);
    if (!isNaN(parsedDate.getTime())) {
      return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
    }

    // 4) Último recurso: se vierem apenas dígitos, tentar interpretar como YYYYMMDD ou DDMMYYYY
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === 8) {
      // Heurística: se começa com 4 dígitos plausíveis de ano (19xx..20xx), tratar como YYYYMMDD; senão tratar como DDMMYYYY
      const first4 = digitsOnly.slice(0, 4);
      if (/^(19|20)\d{2}$/.test(first4)) {
        return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 6)}-${digitsOnly.slice(6, 8)}`;
      } else {
        return `${digitsOnly.slice(4, 8)}-${digitsOnly.slice(2, 4)}-${digitsOnly.slice(0, 2)}`;
      }
    }

    console.error('[TREATMENT_PLAN] Não foi possível normalizar data', trimmed);
    return null;
  }

  // Exibição no formulário: sempre DD/MM/YYYY
  private toFormDateValue(dateValue: string | null | undefined): string {
    const iso = this.normalizeIsoDate(dateValue);
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  // Validação: verifica se uma data em formato BR (DD/MM/YYYY) é válida no calendário
  private isValidBrDate(value: string | null | undefined): boolean {
    if (!value) return false;
    const v = value.toString().trim();
    const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yyyy = parseInt(m[3], 10);
    if (mm < 1 || mm > 12) return false;
    if (dd < 1 || dd > 31) return false;
    // Limites razoáveis de ano (ajuste se necessário)
    if (yyyy < 1900 || yyyy > 2200) return false;
    const d = new Date(yyyy, mm - 1, dd);
    return d.getFullYear() === yyyy && d.getMonth() === (mm - 1) && d.getDate() === dd;
  }

  // Validator Angular para datas BR; só valida quando há valor (required continua separado)
  private brDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (raw === null || raw === undefined || raw === '') return null; // vazio é tratado por required
      return this.isValidBrDate(raw) ? null : { invalidDate: true };
    };
  }

  // Método para exibir o modal de visualização de foto
  showPhotoModal(photo: ITreatmentPlanPhoto): void {
    // Logs removidos para reduzir ruído no console

    // Garantir que a foto tenha uma URL válida antes de mostrar
    if (photo.photo && (!photo.photo_url || !this.photoCaptureService.isUrl(photo.photo))) {
      photo.photo_url = `data:image/jpeg;base64,${photo.photo}`;
    }

    this.selectedViewPhoto = photo;

    // Forçar atualização do componente para garantir que o modal seja exibido
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  // ====== ZOOM/PAN - Estado e Handlers para modal de fotos de tratamento ======
  treatmentZoom = 1;
  treatmentTransX = 0;
  treatmentTransY = 0;
  private treatmentPanning = false;
  private treatmentPanStartX = 0;
  private treatmentPanStartY = 0;

  // ====== ZOOM/PAN - Estado e Handlers para modal de fotos de produto ======
  productZoom = 1;
  productTransX = 0;
  productTransY = 0;
  private productPanning = false;
  private productPanStartX = 0;
  private productPanStartY = 0;

  // Utilitários de zoom
  zoomIn(type: 'treatment' | 'product'): void {
    const step = 0.25;
    const max = 5;
    if (type === 'treatment') {
      this.treatmentZoom = Math.min(max, this.treatmentZoom + step);
    } else {
      this.productZoom = Math.min(max, this.productZoom + step);
    }
  }

  zoomOut(type: 'treatment' | 'product'): void {
    const step = 0.25;
    const min = 1;
    if (type === 'treatment') {
      this.treatmentZoom = Math.max(min, this.treatmentZoom - step);
      if (this.treatmentZoom === 1) { this.treatmentTransX = 0; this.treatmentTransY = 0; }
    } else {
      this.productZoom = Math.max(min, this.productZoom - step);
      if (this.productZoom === 1) { this.productTransX = 0; this.productTransY = 0; }
    }
  }

  resetZoom(type: 'treatment' | 'product'): void {
    if (type === 'treatment') {
      this.treatmentZoom = 1; this.treatmentTransX = 0; this.treatmentTransY = 0;
    } else {
      this.productZoom = 1; this.productTransX = 0; this.productTransY = 0;
    }
  }

  onViewWheel(event: WheelEvent, type: 'treatment' | 'product'): void {
    event.preventDefault();
    const delta = Math.sign(event.deltaY);
    if (delta < 0) this.zoomIn(type); else this.zoomOut(type);
  }

  onViewPanStart(event: MouseEvent | TouchEvent, type: 'treatment' | 'product'): void {
    const isTouch = (event as TouchEvent).touches !== undefined;
    const point = isTouch ? (event as TouchEvent).touches[0] : (event as MouseEvent);
    if (type === 'treatment') {
      this.treatmentPanning = true;
      this.treatmentPanStartX = point.clientX - this.treatmentTransX;
      this.treatmentPanStartY = point.clientY - this.treatmentTransY;
    } else {
      this.productPanning = true;
      this.productPanStartX = point.clientX - this.productTransX;
      this.productPanStartY = point.clientY - this.productTransY;
    }
  }

  onViewPanMove(event: MouseEvent | TouchEvent, type: 'treatment' | 'product'): void {
    const isTouch = (event as TouchEvent).touches !== undefined;
    const point = isTouch ? (event as TouchEvent).touches[0] : (event as MouseEvent);
    if (type === 'treatment') {
      if (!this.treatmentPanning || this.treatmentZoom <= 1) return;
      this.treatmentTransX = point.clientX - this.treatmentPanStartX;
      this.treatmentTransY = point.clientY - this.treatmentPanStartY;
    } else {
      if (!this.productPanning || this.productZoom <= 1) return;
      this.productTransX = point.clientX - this.productPanStartX;
      this.productTransY = point.clientY - this.productPanStartY;
    }
  }

  onViewPanEnd(_event: MouseEvent | TouchEvent, type: 'treatment' | 'product'): void {
    if (type === 'treatment') {
      this.treatmentPanning = false;
    } else {
      this.productPanning = false;
    }
  }

  /**
   * Exibe o modal de foto de produto
   */
  showProductPhotoModal(product: ITreatmentPlanProduct, photoType: 'bottle' | 'box' | 'label'): void {
    if (!product || !product.photos) return;

    // Determinar a ordem da foto com base no tipo
    let photo_order = -1;
    switch (photoType) {
      case 'bottle': photo_order = 0; break;
      case 'box': photo_order = 1; break;
      case 'label': photo_order = 2; break;
    }

    // Encontrar a foto pelo photo_order
    const photo = product.photos.find(p => p.photo_order === photo_order);
    if (!photo) return;

    // Configurar propriedades para exibição do modal
    this.selectedProductViewPhotoType = photoType;
    this.selectedProductViewPhotoProduct = product;
    this.selectedProductViewPhoto = photo.photo_url || this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}`;

    // Usar atualização leve para garantir que o modal seja exibido
    this.cdr.markForCheck();
  }

  // Método para obter o rótulo do tipo de foto de produto para o modal
  getProductPhotoTypeLabel(): string {
    switch (this.selectedProductViewPhotoType) {
      case 'bottle':
        return 'Foto do Frasco';
      case 'box':
        return 'Foto da Caixa do Produto';
      case 'label':
        return 'Foto da Etiqueta';
      default:
        return 'Foto do Produto';
    }
  }

  // Inicializa o estado de "tocado" dos campos
  private initFormTouchedState(): void {
    // Criar um objeto para armazenar o estado "tocado" de cada campo
    this.touchedFields = {
      manufacturer: false,
      application_region: false,
      application_date: false,
      application_regions: []
    };
  }

  // Marcar um campo como "tocado" quando o usuário interagir com ele
  onFieldBlur(fieldName: string): void {
    // Limpar destaque de validação ao interagir com qualquer campo
    this.clearCurrentHighlight();

    // Atualizar o estado "tocado" do campo
    if (fieldName !== 'application_regions') {
      this.touchedFields[fieldName as keyof Omit<typeof this.touchedFields, 'application_regions'>] = true;
    }

    // Verificar o campo quando o usuário sair dele
    this.validateField(fieldName);

    // Autosave rápido após término de preenchimento do campo
    // Usa um debounce curto para permitir pequenos ajustes sem salvar demais
    this.requestSave(`field_blur:${fieldName}`, true, 1200);
  }

  // Marcar um campo de detalhe de unidade como "tocado"
  onApplicationRegionFieldBlur(index: number, fieldName: string): void {
    // Garantir que o array de application_regions tem o tamanho correto
    while (this.touchedFields.application_regions.length <= index) {
      this.touchedFields.application_regions.push({
        id: '',
        region: false,
      });
    }

    // Atualizar o estado "tocado" do campo
    this.touchedFields.application_regions[index][fieldName as keyof { region: boolean }] = true;

    // Autosave rápido após término de preenchimento de campos de região
    this.requestSave(`region_field_blur:${index}:${fieldName}`, true, 1200);
  }

  // Validar um campo específico
  validateField(fieldName: string): void {
    if (!this.newProduct) return;

    const value = this.newProduct[fieldName as keyof ITreatmentPlanProduct];
    if (!value && fieldName !== 'application_regions') {
      // Campo obrigatório não preenchido
      if (fieldName !== 'application_regions') {
        this.touchedFields[fieldName as keyof Omit<typeof this.touchedFields, 'application_regions'>] = true;
      }
    }
  }

  // Verifica se o dispositivo possui câmera
  async checkCameraAvailability(): Promise<void> {
    try {
      // Primeiro, verificar se é um dispositivo móvel
      const isMobile = this.isMobileDevice();

      // Tentar detectar câmera real usando MediaDevices API
      let hasRealCamera = false;

      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          hasRealCamera = devices.some(device => device.kind === 'videoinput');
          // Log removido para reduzir ruído no console
        } catch (enumError) {
          // Log removido para reduzir ruído no console
        }
      }

      // Se não conseguiu enumerar, tentar getUserMedia como teste
      if (!hasRealCamera && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: 640, height: 480 }
          });
          hasRealCamera = true;
          // Parar o stream imediatamente após teste
          stream.getTracks().forEach(track => track.stop());
          // Log removido para reduzir ruído no console
        } catch (getUserMediaError: any) {
          // Log removido para reduzir ruído no console
          hasRealCamera = false;
        }
      }

      // Para dispositivos móveis, assumir que têm câmera mesmo se não conseguirmos detectar
      this.hasCamera = isMobile || hasRealCamera;

      // Log removido para reduzir ruído no console
    } catch (error) {
      console.error('Erro geral ao verificar câmera:', error);
      // Em caso de erro, assumir baseado no tipo de dispositivo
      this.hasCamera = this.isMobileDevice();
    }
  }

  // Verifica se o dispositivo é móvel
  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Utiliza a câmera nativa do dispositivo em vez da câmera via navegador
   * @param type 'treatment' ou 'product' - indica o tipo de foto sendo capturada
   */
  useNativeCamera(type: 'treatment' | 'product'): void {

    // Detectar tipo de dispositivo
    const isMobile = this.isMobileDevice();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);


    // Verificar se há câmera disponível
    if (!this.hasCamera) {
      // Remover timeout - chamar diretamente
      this.selectCaptureOption('gallery');
      return;
    }

    // Para dispositivos móveis, usar input com capture para aproveitar câmera nativa
    if (isMobile) {
      this.useMobileNativeCamera(type);
    } else {
      // Para desktop, usar MediaDevices API para acesso direto à câmera
      this.useDesktopCamera(type);
    }
  }

  /**
   * Implementação para dispositivos móveis usando input com capture
   */
  private useMobileNativeCamera(type: 'treatment' | 'product'): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'environment'); // Usar câmera traseira

    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        this.processSelectedFile(target.files[0], type);
      }
    };

    input.oncancel = () => {
    };

    input.click();
  }

  /**
   * Implementação para desktop usando MediaDevices API
   */
  private async useDesktopCamera(type: 'treatment' | 'product'): Promise<void> {
    try {
      // Definir tipo de upload
      this.currentUploadType = type;

      // Parar qualquer câmera ativa anterior
      this.stopCamera();

      // Limpar previews anteriores
      this.rawImageUrl = null;
      this.previewImageUrl = null;
      this.selectedPhotoFile = null;
      this.selectedProductPhotoFile = null;

      // Ativar interface de câmera
      this.isCameraActive = true;
      this.showUploadOptions = false; // Fechar modal de opções

      // Usar atualização leve da interface
      this.cdr.markForCheck();

      // Configurar constraints de vídeo otimizados
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: 'user', // Começar com câmera frontal para melhor UX
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 }
        },
        audio: false
      };


      // Solicitar acesso à câmera
      this.cameraStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!this.cameraStream || this.cameraStream.getVideoTracks().length === 0) {
        throw new Error('Não foi possível obter stream de vídeo');
      }


      // Remover timeout desnecessário

      // Configurar elemento de vídeo
      const videoElement = document.getElementById('camera-video') as HTMLVideoElement;
      if (!videoElement) {
        throw new Error('Elemento de vídeo não encontrado no DOM');
      }

      videoElement.srcObject = this.cameraStream;

      // Aguardar carregamento dos metadados
      await new Promise<void>((resolve, reject) => {
        videoElement.onloadedmetadata = () => {
          resolve();
        };

        videoElement.onerror = (error) => {
          console.error('Erro no elemento de vídeo:', error);
          reject(new Error('Erro ao carregar vídeo'));
        };

        // Timeout de segurança
        setTimeout(() => reject(new Error('Timeout ao carregar metadados')), 5000);
      });

      // Iniciar reprodução
      await videoElement.play();


    } catch (error: any) {
      console.error('Erro ao acessar câmera:', error);

      // Limpar estado
      this.isCameraActive = false;
      this.stopCamera();

      // Mostrar erro específico baseado no tipo
      let errorMessage = 'Não foi possível acessar a câmera.';

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Permissão de câmera negada. Por favor, permita o acesso à câmera e tente novamente.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'Nenhuma câmera encontrada no dispositivo.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Câmera não suportada neste navegador.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      }


      // Reabrir opções após erro
      setTimeout(() => {
        this.showUploadOptions = true;
        this.cdr.detectChanges();
      }, 3000);
    }
  }

  /**
   * Processa arquivo selecionado (usado tanto para móvel quanto para galeria)
   */
  private async processSelectedFile(file: File, type: 'treatment' | 'product'): Promise<void> {

    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      return;
    }

    // Verificar tamanho (limite de 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return;
    }

    // Ativar loading de processamento HEIC ANTES de fechar o modal
    if (type === 'treatment') {
      this.processingHeic = true;
    } else {
      this.processingProductHeic = true;
    }
    this.cdr.detectChanges();

    // Fechar modal após ativar o loading (loading já está visível se modal ainda estiver aberto)
    this.showUploadOptions = false;

    try {
      // Processar arquivo usando o serviço (inclui conversão HEIC se necessário)
      const processed = await this.photoCaptureService.processFile(file);

      // Processar com base no tipo usando o mesmo fluxo de upload S3
      if (type === 'treatment') {
        await this.uploadTreatmentPhotosToS3([processed]);
      } else {
        const createdPhoto = await this.uploadProductPhotoToS3(processed);
        if (createdPhoto) {
          const product = this.treatmentPlan.products.find(p => p.id === this.selectedProductId);
          if (product) {
            product.photos = product.photos || [];
            product.photos.push({ id: createdPhoto.id, photo: createdPhoto.photo!, photo_order: createdPhoto.photo_order });
            await this.processApiDataForFrontend();
          }
        }
        
        // CORREÇÃO: Atualizar estado do botão de encerramento após upload de foto
        this.updateCanCloseTreatmentPlan();
      }
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
    } finally {
      // Desativar loading de processamento HEIC
      if (type === 'treatment') {
        this.processingHeic = false;
      } else {
        this.processingProductHeic = false;
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Cria preview de arquivo usando FileReader
   */
  private createFilePreview(file: File, callback: (result: string) => void): void {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result as string;
      callback(result);
    };

    reader.onerror = () => {
    };

    reader.readAsDataURL(file);
  }

  /**
   * Adiciona a foto de tratamento capturada temporariamente ao array de fotos
   * Método auxiliar usado após captura com câmera
   */
  addTemporaryTreatmentPhoto(): void {

    if (!this.selectedPhotoFile || !this.rawImageUrl) {
      return;
    }

    // Criar ID temporária para a foto
    const tempId = 'temp_' + Date.now();

    // Converter o tipo para o formato que o backend espera
    const backendType = this.toBackendPhotoType(this.selectedPhotoType);

    // Garantir que o array de fotos existe
    if (!this.treatmentPlan.photos) {
      this.treatmentPlan.photos = [];
    }

    // Obter a parte base64 da URL
    const base64Content = this.rawImageUrl.split(',')[1] || this.rawImageUrl;

    // Criar objeto temporário de foto com todos os campos necessários
    const newPhoto: ITreatmentPlanPhoto = {
      id: tempId,
      photo_url: this.rawImageUrl,
      description: '', // Usando string vazia como descrição padrão
      type: backendType as any, // Tipo para o backend
      created_at: new Date().toISOString(),
      photo_order: (this.treatmentPlan.photos.length + 1), // Usar um valor sequencial dentro do range permitido
      photo: base64Content // Armazenar a parte base64 sem o prefixo
    };

    // Adicionar a foto ao array de fotos
    this.treatmentPlan.photos.push(newPhoto);

    // Limpar cache de validação pois os dados mudaram
    this.clearValidationCache();

    // Marcar que há alterações não salvas
    this.hasUnsavedChanges = true;

    // Atualizar estado do botão de encerramento
    this.updateCanCloseTreatmentPlan();

    // Usar atualização leve da UI para evitar travamento
    this.cdr.markForCheck();

    this.showSuccess('Foto adicionada com sucesso!');
    this.requestPhotoSync(this.selectedPhotoType, 'photo_capture');
  }

  // Capturar foto
  async capturePhoto(): Promise<void> {

    // Verificar se o stream está disponível
    if (!this.cameraStream) {
      return;
    }

    // Snapshot do tipo de foto no momento da captura. Ver coment\u00e1rio em
    // `uploadTreatmentPhotosToS3` / `selectCaptureOption` para racional.
    const capturedPhotoType: FrontendPhotoType = this.selectedPhotoType;

    try {
      // Obter o elemento de vídeo usando ID específico
      const video = document.getElementById('camera-video') as HTMLVideoElement;

      if (!video) {
        return;
      }

      // Verificar se o vídeo está pronto para captura
      if (!video.videoWidth || !video.videoHeight) {
        return;
      }


      // Criar um canvas para capturar a imagem
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Desenhar a imagem do vídeo no canvas
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }

      // Desenhar a imagem do vídeo no canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Converter o canvas para blob com Promise
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b); else reject(new Error('Erro ao gerar blob da imagem capturada'));
        }, 'image/jpeg', 0.95);
      });

      // Criar um arquivo a partir do blob
      const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Parar a câmera após captura e fechar modal
      this.stopCamera();
      this.isCameraActive = false;

      // Ativar loading de processamento
      if (this.currentUploadType === 'treatment') {
        this.processingHeic = true;
      } else {
        this.processingProductHeic = true;
      }
      this.cdr.detectChanges();

      try {
        // Processar o arquivo para seguir o mesmo fluxo da galeria (S3)
        const processed = await this.photoCaptureService.processFile(file);

        if (this.currentUploadType === 'treatment') {
          // Usa o mesmo pipeline de upload para S3 das fotos da galeria
          await this.uploadTreatmentPhotosToS3([processed], capturedPhotoType);
        } else {
          // Produto: upload único via S3
          const createdPhoto = await this.uploadProductPhotoToS3(processed);
          if (createdPhoto) {
            const product = this.treatmentPlan.products.find(p => p.id === this.selectedProductId);
            if (product) {
              product.photos = product.photos || [];
              product.photos.push({ id: createdPhoto.id, photo: createdPhoto.photo!, photo_order: createdPhoto.photo_order });
              await this.processApiDataForFrontend();
            }
          }
          
          // CORREÇÃO: Atualizar estado do botão de encerramento após upload de foto
          this.updateCanCloseTreatmentPlan();
        }

        // Atualizar UI
        this.cdr.markForCheck();
      } finally {
        // Desativar loading de processamento
        if (this.currentUploadType === 'treatment') {
          this.processingHeic = false;
        } else {
          this.processingProductHeic = false;
        }
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Erro ao capturar foto:', error);
      // Desativar loading em caso de erro
      this.processingHeic = false;
      this.processingProductHeic = false;
    }
  }

  // Método para garantir que o modal seja exibido
  private ensurePreviewModalDisplayed(): void {
    // Função desativada
    return;
  }

  // Verificar elementos de preview no DOM
  private checkPreviewElements(): void {
    // Função desativada
    return;
  }

  // Parar câmera - método melhorado para liberar todos os recursos
  stopCamera(): void {

    // Parar stream de vídeo se estiver ativo
    if (this.cameraStream) {
      try {
        this.cameraStream.getTracks().forEach(track => {
          track.stop();
        });
      } catch (error) {
        console.error('Erro ao parar tracks de vídeo:', error);
      }
      this.cameraStream = null;
    }

    // Limpar elemento de vídeo se existir
    const videoElement = document.getElementById('camera-video') as HTMLVideoElement;
    if (videoElement) {
      try {
        videoElement.srcObject = null;
        videoElement.pause();
        // MELHORIA: Remover atributos que podem manter referências
        videoElement.removeAttribute('src');
        videoElement.load(); // Força limpeza interna do elemento de vídeo
      } catch (error) {
        console.error('Erro ao limpar elemento de vídeo:', error);
      }
    }

    // Limpar URLs de imagem temporárias com verificação mais robusta
    if (this.rawImageUrl) {
      try {
        if (this.rawImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.rawImageUrl);
        }
      } catch (e) {
        console.error('Erro ao revogar URL de imagem:', e);
      }
      this.rawImageUrl = null;
    }

    // Resetar flags e estados
    this.isCameraActive = false;

    // MELHORIA: Forçar limpeza de memória
    if (typeof window !== 'undefined' && window.gc) {
      try {
        window.gc(); // Funciona apenas em ambientes de desenvolvimento com flag --js-flags="--expose-gc"
      } catch (e) {
        // Ignorar erro se gc não estiver disponível
      }
    }

  }

  // Método para limpar a visualização prévia da foto
  clearPhotoPreview(stopCameraOnClear: boolean = false): void {

    // MELHORIA: Liberar URL do objeto de forma mais robusta
    if (this.rawImageUrl) {
      try {
        if (this.rawImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.rawImageUrl);
        }
      } catch (e) {
        console.error('Erro ao revogar URL:', e);
      }
      this.rawImageUrl = null;
    }

    // Limpar referências
    this.previewImageUrl = null;
    this.selectedPhotoFile = null;

    // MELHORIA: Parar câmera se solicitado E se estiver ativa
    if (stopCameraOnClear && this.isCameraActive) {
      this.stopCamera();
      this.isCameraActive = false;
    }

    // MELHORIA: Forçar coleta de lixo do Angular
    this.cdr.markForCheck();
  }

  // Método para limpar a visualização prévia da foto do produto
  clearProductPhotoPreview(stopCameraOnClear: boolean = false): void {
    // Log removido para reduzir ruído

    if (this.rawImageUrl) {
      try {
        // Se for uma URL de blob, revogar para liberar recursos
        if (this.rawImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.rawImageUrl);
        }
      } catch (e) {
        console.error('Erro ao tentar revogar URL:', e);
      }
    }

    // Reiniciar variáveis para limpar a pré-visualização
    this.previewImageUrl = null;
    this.rawImageUrl = null;
    this.selectedProductPhotoFile = null;

    // Usar atualização leve da view para refletir as mudanças
    this.cdr.markForCheck();
  }

  // Método para lidar com a seleção de múltiplas fotos do tratamento
  async onMultiplePhotosSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files.length > 0) {
      try {
        // Ativar loading
        if (this.currentUploadType === 'treatment') {
          this.processingHeic = true;
        } else {
          this.processingProductHeic = true;
        }

        this.cdr.detectChanges();

        // Converter FileList para Array
        const files = Array.from(input.files);

        // Processar múltiplos arquivos
        const results = await this.photoCaptureService.processMultipleFiles(
          files,
          (current, total, fileName) => {
            this.currentFileIndex = current;
            this.totalFilesToUpload = total;
            this.currentUploadFileName = fileName;
            this.multipleUploadProgress = (current / total) * 100;
            this.cdr.detectChanges();
          }
        );

        // Criar resultado em formato compatível
        const multipleResult: MultiplePhotoCaptureResult = {
          files: results,
          totalCount: results.length
        };

        // Processar resultados
        await this.processMultiplePhotoResults(multipleResult, this.currentUploadType);

      } catch (error) {
        console.error('Erro ao processar múltiplas fotos:', error);
      } finally {
        // Desativar loading
        if (this.currentUploadType === 'treatment') {
          this.processingHeic = false;
        } else {
          this.processingProductHeic = false;
        }

        // Limpar input
        input.value = '';
      }
    }
  }

  // Método para lidar com a seleção de foto única do produto
  async onSinglePhotoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files.length > 0) {
      try {
        // Ativar loading
        this.processingProductHeic = true;
        this.cdr.detectChanges();

        // Pegar apenas o primeiro arquivo (seleção única)
        const file = input.files[0];

        // Processar arquivo único
        const result = await this.photoCaptureService.processFile(file);

        // Processar resultado único
        await this.processSinglePhotoResult(result, 'product');

      } catch (error) {
        console.error('Erro ao processar foto de produto:', error);
      } finally {
        // Desativar loading
        this.processingProductHeic = false;

        // Limpar input
        input.value = '';
      }
    }
  }



  /**
   * NOVO MÉTODO: Limpa apenas a prévia atual de imagem antes de criar uma nova
   */
  private cleanupCurrentImagePreview(): void {
    if (this.rawImageUrl) {
      try {
        if (this.rawImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.rawImageUrl);
        }
      } catch (e) {
        console.warn('Erro ao revogar URL anterior:', e);
      }
      this.rawImageUrl = null;
    }
    this.previewImageUrl = null;
  }

  /**
   * Obtém todas as fotos do produto em um formato mais simples para exibição na grid
   */
  getProductPhotos(): { id: string, url: string, type: string }[] {
    if (this.editingProductId) {
      // Se estiver editando, pega as fotos do produto atual
      const product = this.treatmentPlan.products.find(p => p.id === this.editingProductId);
      if (!product || !product.photos) return [];

      // Mapear as fotos do array photos[] para o formato de exibição
      return product.photos.map(photo => {
        let type = '';
        switch (photo.photo_order) {
          case 0: type = 'Frasco'; break;
          case 1: type = 'Validade'; break;
          case 2: type = 'Caixa'; break;
          case 3: type = 'Etiqueta'; break;
          default: type = `Foto ${photo.photo_order}`;
        }

        return {
          id: photo.id || `photo_${photo.photo_order}`,
          url: photo.photo_url || this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}`,
          type
        };
      });
    } else {
      // Se está criando novo produto
      return [];
    }
  }



  async removeProductPhoto(productId: string, photoType: 'bottle' | 'box' | 'label'): Promise<void> {
    const product = this.treatmentPlan.products.find(p => p.id === productId);
    const photoOrder = { 'bottle': 0, 'box': 1, 'label': 2 }[photoType];
    const photo = product?.photos?.find(p => p.photo_order === photoOrder);

    if (!photo || !photo.id) return;

    this.setProductPhotoUploading(productId, photoType, true);

    try {
      await this.treatmentPlanService.deletePhoto(this.treatmentPlanId, photo.id, productId);

      const productIndex = this.treatmentPlan.products.findIndex(p => p.id === productId);
      if (productIndex > -1) {
        const newProducts = [...this.treatmentPlan.products];
        const productToUpdate = { ...newProducts[productIndex] };
        productToUpdate.photos = productToUpdate.photos?.filter(p => p.id !== photo.id);

        // Limpar a URL específica para garantir que a imagem suma da tela
        switch (photoType) {
          case 'bottle': (productToUpdate as any).bottle_photo_url = null; break;
          case 'box': (productToUpdate as any).box_photo_url = null; break;
          case 'label': (productToUpdate as any).label_photo_url = null; break;
        }

        newProducts[productIndex] = productToUpdate;
        this.treatmentPlan = { ...this.treatmentPlan, products: newProducts };

        const productFormGroup = this.productsArray.at(productIndex);
        if (productFormGroup) {
          // Apenas atualizar os campos relacionados a fotos para evitar sobrescrever
          // campos como application_date que devem permanecer conforme o formulário
          try {
            productFormGroup.patchValue({
              photos: productToUpdate.photos || []
            }, { emitEvent: false });

            // Se há propriedades de URL específicas mantidas no modelo, atualizar separadamente no form se existirem
            // (não todos os nomes, apenas campos que estejam presentes no form)
            if (productFormGroup.get('bottle_photo_url')) {
              productFormGroup.patchValue({ bottle_photo_url: (productToUpdate as any).bottle_photo_url || null }, { emitEvent: false });
            }
            if (productFormGroup.get('box_photo_url')) {
              productFormGroup.patchValue({ box_photo_url: (productToUpdate as any).box_photo_url || null }, { emitEvent: false });
            }
            if (productFormGroup.get('label_photo_url')) {
              productFormGroup.patchValue({ label_photo_url: (productToUpdate as any).label_photo_url || null }, { emitEvent: false });
            }
          } catch (e) {
            console.warn('Falha ao atualizar campos de foto do formulário do produto, aplicando fallback completo', e);
            productFormGroup.patchValue(productToUpdate, { emitEvent: false });
          }
        }

        this.cdr.detectChanges();
      }


      this.showSuccess('Foto do produto removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover foto do produto:', error);
    } finally {
      this.setProductPhotoUploading(productId, photoType, false);
    }
  }


  /**
   * Inicializa os arrays de controle para regiões de todos os produtos
   */
  initializeProductControls() {
    // Limpar os arrays
    this.selectedRegions = [];

    // Para cada produto no plano, inicialize os controles
    if (this.treatmentPlan && this.treatmentPlan.products && this.treatmentPlan.products.length > 0) {
      this.treatmentPlan.products.forEach((product, productIndex) => {
        this.selectedRegions[productIndex] = [];
        this.selectedRegions[productIndex][0] = '';
      });
    }
    // Removido: Criação automática de produto vazio quando não há produtos
    // Agora a aba produtos estará vazia até o usuário adicionar um produto manualmente

    // Limpar e inicializar o array de produtos disponíveis
    this.availableProducts = [];

    // Inicializar produtos disponíveis para cada produto existente
    if (this.treatmentPlan && this.treatmentPlan.products) {
      this.treatmentPlan.products.forEach((product, index) => {
        // Garantir que o array de produtos disponíveis tenha espaço para este índice
        while (this.availableProducts.length <= index) {
          this.availableProducts.push([]);
        }

        // Se o produto tem um fabricante, obter os produtos relacionados
        if (product.manufacturer && product.manufacturer !== 'Outro' && this.manufacturerProductMap) {
          this.availableProducts[index] = this.manufacturerProductMap[product.manufacturer] || [];
        } else {
          this.availableProducts[index] = [];
        }
      });
    }

    // Garantir que o array manufacterersList esteja inicializado
    if (!this.manufacturersList || this.manufacturersList.length === 0) {
      this.initManufacturerProductMap();
    }
  }

  // Método para esconder o formulário de novo produto
  hideNewProductForm(): void {
    this.showNewProductForm = false;
  }

  /**
   * Processa um arquivo de foto, adiciona-o ao produto correspondente (no modelo de dados local)
   * e retorna uma Promise que resolve com o array de fotos ATUALIZADO do produto.
   * @param skipSave Se o salvamento no final deve ser pulado (usado em uploads múltiplos).
   * @returns Uma Promise que resolve com o array de fotos do produto (IProductPhoto[]).
   */
  private storeProductPhotoTemporarily(skipSave: boolean = false): Promise<IProductPhoto[]> {
    // Envolvemos toda a lógica em uma Promise para controlar explicitamente quando ela termina (resolve) ou falha (reject).
    return new Promise((resolve, reject) => {

      // 1. Validação inicial: Verificamos se temos tudo o que precisamos antes de começar.
      if (!this.selectedProductPhotoType || !this.selectedProductPhotoFile || !this.selectedProductId) {
        const errorMsg = 'Dados incompletos para armazenar a foto. ID do produto, tipo da foto ou arquivo faltando.';
        console.error(errorMsg, {
          productId: this.selectedProductId,
          photoType: this.selectedProductPhotoType,
          file: !!this.selectedProductPhotoFile,
        });
        // Se a validação falhar, nós rejeitamos a Promise imediatamente.
        return reject(new Error(errorMsg));
      }

      // 2. Copiamos os dados para constantes locais para segurança dentro dos callbacks.
      const productId = this.selectedProductId;
      const photoType = this.selectedProductPhotoType;
      const photoFile = this.selectedProductPhotoFile;


      // 3. Iniciamos o FileReader.
      const reader = new FileReader();

      // 4. Definimos o que acontece quando o arquivo for lido com SUCESSO.
      reader.onload = async (e) => {
        try {
          const base64Url = e.target?.result as string;

          // Encontra o produto no nosso modelo de dados.
          let product = this.treatmentPlan.products.find(p => p.id === productId);

          // Se o produto não for encontrado no modelo (pode acontecer), usamos a lógica de fallback.
          if (!product) {
            console.warn(`Produto com ID ${productId} não encontrado no modelo, procurando no FormArray.`);
            const productIndex = this.productsArray.controls.findIndex(c => c.get('id')?.value === productId);

            if (productIndex >= 0) {
              // Se encontramos no formulário, usamos esse como base.
              product = this.productsArray.at(productIndex).value;
              // Garantimos que ele seja adicionado ao modelo se não estiver lá.
              if (!this.treatmentPlan.products.some(p => p.id === productId)) {
                this.treatmentPlan.products.push(product!);
              }
            } else {
              // Se não existe em lugar nenhum, é um erro.
              const errorMsg = `Produto ${productId} não foi encontrado em lugar nenhum.`;
              console.error(errorMsg);
              return reject(new Error(errorMsg));
            }
          }

          // 5. Chamamos o método que atualiza o array de fotos e nos RETORNA o novo array.
          const updatedPhotos = this.addPhotoToProduct(product!, base64Url, photoType);

          // Atualizamos o estado da aplicação
          this.hasUnsavedChanges = true;
          this.updateCanCloseTreatmentPlan();
          this.clearProductPhotoPreview(true);
          this.showUploadOptions = false;

          // 6. SUCESSO! Resolvemos a Promise, entregando o array de fotos atualizado para quem chamou (o 'await').
          resolve(updatedPhotos);

        } catch (error) {
          // Se qualquer coisa dentro do `onload` der errado, rejeitamos a Promise.
          reject(error);
        }
      };

      // 7. Definimos o que acontece se a leitura do arquivo FALHAR.
      reader.onerror = () => {
        console.error('Erro ao ler arquivo para converter em base64');
        // Rejeitamos a Promise com uma mensagem de erro.
        reject(new Error('Erro no FileReader ao processar a imagem'));
      };

      // 8. Inicia a operação de leitura do arquivo.
      reader.readAsDataURL(photoFile);
    });
  }

  // NOVO MÉTODO addPhotoToProduct
  private addPhotoToProduct(product: ITreatmentPlanProduct, base64Url: string, photoType: 'bottle' | 'box' | 'label'): IProductPhoto[] {

    // Inicializa o array de fotos se não existir, garantindo que seja um novo array.
    let currentPhotos = product.photos ? [...product.photos] : [];

    // Determinar a ordem da foto com base no tipo
    let photo_order = 0; // Valor padrão
    switch (photoType) {
      case 'bottle': photo_order = 0; break;
      case 'box': photo_order = 1; break;
      case 'label': photo_order = 2; break;
    }

    // Obter apenas a parte base64 da URL
    const photoBase64 = base64Url.split(',')[1] || base64Url;

    // Preparar o objeto de foto
    const photoData: IProductPhoto = {
      photo: photoBase64,
      photo_order,
      photo_url: base64Url
    };

    // Verificar se já existe uma foto com esta ordem
    const existingPhotoIndex = currentPhotos.findIndex(
      p => p.photo_order === photo_order
    );

    if (existingPhotoIndex !== -1) {
      currentPhotos = currentPhotos.map((photo, index) =>
        index === existingPhotoIndex ? photoData : photo
      );
    } else {
      currentPhotos = [...currentPhotos, photoData];
    }

    product.photos = currentPhotos;

    switch (photoType) {
      case 'bottle': (product as any).bottle_photo_url = base64Url; break;
      case 'box': (product as any).box_photo_url = base64Url; break;
      case 'label': (product as any).label_photo_url = base64Url; break;
    }

    this.updateExistingProductWithPhotos(product);
    this.cdr.detectChanges();

    return currentPhotos;
  }

  /**
   * Calcula o total de unidades para um produto específico
   * @deprecated Campos quantity/unit foram removidos
   */
  calculateProductTotalUnits(productIndex: number): number {
    return 0;
  }

  /**
   * Adiciona uma nova região e unidade para um produto específico
   * Método mantido para compatibilidade com código existente
   */
  addRegionToProduct(productIndex: number): void {
    this.addRegion(productIndex);
  }

  /**
   * Remove uma região e unidade de um produto específico
   * Método mantido para compatibilidade com código existente
   */
  removeRegionFromProduct(productIndex: number, regionIndex: number): void {
    this.removeRegion(productIndex, regionIndex);
  }

  // Método para converter as fotos do array para os campos individuais (para compatibilidade)
  updateExistingProductWithPhotos(product: ITreatmentPlanProduct): void {
    if (!product || !product.photos) return;

    // Encontrar o índice do produto no FormArray
    const productIndex = this.productsArray.controls.findIndex(
      control => control.get('id')?.value === product.id
    );

    if (productIndex === -1) return;

    // Atualizar o campo photos no FormGroup
    const formGroup = this.getProductFormGroup(productIndex);
    formGroup.get('photos')?.setValue(product.photos);

    // Forçar atualização da interface
    this.cdr.detectChanges();
  }

  // Método para remover produto do FormArray
  removeProductControl(productIndex: number): void {
    // Verificar se é produto válido
    const productId = this.productsArray.at(productIndex).get('id')?.value;
    if (productId) {
      // Chamar método de remoção direto
      this.removeProduct(productId);
    } else {
      // Apenas remover produtos sem ID (situação rara)
      this.productsArray.removeAt(productIndex);
    }
  }

  // Sincroniza o modelo com os dados do formulário
  syncFormToModel(): void {
    // Preservar dados importantes que não estão no formulário
    const clientId = this.treatmentPlan.client_id;
    const status = this.treatmentPlan.status;
    const created_at = this.treatmentPlan.created_at;

    // Atualizar observações no modelo
    this.treatmentPlan.observations = this.treatmentPlanForm.get('observations')?.value;

    // Atualizar idade do paciente, se editável
    if (this.treatmentPlanForm.get('patient_age')?.enabled) {
      this.treatmentPlan.patient_age = this.treatmentPlanForm.get('patient_age')?.value;
    }

    // Atualizar produtos mantendo a ordem original, PERMITINDO produtos incompletos para salvamento
    this.treatmentPlan.products = this.productsArray.controls
      .map((productControl, index): ITreatmentPlanProduct => {
        const product = productControl.value;

        // Converter apenas para o modelo (ISO) sem alterar o valor visível no formulário (dd/MM/yyyy)
        // Não forçar data atual caso seja inválida; manter vazio para evitar enviar data incorreta
        const normalizedApplicationDate = this.normalizeIsoDate(product.application_date) || '';

        product.application_date = normalizedApplicationDate;

        // Usar valores personalizados quando "Outro" for selecionado
        const finalManufacturer = product.manufacturer === 'Outro' ? product.custom_manufacturer : product.manufacturer;
        const finalProductName = product.product_name === 'Outro' ? product.custom_product_name : product.product_name;

        // Processar as regiões
        const regionsArray = (productControl.get('application_regions') as FormArray).controls;
        const applicationRegions = regionsArray.map((regionControl, regionIndex) => {
          const region = regionControl.value;

          // Usar a região personalizada se selecionado "Outra"
          const applicationSite = region.region === 'Outra' ? region.custom_region : region.region;

          return {
            id: region.id || '',
            application_site: applicationSite || '', // Garantir que não seja null/undefined
          };
        }).filter(r => r.application_site.trim()); // Filtrar regiões com application_site vazio

        // Criar string de regiões de aplicação para compatibilidade
        const applicationRegion = applicationRegions.map(region => region.application_site).filter(site => site).join(', ');

        // Garantir que as fotos sejam mantidas
        const existingProduct = this.treatmentPlan.products.find(p => p.id === product.id);
        const photos = existingProduct?.photos || product.photos || [];

        // Construir o objeto do produto atualizado com ordem preservada
        const updatedProduct: ITreatmentPlanProduct = {
          id: product.id || '', // Garantir que não seja null/undefined
          manufacturer: finalManufacturer || '', // ALTERAÇÃO: Permitir vazio
          application_date: product.application_date || '',
          product: finalProductName || '', // ALTERAÇÃO: Permitir vazio
          product_name: finalProductName || '', // ALTERAÇÃO: Permitir vazio
          application_region: applicationRegion,
          application_regions: applicationRegions,
          photos: photos,
          product_order: index, // Garantir que a ordem seja preservada
          total_quantity: (product.total_quantity !== null && product.total_quantity !== undefined && product.total_quantity !== '') && Number.isFinite(Number(product.total_quantity)) ? Number(product.total_quantity) : undefined,
          total_unit: product.total_unit ?? 'ml'
        };

        return updatedProduct;
      });

    // Restaurar dados preservados
    this.treatmentPlan.client_id = clientId;
    this.treatmentPlan.status = status;
    this.treatmentPlan.created_at = created_at;

  }

  // Método para salvar manualmente o plano de tratamento
  async saveTreatmentPlan(showSaving: boolean = true): Promise<void> {

    if (this.treatmentPlan == null || this.treatmentPlan == undefined) {
      return;
    }


    // Só define saving se não estiver sendo chamado através do executeSave
    // (que já define saving corretamente)
    if (!this.saving) {
      this.saving = showSaving;
      // Forçar detecção de mudanças imediatamente
      this.cdr.detectChanges();
    } else {
      // Forçar detecção de mudanças mesmo quando mantém o valor
      this.cdr.detectChanges();
    }

    try {
      // Sincronizar dados do formulário para o modelo
      this.syncFormToModel();

      // Processar fotos temporárias de produtos
      for (const productId in this.tempProductPhotos) {
        if (productId) {
          // Buscar produto pelo ID real
          const product = this.treatmentPlan.products.find(p => p.id === productId);

          if (product && !product.photos) {
            product.photos = [];
          }
        }
      }

      // Processar fotos temporárias de tratamento
      if (this.treatmentPlan.photos && this.treatmentPlan.photos.length > 0) {
        // Converter os tipos de fotos para o formato esperado pelo backend
        this.treatmentPlan.photos = this.treatmentPlan.photos.map(photo => {
          const updatedPhoto = { ...photo };
          if (updatedPhoto.type === 'PRE') {
            updatedPhoto.type = 'PRE_TREATMENT';
          } else if (updatedPhoto.type === 'POST') {
            updatedPhoto.type = 'POST_TREATMENT';
          }
          return updatedPhoto;
        });
      }

      // Como não gerenciamos fotos por aqui, vamos remover as fotos do envio do endpoint.
      var finalTreatmentPlan = {
        ...this.treatmentPlan,
        photos: [],
        products: this.treatmentPlan.products.map(product => {
          const sanitizedApplicationDate = this.normalizeIsoDate(product.application_date) || undefined;

          // Construir payload do produto e garantir que datas inválidas não sejam enviadas
          const payload: any = { ...product, photos: [] };
          // Remover qualquer valor de data existente (ex.: string dd/MM/yyyy)
          delete payload.application_date;
          // Incluir apenas se normalizadas
          if (sanitizedApplicationDate) payload.application_date = sanitizedApplicationDate;
          return payload;
        })
      }


      // Enviar o plano completo para o backend
      return this.treatmentPlanService.updateTreatmentPlan(this.treatmentPlanId, finalTreatmentPlan)
        .then(async response => {

          // Atualizar o modelo local com a resposta do servidor
          this.treatmentPlan = {
            ...this.treatmentPlan,
            id: response.id,
            updated_at: response.updated_at
          };

          // Resetar flag de alterações não salvas
          this.hasUnsavedChanges = false;

          this.saving = false;

          // Forçar detecção de mudanças
          this.cdr.detectChanges();
        })
        .catch(error => {
          console.error('💾 [SAVE_TREATMENT] Erro ao salvar plano de tratamento:', error);
          this.saving = false;
        });
    } catch (error) {
      console.error('💾 [SAVE_TREATMENT] Erro ao preparar dados para salvamento:', error);
      this.saving = false;
    }
  }

  // Método auxiliar para obter o nome da aba atual para exibição
  getTabDisplayName(): string {
    switch (this.activeTab) {
      case 'info':
        return 'Informações Gerais';
      case 'products':
        return 'Produtos';
      case 'photos':
        return 'Fotos';
      case 'intercorrencias':
        return 'Intercorrência';
      default:
        return 'atual';
    }
  }

  // ─── Intercorrências ────────────────────────────────────────────────

  /**
   * Atualiza a config do seletor de regiões da intercorrência
   * mantendo as pré-seleções mas atualizando as listas de regiões faciais/corporais.
   */
  private refreshIncidentRegionSelectorConfig(): void {
    if (this.incidentRegionSelectorConfig['Área tratada']) {
      this.incidentRegionSelectorConfig = {
        ...this.incidentRegionSelectorConfig,
        'Área tratada': {
          ...this.incidentRegionSelectorConfig['Área tratada'],
          facial: this.getFacialRegions(),
          corporal: this.getCorporalRegions()
        }
      };
    }
  }

  /** Carrega perguntas da clínica e intercorrências do plano */
  async loadIntercorrencias(): Promise<void> {
    if (this.loadingIntercorrencias) return;
    this.loadingIntercorrencias = true;

    try {
      // ── 1. Construir a seção fixa com as 3 perguntas obrigatórias ──────

      // 1a. "Data do procedimento" — DATE, pré-preenchido com created_at do plano
      let dateValue = '';
      if (this.treatmentPlan?.created_at) {
        const createdAt = this.treatmentPlan.created_at;
        if (createdAt.includes('T')) {
          dateValue = createdAt.split('T')[0];
        } else if (createdAt.includes('/')) {
          const parts = createdAt.split('/');
          if (parts.length === 3) {
            dateValue = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          }
        } else if (/^\d{4}-\d{2}-\d{2}/.test(createdAt)) {
          dateValue = createdAt.substring(0, 10);
        }
      }

      // 1b. "Procedimento da intercorrência" — MULTIPLE_CHOICE com opções dos procedimentos contratados
      let procedureOptions: any[] = [];
      if (this.treatmentPlan?.contracted_procedures) {
        // Separar por ", " seguido de letra maiúscula (evita quebrar nomes com vírgula interna)
        // Ex: "Bioestimulador de colágeno HaCa ( 2 seringas), Preenchimento labial"
        //     → ["Bioestimulador de colágeno HaCa ( 2 seringas)", "Preenchimento labial"]
        // Também trata " e " como separador (antes de maiúscula)
        const raw = this.treatmentPlan.contracted_procedures;
        const procedures = raw
          .split(/,\s*(?=[A-ZÀ-ÚÇ])/)
          .flatMap((part: string) => part.split(/\s+e\s+(?=[A-ZÀ-ÚÇ])/))
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0);
        procedureOptions = procedures.map((proc, idx) => ({
          id: proc,
          option_text: proc,
          option_order: idx + 1,
          triggers_free_text: false
        }));
        console.log('[loadIntercorrencias] contracted_procedures RAW:', JSON.stringify(this.treatmentPlan.contracted_procedures));
        console.log('[loadIntercorrencias] procedureOptions IDs:', procedures);
      }

      // 1c. "Área tratada" — TEXT_INPUT (renderizado como seletor de regiões)
      // Pré-seleciona as regiões de aplicação de todos os produtos do plano
      const allProductRegions: string[] = [];
      if (this.treatmentPlan?.products && this.treatmentPlan.products.length > 0) {
        for (const product of this.treatmentPlan.products) {
          if (product.application_regions && product.application_regions.length > 0) {
            for (const region of product.application_regions) {
              const site = region.application_site;
              if (site && !allProductRegions.includes(site)) {
                allProductRegions.push(site);
              }
            }
          }
        }
      }

      const fixedSection: IIncidentSection = {
        id: '__fixed_section__',
        section_title: 'Informações do Procedimento',
        section_order: 0,
        questions: [
          {
            id: '__fixed_data_procedimento__',
            question_text: 'Data do procedimento',
            type: 'DATE',
            is_required: true,
            placeholder: '',
            order_in_section: 1,
            options: []
          },
          {
            id: '__fixed_procedimento__',
            question_text: 'Procedimento da intercorrência',
            type: 'MULTIPLE_CHOICE',
            is_required: true,
            placeholder: '',
            order_in_section: 2,
            options: procedureOptions
          },
          {
            id: '__fixed_area_tratada__',
            question_text: 'Área tratada',
            type: 'TEXT_INPUT',
            is_required: true,
            placeholder: '',
            order_in_section: 3,
            options: []
          }
        ]
      };

      // ── 2. Buscar perguntas configuráveis da clínica (se houver) ──────
      let clinicSections: IIncidentSection[] = [];
      try {
        const latest: any = await this.incidentService.getLatestQuestions();
        console.log('[loadIntercorrencias] Perguntas da clínica:', latest);
        const latestSections: any[] = Array.isArray(latest?.sections) ? latest.sections : [];

        clinicSections = latestSections.map((ls: any, idx: number) => ({
          id: ls.section_id || ls.id || '',
          section_title: ls.section_name || ls.section_title || '',
          section_order: (ls.section_order || (idx + 1)) + 1, // +1 para ficar depois da seção fixa
          questions: (Array.isArray(ls.questions) ? ls.questions : []).map((q: any) => ({
            id: q.id || '',
            question_text: q.label ?? q.question_text ?? '',
            type: q.type || 'TEXT_INPUT',
            is_required: q.is_required ?? false,
            placeholder: q.placeholder ?? '',
            order_in_section: q.question_order ?? q.order_in_section ?? 0,
            options: (Array.isArray(q.options) ? q.options : []).map((o: any) => ({
              id: o.id || '',
              option_text: o.option_label ?? o.option_text ?? '',
              option_order: o.option_order ?? 0,
              triggers_free_text: o.triggers_free_text ?? false,
              sub_questions: (Array.isArray(o.sub_questions) ? o.sub_questions : []).map((sq: any) => ({
                id: sq.id || '',
                question_text: sq.label ?? sq.question_text ?? '',
                type: sq.type || 'TEXT_INPUT',
                is_required: sq.is_required ?? false,
                placeholder: sq.placeholder ?? '',
                order_in_section: sq.question_order ?? sq.order_in_section ?? 0,
                options: (Array.isArray(sq.options) ? sq.options : []).map((sqo: any) => ({
                  id: sqo.id || '',
                  option_text: sqo.option_label ?? sqo.option_text ?? '',
                  option_order: sqo.option_order ?? 0,
                  triggers_free_text: sqo.triggers_free_text ?? false
                }))
              }))
            })),
            object_fields: (Array.isArray(q.object_fields) ? q.object_fields : []).map((f: any) => ({
              id: f.id || '',
              field_key: f.field_key || f.fieldKey || '',
              field_label: f.field_label || f.fieldLabel || '',
              field_type: f.field_type || f.fieldType || 'TEXT',
              placeholder: f.placeholder || '',
              is_required: f.is_required ?? f.isRequired ?? false,
              field_order: f.field_order ?? f.fieldOrder ?? 0,
              select_options: Array.isArray(f.select_options ?? f.selectOptions) ? (f.select_options ?? f.selectOptions) : []
            }))
          }))
        } as IIncidentSection));
      } catch (clinicErr: any) {
        console.warn('[loadIntercorrencias] Sem perguntas da clínica (OK):', clinicErr?.status);
      }

      // ── 3. Montar seções finais: fixa + clínica ──────
      this.incidentSections = [fixedSection, ...clinicSections];
      console.log('[loadIntercorrencias] incidentSections (fixa + clínica):', JSON.stringify(this.incidentSections, null, 2));

      // Indica se existem perguntas configuradas pela clínica, além da seção fixa
      this.hasClinicQuestions = clinicSections.length > 0;

      // ── 4. Configurar valores fixos, opções dinâmicas e seletor de regiões ──────
      this.incidentFixedValues = {};
      if (dateValue) {
        this.incidentFixedValues['Data do procedimento'] = dateValue;
      }

      // dynamicOptions não é mais necessário (opções já estão na pergunta fixa),
      // mas mantemos vazio para compatibilidade
      this.incidentDynamicOptions = {};

      // Config do seletor de regiões para "Área tratada"
      this.incidentRegionSelectorConfig = {
        'Área tratada': {
          facial: this.getFacialRegions(),
          corporal: this.getCorporalRegions(),
          preSelected: allProductRegions
        }
      };
      console.log('[loadIntercorrencias] regionSelectorConfig:', this.incidentRegionSelectorConfig);

      // ── 5. Buscar intercorrências existentes deste plano ──────
      try {
        if (this.treatmentPlanId) {
          this.intercorrencias = await this.incidentService.getIncidentReports(this.treatmentPlanId);
          console.log('[loadIntercorrencias] intercorrencias carregadas:', this.intercorrencias?.length, JSON.stringify(this.intercorrencias?.[0], null, 2));
          if (this.intercorrencias && this.intercorrencias.length > 0) {
            const first = this.intercorrencias[0];
            this.editingIncidentId = first.id || null;
            this.editingIncidentStatus = first.status || 'ACTIVE';
            this.editingIncidentAnswers = this.extractAnswersFromReport(first);
            this.editingIncidentAttachments = first.attachments || [];
            console.log('[loadIntercorrencias] editingIncidentAnswers:', this.editingIncidentAnswers.length, JSON.stringify(this.editingIncidentAnswers.slice(0, 5), null, 2));
            console.log('[loadIntercorrencias] editingIncidentAttachments:', this.editingIncidentAttachments.length);
            this.showIncidentForm = true;
          }
        }
      } catch (err) {
        console.warn('[loadIntercorrencias] Não foi possível carregar intercorrências existentes:', err);
      }

    } catch (error: any) {
      console.error('Erro ao carregar intercorrências:', error);
      // Mesmo com erro, as 3 perguntas fixas sempre existem
      this.hasClinicQuestions = true;
    } finally {
      this.loadingIntercorrencias = false;
    }
  }

  /** Exibe o formulário para registrar nova intercorrência */
  startNewIncident(): void {
    this.editingIncidentId = null;
    this.editingIncidentStatus = 'ACTIVE';
    this.editingIncidentAnswers = [];
    this.editingIncidentAttachments = [];
    this.showIncidentForm = true;
  }

  /** Cancela o formulário de intercorrência */
  cancelIncidentForm(): void {
    this.showIncidentForm = false;
    this.editingIncidentId = null;
    this.editingIncidentStatus = 'ACTIVE';
    this.editingIncidentAnswers = [];
    this.editingIncidentAttachments = [];
  }

  /**
   * Extrai respostas de um relatório retornado pelo backend.
   * O backend retorna respostas embutidas em form.sections[].questions[] (answer, observation, object_entries).
   * Quando há um array answers[] direto, usa-o como fallback (formato legado).
   */
  private extractAnswersFromReport(report: any): IIncidentAnswer[] {
    const answers: IIncidentAnswer[] = [];
    const seenIds = new Set<string>();

    console.log('[extractAnswersFromReport] report keys:', Object.keys(report || {}));
    console.log('[extractAnswersFromReport] answers count:', report?.answers?.length);
    console.log('[extractAnswersFromReport] fixed_answers count:', report?.fixed_answers?.length);
    // Log detalhado das respostas do backend
    if (Array.isArray(report.answers)) {
      for (const a of report.answers) {
        console.log('[extractAnswersFromReport] raw answer:', JSON.stringify({ qid: a.question_id, answer: a.answer }));
      }
    }

    // ── 1. Fonte primária: answers[] — lista flat com TODAS as respostas (fixas + clínica) ──
    if (Array.isArray(report.answers)) {
      for (const a of report.answers) {
        const qId = a.question_id ?? a.questionId ?? '';
        if (qId && !seenIds.has(qId)) {
          seenIds.add(qId);
          answers.push({
            question_id: qId,
            answer: a.answer ?? a.answer_text ?? a.answerText ?? '',
            observation: a.observation ?? '',
            object_entries: (Array.isArray(a.object_entries ?? a.objectEntries)
              ? (a.object_entries ?? a.objectEntries) : []).map((e: any) => ({
                id: e.id || '',
                entry_order: e.entry_order ?? e.entryOrder ?? 0,
                values: (Array.isArray(e.values) ? e.values : []).map((v: any) => ({
                  field_key: v.field_key ?? v.fieldKey ?? '',
                  field_label: v.field_label ?? v.fieldLabel ?? '',
                  field_value: v.field_value ?? v.fieldValue ?? ''
                }))
              }))
          });
        }
      }
    }

    // ── 2. Complemento: fixed_answers[] — caso answers[] não contenha as fixas ──
    const fixedArr = report.fixed_answers ?? report.fixedAnswers;
    if (Array.isArray(fixedArr)) {
      for (const fa of fixedArr) {
        const qId = fa.question_id_text ?? fa.questionIdText ?? fa.question_id ?? fa.questionId ?? '';
        if (qId && !seenIds.has(qId)) {
          seenIds.add(qId);
          answers.push({
            question_id: qId,
            answer: fa.answer ?? fa.answer_text ?? fa.answerText ?? '',
            observation: fa.observation ?? '',
            object_entries: []
          });
        }
      }
    }

    // ── 3. Complemento: form.sections[].questions[] — respostas embutidas nas perguntas ──
    const form = report.form;
    if (form && Array.isArray(form.sections)) {
      for (const section of form.sections) {
        const questions = section.questions || [];
        for (const q of questions) {
          const qId = q.id ?? '';
          if (qId && !seenIds.has(qId) && (q.answer || q.observation || (q.object_entries && q.object_entries.length > 0))) {
            seenIds.add(qId);
            answers.push({
              question_id: qId,
              answer: q.answer ?? '',
              observation: q.observation ?? '',
              object_entries: (Array.isArray(q.object_entries ?? q.objectEntries)
                ? (q.object_entries ?? q.objectEntries) : []).map((e: any) => ({
                  id: e.id || '',
                  entry_order: e.entry_order ?? e.entryOrder ?? 0,
                  values: (Array.isArray(e.values) ? e.values : []).map((v: any) => ({
                    field_key: v.field_key ?? v.fieldKey ?? '',
                    field_label: v.field_label ?? v.fieldLabel ?? '',
                    field_value: v.field_value ?? v.fieldValue ?? ''
                  }))
                }))
            });
          }

          // Sub-perguntas das opções
          if (Array.isArray(q.options)) {
            for (const opt of q.options) {
              const subQuestions = opt.sub_questions ?? opt.subQuestions ?? [];
              for (const subQ of subQuestions) {
                const sqId = subQ.id ?? '';
                if (sqId && !seenIds.has(sqId) && (subQ.answer || subQ.observation || (subQ.object_entries && subQ.object_entries.length > 0))) {
                  seenIds.add(sqId);
                  answers.push({
                    question_id: sqId,
                    answer: subQ.answer ?? '',
                    observation: subQ.observation ?? '',
                    object_entries: (Array.isArray(subQ.object_entries ?? subQ.objectEntries)
                      ? (subQ.object_entries ?? subQ.objectEntries) : []).map((e: any) => ({
                        id: e.id || '',
                        entry_order: e.entry_order ?? e.entryOrder ?? 0,
                        values: (Array.isArray(e.values) ? e.values : []).map((v: any) => ({
                          field_key: v.field_key ?? v.fieldKey ?? '',
                          field_label: v.field_label ?? v.fieldLabel ?? '',
                          field_value: v.field_value ?? v.fieldValue ?? ''
                        }))
                      }))
                  });
                }
              }
            }
          }
        }
      }
    }

    console.log('[extractAnswersFromReport] total extracted answers:', answers.length);
    return answers;
  }

  /** Salva ou atualiza uma intercorrência */
  async saveIncident(payload: { answers: IIncidentAnswer[]; attachments: any[] }): Promise<void> {
    if (!this.treatmentPlanId) return;
    this.savingIncident = true;

    const { answers, attachments } = payload;

    try {
      let reportId: string | undefined;
      if (this.editingIncidentId) {
        // Atualizar existente (backend expects only { answers })
        const updatedReport = await this.incidentService.updateIncidentReport(
          this.treatmentPlanId,
          this.editingIncidentId,
          { answers } as any
        );
        reportId = updatedReport?.id || this.editingIncidentId;
      } else {
        // Criar novo (backend expects only { answers })
        const createdReport = await this.incidentService.saveIncidentReport(
          this.treatmentPlanId,
          { answers } as any
        );
        reportId = createdReport?.id;
      }

      // Após salvar o relatório, registrar anexos novos (sem id)
      if (reportId && attachments && attachments.length > 0) {
        for (const [idx, att] of attachments.entries()) {
          if (!att.id) {
            // Montar dados para o backend
            const data = {
              file_name: att.file_name,
              // backend expects file_type — our model uses content_type
              file_type: (att as any).content_type || (att as any).file_type || '',
              file_size: att.file_size,
              storage_key: att.storage_key,
              attachment_order: idx
            };
            try {
              await this.attachmentService.registerAttachment(this.treatmentPlanId, data);
            } catch (err) {
              console.error('Erro ao registrar anexo:', err);
            }
          }
        }
      }

      // Recarregar dados da intercorrência salva e manter formulário aberto
      this.intercorrencias = await this.incidentService.getIncidentReports(this.treatmentPlanId);
      if (this.intercorrencias && this.intercorrencias.length > 0) {
        const saved = this.intercorrencias[0];
        this.editingIncidentId = saved.id || null;
        this.editingIncidentStatus = saved.status || 'ACTIVE';
        this.editingIncidentAnswers = this.extractAnswersFromReport(saved);
        this.editingIncidentAttachments = saved.attachments || [];
      }
      this.showIncidentForm = true;
      this.store.dispatch(setWarningText('Intercorrência salva com sucesso.', 200));
    } catch (error) {
      console.error('Erro ao salvar intercorrência:', error);
      this.store.dispatch(setWarningText('Erro ao salvar intercorrência. Tente novamente.', 500));
    } finally {
      this.savingIncident = false;
    }
  }

  /** Finaliza a intercorrência — o PDF é gerado no backend (Thymeleaf + openhtmltopdf), muda status para CLOSED (irreversível) */
  async finalizeIncident(): Promise<void> {
    if (!this.treatmentPlanId) return;
    this.finalizingIncident = true;

    try {
      const result = await this.incidentService.finalizeIncidentReport(this.treatmentPlanId);

      // Atualizar estado local
      this.editingIncidentStatus = result.status || 'CLOSED';

      // Recarregar dados completos
      this.intercorrencias = await this.incidentService.getIncidentReports(this.treatmentPlanId);
      if (this.intercorrencias && this.intercorrencias.length > 0) {
        const saved = this.intercorrencias[0];
        this.editingIncidentId = saved.id || null;
        this.editingIncidentStatus = saved.status || 'CLOSED';
        this.editingIncidentAnswers = this.extractAnswersFromReport(saved);
        this.editingIncidentAttachments = saved.attachments || [];
      }

      this.showIncidentForm = true;
      this.store.dispatch(setWarningText('Intercorrência finalizada com sucesso.', 200));
    } catch (error) {
      console.error('Erro ao finalizar intercorrência:', error);
      this.store.dispatch(setWarningText('Erro ao finalizar intercorrência. Tente novamente.', 500));
    } finally {
      this.finalizingIncident = false;
    }
  }

  /**
   * Baixa o PDF da intercorrência gerado no backend.
   * Dispara download automático no browser.
   */
  async downloadIncidentPdf(): Promise<void> {
    if (!this.treatmentPlanId) return;
    try {
      const blob = await this.incidentService.downloadIncidentPdf(this.treatmentPlanId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Intercorrencia_${this.treatmentPlanId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF da intercorrência:', error);
      this.store.dispatch(setWarningText('Erro ao baixar o PDF. Tente novamente.', 500));
    }
  }

  /**
   * Reenvia o e-mail da intercorrência para a Autentique.
   */
  async resendIncidentEmail(): Promise<void> {
    if (!this.treatmentPlanId) return;
    try {
      await this.incidentService.finalizeIncidentReport(this.treatmentPlanId);
      this.store.dispatch(setWarningText('E-mail reenviado com sucesso.', 200));
    } catch (error) {
      console.error('Erro ao reenviar e-mail da intercorrência:', error);
      this.store.dispatch(setWarningText('Erro ao reenviar o e-mail. Tente novamente.', 500));
    }
  }

  /**
   * Método auxiliar para diagnosticar o estado das fotos temporárias
   * Útil para depuração quando as fotos não estão sendo enviadas corretamente
   */
  debugTempPhotos(): void {

    // Verificar a quantidade total de fotos temporárias
    const totalProductsWithPhotos = Object.keys(this.tempProductPhotos).length;

    // Listar cada produto e suas fotos
    for (const productId in this.tempProductPhotos) {
      const productPhotos = this.tempProductPhotos[productId];
      const photoCount = Object.keys(productPhotos).length;


      // Listar cada tipo de foto
      for (const photoType in productPhotos) {
        const file = productPhotos[photoType];
      }
    }

  }

  /**
   * Método para forçar o envio de todas as fotos temporárias pendentes
   * Útil quando o auto-salvamento não conseguiu processar as fotos
   */
  forceUploadAllPendingPhotos(): void {

    // Para cada produto com fotos temporárias
    for (const productId in this.tempProductPhotos) {
      // Processar todos os produtos (locais serão enviados junto com o salvamento)
      this.processProductPhotos(productId);
    }
  }

  /**
   * Processa uploads de fotos pendentes para um produto
   */
  processProductPhotos(productId: string): void {
    // Como estamos enviando tudo de uma vez, este método não precisa fazer upload separado
  }

  /**
   * Função auxiliar para obter a URL da foto de um produto com base no tipo
   */
  getProductPhotoUrl(product: ITreatmentPlanProduct, photoType: 'bottle' | 'box' | 'label'): string | null {
    if (!product || !product.photos || !product.photos.length) return null;

    // Determinar a ordem da foto com base no tipo
    let photo_order = -1;
    switch (photoType) {
      case 'bottle': photo_order = 0; break;
      case 'box': photo_order = 1; break;
      case 'label': photo_order = 2; break;
    }

    // Encontrar a foto pelo photo_order
    const photo = product.photos.find(p => p.photo_order === photo_order);
    if (!photo) return null;

    return photo.photo_url || this.photoCaptureService.isUrl(photo.photo) ? photo.photo : `data:image/jpeg;base64,${photo.photo}`;
  }

  /**
   * Função auxiliar para verificar se um produto tem foto de um tipo específico
   * Agora com verificação direta das fotos para garantir atualizações em tempo real
   */
  hasProductPhoto(product: ITreatmentPlanProduct, photoType: 'bottle' | 'box' | 'label'): boolean {
    if (!product || !product.photos || !product.photos.length) return false;

    // Determinar a ordem da foto com base no tipo
    let photo_order = -1;
    switch (photoType) {
      case 'bottle': photo_order = 0; break;
      case 'box': photo_order = 1; break;
      case 'label': photo_order = 2; break;
    }

    // Verificar se existe uma foto com o photo_order correspondente
    return product.photos.some(photo => photo.photo_order === photo_order);
  }

  // Métodos para gerenciar o FormArray de produtos
  get productsArray(): FormArray {
    try {
      if (!this.treatmentPlanForm) {
        return this.fb.array([]);
      }
      const products = this.treatmentPlanForm.get('products');
      if (!products) {
        return this.fb.array([]);
      }
      return products as FormArray;
    } catch (error) {
      console.error('Erro ao acessar productsArray:', error);
      return this.fb.array([]);
    }
  }

  // Verifica se o browser está em um dispositivo desktop (não mobile ou tablet)
  isDesktopBrowser(): boolean {
    // Verifica se a largura da tela é maior que 768px (tablet portrait) e não é um dispositivo touch
    return window.innerWidth > 768 && !('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }

  // Inicia captura de foto para produtos
  initPhotoCapture(productId: string, photoType: 'bottle' | 'box' | 'label'): void {
    if (!productId) {
      return;
    }

    // NOVO: Verificar se já há uma foto sendo enviada para este produto e tipo
    if (this.isProductPhotoUploading(productId, photoType)) {
      return;
    }

    this.selectedProductId = productId;
    this.selectedProductPhotoType = photoType;
    this.currentUploadType = 'product';
    this.showUploadOptions = true; // Abre o modal de opções (Câmera, Galeria, QR Code)
  }


  /**
   * Verifica se um produto pode receber fotos
   * Para produtos locais: sempre permitir (validação será feita no salvamento)
   * Para produtos salvos: exigir dados mínimos
   */
  private canAddPhotosToProduct(product: ITreatmentPlanProduct): boolean {
    if (!product) return false;
    return true;
  }

  /**
   * Verifica se um produto local está válido para salvamento
   */
  private isProductValidForSaving(product: ITreatmentPlanProduct): boolean {
    if (!product) return false;

    return !!(
      product.manufacturer &&
      product.product_name &&
      product.manufacturer.trim() !== '' &&
      product.product_name.trim() !== '' &&
      product.application_regions &&
      product.application_regions.length > 0 &&
      product.application_regions.some(region =>
        region.application_site &&
        region.application_site.trim() !== ''
      )
    );
  }

  getRegionsArray(productIndex: number): FormArray {
    try {
      if (!this.treatmentPlanForm || !this.productsArray || productIndex >= this.productsArray.length || productIndex < 0) {
        return this.fb.array([]);
      }
      const productGroup = this.getProductFormGroup(productIndex);
      if (!productGroup) {
        return this.fb.array([]);
      }
      const regions = productGroup.get('application_regions');
      if (!regions) {
        return this.fb.array([]);
      }
      return regions as FormArray;
    } catch (error) {
      console.error('Erro ao acessar getRegionsArray:', error);
      return this.fb.array([]);
    }
  }

  addRegion(productIndex: number): void {
    const regionsArray = this.getRegionsArray(productIndex);
    regionsArray.push(this.fb.group({
      region: ['', Validators.required],
      custom_region: ['']
    }));

    // Marcar que há alterações não salvas
    this.hasUnsavedChanges = true;

    // Autosave após adicionar região
    this.requestSave(`region_added:${productIndex}`, true, 1200);
  }

  removeRegion(productIndex: number, regionIndex: number): void {
    const regions = this.getRegionsArray(productIndex);
    if (regions.length > 1) {
      regions.removeAt(regionIndex);

      // Marcar que há alterações não salvas
      this.hasUnsavedChanges = true;

      // Atualizar estado do botão de encerramento
      this.updateCanCloseTreatmentPlan();

      // Atualizar a interface
      this.cdr.detectChanges();

      // Autosave após remover região
      this.requestSave(`region_removed:${productIndex}:${regionIndex}`, true, 1200);
    }
  }

  addExistingProduct(product: ITreatmentPlanProduct): void {

    // Verificar se o fabricante é personalizado (não está na lista padrão)
    const isCustomManufacturer = product.manufacturer && !this.manufacturersList.includes(product.manufacturer);
    const manufacturerValue = isCustomManufacturer ? 'Outro' : (product.manufacturer || '');
    const customManufacturerValue = isCustomManufacturer ? product.manufacturer : '';

    // Verificar se o produto é personalizado (não está na lista do fabricante)
    const availableProductsForManufacturer = product.manufacturer ? this.manufacturerProductMap[product.manufacturer] : [];
    const isCustomProduct = isCustomManufacturer || (product.product_name && availableProductsForManufacturer && !availableProductsForManufacturer.includes(product.product_name));
    const productNameValue = isCustomProduct ? 'Outro' : (product.product_name || product.product || '');
    const customProductNameValue = isCustomProduct ? (product.product_name || product.product || '') : '';

    const applicationDateFormValue = this.toFormDateValue(product.application_date);

    // Criar o formulário do produto — inclui `version` como campo oculto para
    // preservar o valor ao serializar via `syncFormToModel` (optimistic locking).
    const productForm = this.fb.group({
      id: [product.id || ''],
      version: [product.version ?? null],
      manufacturer: [manufacturerValue, Validators.required],
      custom_manufacturer: [customManufacturerValue, isCustomManufacturer ? [Validators.required] : []],
      product_name: [productNameValue, Validators.required],
      custom_product_name: [customProductNameValue, isCustomProduct ? [Validators.required] : []],
      application_date: new FormControl(
        applicationDateFormValue,
        { validators: [Validators.required, this.brDateValidator()], updateOn: 'blur' }
      ),
      total_quantity: [product.total_quantity ?? null],
      total_unit: [product.total_unit ?? 'ml'],
      photos: [product.photos || []],
      application_regions: this.fb.array([])
    });

    // Adicionar regiões existentes
    const regionsArray = productForm.get('application_regions') as FormArray;

    // Verificar se o produto tem regiões de aplicação
    if (product.application_regions && product.application_regions.length > 0) {
      // Adicionar cada região existente como um FormGroup
      // Filtrar regiões com application_site vazio para evitar phantom "Região personalizada"
      product.application_regions.filter(region => region.application_site && region.application_site.trim()).forEach(region => {
        const regionName = region.application_site || '';
        const isStandardRegion = this.isRegionInStandardList(regionName);

        regionsArray.push(this.fb.group({
          id: [region.id || ''],
          version: [region.version ?? null],
          region: [isStandardRegion ? regionName : 'Outra', Validators.required],
          custom_region: [!isStandardRegion ? regionName : '', isStandardRegion ? [] : [Validators.required]]
        }));
      });
    }
    // Com o checkbox multi-select, não precisamos de uma região vazia padrão

    // Adicionar o produto ao formulário
    this.productsArray.push(productForm);

    // Inicializar produtos disponíveis para este produto
    const index = this.productsArray.length - 1;
    if (product.manufacturer && product.manufacturer !== 'Outro') {
      // Garantir que o array tenha espaço
      while (this.availableProducts.length <= index) {
        this.availableProducts.push([]);
      }
      // Preencher com os produtos do fabricante
      this.availableProducts[index] = this.manufacturerProductMap[product.manufacturer] || [];
    } else {
      this.availableProducts[index] = [];
    }

  }


  /**
   * Gera um ID local único para novos produtos
   */
  private generateLocalProductId(): string {
    return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Verifica se um produto é local (ainda não salvo no servidor)
   */
  /**
   * Adicionar produto com ID real do banco IMEDIATAMENTE
   */
  async addNewProduct(): Promise<void> {
    try {

      // Mostrar loading global
      this.loading = true;

      // 1. Criar produto no banco (só para obter ID)
      const createdProduct = await this.treatmentPlanService.createQuickProduct(this.treatmentPlan.id);

      // 2. Criar produto local com ID real do banco.
      // IMPORTANTE: propagamos `version` recebido do backend para que, em updates
      // subsequentes, o optimistic locking consiga detectar conflitos de edição.
      const newProduct: ITreatmentPlanProduct = {
        id: createdProduct.id, // ID REAL DO BANCO
        version: createdProduct.version, // @Version para optimistic locking
        manufacturer: '',
        product_name: '',
        application_date: this.getCurrentDate(),
        application_regions: [],
        photos: [],
        total_quantity: undefined,
        total_unit: 'ml'
      };

      // 4. Criar FormGroup — inclui `version` como campo oculto para que o value
      // do form preserve o valor ao serializar em `syncFormToModel`.
      const newProductForm = this.fb.group({
        id: [createdProduct.id],
        version: [createdProduct.version ?? null],
        manufacturer: ['', Validators.required],
        custom_manufacturer: [''],
        product_name: ['', Validators.required],
        custom_product_name: [''],
        application_date: new FormControl(
          this.toFormDateValue(this.getCurrentDate()),
          { validators: [Validators.required, this.brDateValidator()], updateOn: 'blur' }
        ),
        total_quantity: [null],
        total_unit: ['ml'],
        photos: [[]],
        application_regions: this.fb.array([])
      });

      // Com checkbox multi-select, não precisamos de região vazia padrão

      this.productsArray.push(newProductForm);
      this.treatmentPlan.products.push(newProductForm.value as ITreatmentPlanProduct);

      // Inicializar array vazio para produtos disponíveis
      this.availableProducts.push([]);

      // Marcar que há alterações não salvas
      this.hasUnsavedChanges = true;

      // Atualizar estado do botão de encerramento
      this.updateCanCloseTreatmentPlan();

      this.cdr.detectChanges();

      this.processApiDataForFrontend();

      // Disparar salvamento automático para persistir os dados iniciais do produto
      this.requestSave('new_product_created', true, 1500);
      
    } catch (error) {
      console.error('❌ Erro ao criar produto:', error);
      // Mostrar notificação de erro
    } finally {
      this.loading = false;
    }
  }

  /**
   * Sanitiza URLs para exibição segura na interface
   * Necessário mesmo para imagens base64 devido às verificações de segurança do Angular
   */
  sanitizeUrl(url: string | null | undefined): any {
    if (!url || url === 'null' || url === 'undefined' || url === '') {
      return '';
    }

    try {
      // Bypass de segurança para qualquer URL válida
      return this.sanitizer.bypassSecurityTrustUrl(url);
    } catch (e) {
      console.error('Erro ao sanitizar URL', e);
      return '';
    }
  }

  // Atualiza a renderização após receber uma nova URL de imagem
  updateImageRendering(photoType: string, productId: string): void {

    // Garantir que o produto existe
    const product = this.treatmentPlan.products.find(p => p.id === productId);
    if (!product) {
      console.error(`Produto ${productId} não encontrado para atualização de renderização`);
      return;
    }

    // Adicionar logs detalhados para diagnóstico
    if (product.photos) {
      // Logs de debug removidos para reduzir ruído no console
    } else {
      // Logs de debug removidos para reduzir ruído no console
    }

    // Forçar várias atualizações da interface com intervalos para garantir que as mudanças sejam aplicadas
    this.cdr.detectChanges();

    // Força atualizações adicionais para garantir que a imagem seja renderizada
    setTimeout(() => {
      this.cdr.detectChanges();

      // Tentar encontrar e atualizar a imagem diretamente no DOM
      setTimeout(() => {
        // Procurar o elemento específico da imagem
        const selector = `img[key="${productId}"][alt*="${photoType}"]`;
        const imageElements = document.querySelectorAll(selector);

        if (imageElements.length > 0) {

          // Tentar atualizar o src da imagem
          const photo = product.photos?.find(p => {
            let order = -1;
            switch (photoType) {
              case 'bottle': order = 0; break;
              case 'box': order = 1; break;
              case 'label': order = 2; break;
            }
            return p.photo_order === order;
          });

          if (photo && photo.photo_url) {
            // Atualizar todas as imagens encontradas
            imageElements.forEach((element: Element) => {
              // Converter para HTMLImageElement e verificar se é uma imagem
              if (element instanceof HTMLImageElement) {
                const img = element as HTMLImageElement;
                // Verificar se a imagem já tem um src definido e se a URL da foto é válida
                const photoUrl = photo.photo_url || '';
                if (img.src !== photoUrl) {
                  img.src = photoUrl;
                }
              }
            });
          }
        } else {
        }

        // Uma última atualização
        this.cdr.detectChanges();
      }, 100);
    }, 50);
  }

  /**
   * Inicia captura de foto para o tratamento
   */
  initTreatmentPhotoCapture(): void {
    this.currentUploadType = 'treatment';
    this.showUploadOptions = true; // Abre o modal de opções
  }

  /**
   * Seleciona a opção de captura desejada
   */
  async selectCaptureOption(option: 'camera' | 'gallery' | 'qrcode'): Promise<void> {
    // Snapshot do tipo de foto no momento do clique. Evita que uma nova intera\u00e7\u00e3o
    // do usu\u00e1rio em outra se\u00e7\u00e3o (que sobrescreve `this.selectedPhotoType`)
    // afete um upload que j\u00e1 est\u00e1 em andamento.
    const capturedPhotoType: FrontendPhotoType = this.selectedPhotoType;

    // Só fecha o modal imediatamente para qrcode e camera (desktop)
    // Para gallery, mantemos aberto para mostrar o loading durante conversão HEIC
    if (option === 'qrcode') {
      this.showUploadOptions = false;
    }

    try {
      if (option === 'qrcode') {
        // Lógica do QR Code permanece a mesma
        if (this.currentUploadType === 'treatment') {
          await this.toggleQrCode('treatment_photos');
        } else {
          await this.toggleQrCode('product', this.selectedProductId!, this.selectedProductPhotoType!);
        }
        return;
      }

      // Se a opção for câmera E NÃO for um dispositivo móvel, usamos a câmera do desktop
      if (option === 'camera' && !this.isMobileDevice()) {
        this.showUploadOptions = false; // Fecha o modal antes de abrir câmera
        // O currentUploadType ('treatment' ou 'product') já está definido
        await this.useDesktopCamera(this.currentUploadType);
        return; // Encerra a execução aqui para não prosseguir para a lógica de arquivo
      }

      // Para Câmera móvel ou Galeria, ativar loading antes de processar
      if (this.currentUploadType === 'treatment') {
        this.processingHeic = true;
      } else {
        this.processingProductHeic = true;
      }
      this.cdr.detectChanges();

      // Para Câmera ou Galeria, usamos o PhotoCaptureService
      if (this.currentUploadType === 'treatment') {
        // Tratamento permite múltiplas fotos da galeria
        const result = (option === 'camera')
          ? [await this.photoCaptureService.openNativeCamera()]
          : (await this.photoCaptureService.openMultipleFileSelector()).files;

        // Desativar loading e fechar modal antes do upload (upload tem seu próprio loading)
        this.processingHeic = false;
        this.showUploadOptions = false;
        this.cdr.detectChanges();

        this.uploadTreatmentPhotosToS3(result, capturedPhotoType); // Chama o novo método de upload para S3

      } else { // 'product'
        // Produto é sempre uma foto por vez
        const result = (option === 'camera')
          ? await this.photoCaptureService.openNativeCamera()
          : await this.photoCaptureService.openFileSelector();

        // Desativar loading e fechar modal antes do upload (upload tem seu próprio loading)
        this.processingProductHeic = false;
        this.showUploadOptions = false;
        this.cdr.detectChanges();

        var createdPhoto = await this.uploadProductPhotoToS3(result); // Chama o novo método de upload para S3
        if (createdPhoto) {

          var product = this.treatmentPlan.products.find(p => p.id === this.selectedProductId);
          if (product) {
            product.photos?.push(
              { id: createdPhoto.id, photo: createdPhoto.photo!, photo_order: createdPhoto.photo_order });
          }
          this.processApiDataForFrontend();
          
          // CORREÇÃO: Atualizar estado do botão de encerramento após upload de foto
          this.updateCanCloseTreatmentPlan();
        }
      }

    } catch (error) {
      console.error('Operação de captura cancelada ou falhou:', error);
      // Desativar loading e fechar modal em caso de erro
      this.processingHeic = false;
      this.processingProductHeic = false;
      this.showUploadOptions = false;
      this.cdr.detectChanges();
    }
  }


  async uploadProductPhotoToS3(photoResult: PhotoCaptureResult): Promise<ITreatmentPlanPhoto | null> {
    if (!this.selectedProductId || !this.selectedProductPhotoType) return null;

    const productId = this.selectedProductId;
    const photoType = this.selectedProductPhotoType;

    this.setProductPhotoUploading(productId, photoType, true);
    var photoForDb: any;
    var response: ITreatmentPlanPhoto[] = [];
    try {
      const { file } = photoResult;
      const fileRequest = { file_name: file.name, content_type: file.type };

      const presignedData = await this.treatmentPlanService.generatePresignedUrls(
        this.treatmentPlanId, [fileRequest], productId
      );
      const uploadData = presignedData[0];

      await this.treatmentPlanService.uploadFileToS3(uploadData.uploadUrl, file, file.type);

      const photoOrder = { 'bottle': 0, 'box': 1, 'label': 2 }[photoType];
      photoForDb = { photo: uploadData.objectKey, photo_order: photoOrder };

      response = await this.treatmentPlanService.uploadProductPhotos(
        this.treatmentPlanId, productId, [photoForDb]
      );

      this.showSuccess('Foto do produto enviada com sucesso!');
    } catch (error) {
      console.error('Erro no upload da foto do produto:', error);
    } finally {
      this.setProductPhotoUploading(productId, photoType, false);
      return response[0] as ITreatmentPlanPhoto;
    }
  }

  async uploadTreatmentPhotosToS3(photoResults: PhotoCaptureResult[], photoType?: FrontendPhotoType): Promise<void> {
    if (photoResults.length === 0) return;

    // Snapshot do tipo de foto no início do fluxo.
    // Evita race condition quando o usuário clica "Adicionar Foto" em outra
    // seção (o que muda `this.selectedPhotoType`) enquanto um upload ainda
    // está em voo. Sem o snapshot, leituras posteriores (após awaits) dentro
    // deste método usariam o tipo da seção clicada por último, fazendo com
    // que as fotos fossem classificadas incorretamente.
    const currentPhotoType: FrontendPhotoType = photoType ?? this.selectedPhotoType;

    this.uploadingPhoto = true;
    const tempPhotoPlaceholders: ITreatmentPlanPhoto[] = [];

    // --- INÍCIO DA MODIFICAÇÃO: Criar Placeholders ---
    const batchStamp = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    photoResults.forEach((result, index) => {
      const tempId = `uploading_${batchStamp}_${index}`;
      const previewUrl = URL.createObjectURL(result.file); // Cria uma URL local para preview imediato

      const placeholder: ITreatmentPlanPhoto = {
        id: tempId,
        photo_url: previewUrl,
        description: 'Enviando...',
        type: currentPhotoType, // 'PRE', 'POST', 'MARKING' ou 'IMMEDIATE_POST' (snapshot)
        created_at: new Date().toISOString(),
        photo_order: 0, // A ordem será definida pelo backend
        photo: '', // Vazio por enquanto
        loading: true,
      };

      tempPhotoPlaceholders.push(placeholder);
      this.uploadingTreatmentPhotoIds.add(tempId);
    });

    // Adiciona os placeholders à lista de fotos para exibição imediata
    this.treatmentPlan.photos.unshift(...tempPhotoPlaceholders);
    this.invalidatePhotoCache();
    this.cdr.detectChanges();
    // --- FIM DA MODIFICAÇÃO ---

    try {
      // O restante da sua lógica de upload permanece a mesma
      const fileRequests = photoResults.map(pr => ({ file_name: pr.file.name, content_type: pr.file.type }));
      const presignedData = await this.treatmentPlanService.generatePresignedUrls(this.treatmentPlanId, fileRequests);

      const uploadPromises = presignedData.map(data => {
        const photoResult = photoResults.find(pr => pr.file.name === data.fileName);
        if (!photoResult) return Promise.reject('Arquivo correspondente não encontrado.');
        return this.treatmentPlanService.uploadFileToS3(data.uploadUrl, photoResult.file, photoResult.file.type);
      });
      await Promise.all(uploadPromises);

      const photosForDb = presignedData.map((data, index) => {
        const existingPhotosCount = this.treatmentPlan.photos.filter(p => !p.id?.startsWith('uploading_') && this.isPhotoOfType(p.type, currentPhotoType)).length;
        return {
          photo: data.objectKey,
          photoOrder: existingPhotosCount + index,
          type: this.toBackendPhotoType(currentPhotoType)
        };
      });

      const mainType = this.toBackendPhotoType(currentPhotoType);

      const updatedPhotos = await this.treatmentPlanService.uploadTreatmentPhotos(
        this.treatmentPlanId, photosForDb, mainType
      );


      // Remove only the temporary placeholders (keep existing photos of this type)
      this.treatmentPlan.photos = this.treatmentPlan.photos.filter(p => !p.id?.startsWith('uploading_'));

      // Add the newly uploaded photos returned by the backend
      const newPhotos = updatedPhotos.filter(p => this.isPhotoOfType(p.type, currentPhotoType));
      this.treatmentPlan.photos.unshift(...newPhotos);

      // Limpa o estado de loading
      tempPhotoPlaceholders.forEach(p => {
        this.uploadingTreatmentPhotoIds.delete(p.id!);
        URL.revokeObjectURL(p.photo_url!); // Libera memória da URL local
      });

      this.invalidatePhotoCache();
      this.cdr.detectChanges();

      this.showSuccess(`Foto(s) do tratamento enviada(s) com sucesso!`);
    } catch (error) {
      console.error('Erro no upload das fotos de tratamento:', error);

      // Em caso de erro, remove os placeholders
      this.treatmentPlan.photos = this.treatmentPlan.photos.filter(p => !p.id?.startsWith('uploading_'));
      tempPhotoPlaceholders.forEach(p => {
        this.uploadingTreatmentPhotoIds.delete(p.id!);
        URL.revokeObjectURL(p.photo_url!);
      });
      this.invalidatePhotoCache();

    } finally {
      this.uploadingPhoto = false;
    }
  }


  /**
   * Abre seletor de múltiplos arquivos
   */
  async openMultipleFileSelector(type: 'treatment' | 'product'): Promise<void> {

    const input = document.getElementById('treatment-photo-input-multiple') as HTMLInputElement;
    if (input) {
      // Reset the input value to ensure the change event triggers
      input.value = '';
      this.currentUploadType = type;
      input.click();
    } else {
      console.error('Input de múltiplas fotos de tratamento não encontrado');
      this.processingHeic = false;
    }
  }

  private async syncTreatmentPhotos(photoTypeToSync: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST', reason: string): Promise<void> {
    this.saving = true;
    this.cdr.detectChanges();

    try {
      // 1. Filtra o array local para pegar apenas as fotos do tipo que será sincronizado.
      const photosToSync = (this.treatmentPlan.photos || []).filter(p => {
        return this.isPhotoOfType(p.type, photoTypeToSync);
      });

      // 2. Mapeia as fotos filtradas para o formato da API.
      const photosForApi = photosToSync.map(p => {
        const backendType = this.toBackendPhotoType(photoTypeToSync);
        return {
          photo: p.photo || '',
          photoOrder: p.photo_order,
          type: backendType
        };
      });

      // LOG: Payload enviado para o backend
      if (!environment.production) {
        console.log('[SYNC PHOTOS] Payload enviado para o backend:', {
          treatmentPlanId: this.treatmentPlanId,
          type: this.toBackendPhotoType(photoTypeToSync),
          photos: photosForApi
        });
      }

      // 3. Chama o serviço. O backend receberá um array contendo apenas fotos de um tipo.
      const response = await this.treatmentPlanService.uploadTreatmentPhotos(
        this.treatmentPlanId,
        photosForApi,
        this.toBackendPhotoType(photoTypeToSync)
      );

      // LOG: Resposta recebida do backend
      if (!environment.production) {
        console.log('[SYNC PHOTOS] Resposta recebida do backend:', response);
      }

      const photoTypeLabels: Record<string, string> = {
        'PRE': 'Pré-tratamento',
        'POST': 'Pós-tratamento',
        'MARKING': 'Marcação',
        'IMMEDIATE_POST': 'Pós-imediato'
      };

      this.invalidatePhotoCache();
      this.hasUnsavedChanges = false;
      this.updateCanCloseTreatmentPlan();
      this.showSuccess(`Fotos de ${photoTypeLabels[photoTypeToSync] || photoTypeToSync} atualizadas!`);

    } catch (error) {
      console.error(`[SYNC PHOTOS] Erro ao sincronizar fotos do tipo ${photoTypeToSync}:`, error);
      this.loadTreatmentPlan();
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Abre seletor de arquivo único (para fotos de produtos)
   */
  async openSingleFileSelector(type: 'product'): Promise<void> {

    // Usar o input single existente no DOM ou criar dinamicamente
    const input = document.getElementById('product-photo-input-single') as HTMLInputElement;
    if (input) {
      // Reset the input value to ensure the change event triggers
      input.value = '';
      this.currentUploadType = type;
      input.click();
    } else {
      console.error('Input de foto única de produto não encontrado');
      this.processingProductHeic = false;
    }
  }

  /**
   * Inicia captura múltipla da câmera
   */
  async startMultipleCameraCapture(type: 'treatment' | 'product'): Promise<void> {
    // Mostrar modal de instrução para múltiplas capturas
    this.showMultipleCameraInstructions(type);
  }

  // treatment-plan.component.ts

  /**
   * NOVO MÉTODO: Solicita uma sincronização de fotos com debounce.
   * Em vez de chamar o sync diretamente, agenda a execução para daqui a 3 segundos.
   */
  private requestPhotoSync(photoType: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST', reason: string): void {

    // Adiciona o tipo à fila de sincronização
    this.pendingPhotoSyncs.add(photoType);

    // Mostra um indicador visual para o usuário de que algo está acontecendo
    this.showSaveIndicator = true;
    this.saveIndicatorText = 'Sincronizando fotos em 3s...';
    this.cdr.detectChanges();

    // Se já existe um timer, cancela para resetar o tempo de espera
    if (this.photoSyncTimeout) {
      clearTimeout(this.photoSyncTimeout);
    }

    // Agenda a execução do sync
    this.photoSyncTimeout = setTimeout(() => {
      this.executePhotoSync();
    }, this.photoSyncDebounceTime);
  }

  /**
   * NOVO MÉTODO: Executa a sincronização das fotos que estão na fila.
   * É chamado automaticamente pelo setTimeout do requestPhotoSync.
   */
  private async executePhotoSync(): Promise<void> {
    // Se a fila estiver vazia (por algum motivo), não faz nada
    if (this.pendingPhotoSyncs.size === 0) {
      return;
    }

    this.saveIndicatorText = 'Sincronizando fotos...';
    this.cdr.detectChanges();

    // Copia a fila e a limpa para futuras solicitações
    const typesToSync = Array.from(this.pendingPhotoSyncs);
    this.pendingPhotoSyncs.clear();
    this.photoSyncTimeout = null;

    // Executa a sincronização em sequência para cada tipo pendente
    for (const photoType of typesToSync) {
      await this.syncTreatmentPhotos(photoType, 'debounced_sync');
    }

    this.saveIndicatorText = 'Fotos sincronizadas!';
    setTimeout(() => {
      this.showSaveIndicator = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  async processMultiplePhotoResults(result: MultiplePhotoCaptureResult, type: 'treatment' | 'product'): Promise<void> {
    if (result.files.length === 0) {
      return;
    }

    // Configuração da barra de progresso 
    this.uploadedFiles = result.files;
    this.totalFilesToUpload = result.totalCount;
    this.currentFileIndex = 0;
    this.showMultipleUploadProgress = true;
    this.showSuccess(`${result.totalCount} foto(s) selecionada(s). Iniciando processamento...`);

    let finalPhotos: IProductPhoto[] = [];

    // Loop para processar cada foto localmente
    for (const photoResult of result.files) {
      this.currentFileIndex++;
      this.currentUploadFileName = photoResult.file.name;
      this.multipleUploadProgress = (this.currentFileIndex / this.totalFilesToUpload) * 100;
      this.cdr.detectChanges();

      try {
        if (type === 'treatment') {
          // A chamada para uploadPhoto aqui usa skipSave = true, o que está correto.
          await this.uploadTreatmentPhotoFromResult(photoResult, true);
        } else {
          finalPhotos = await this.uploadProductPhotoFromResult(photoResult, true);
        }
      } catch (error) {
        console.error(`Erro ao processar a foto ${photoResult.file.name}:`, error);
      }
    }

    // Esconde a barra de progresso e mostra mensagem de sucesso
    this.showMultipleUploadProgress = false;
    this.uploadedFiles = [];
    this.showSuccess(`Processamento local completo! ${result.files.length} foto(s) adicionada(s).`);

    // ======================= INÍCIO DA CORREÇÃO =======================
    //
    // Após o loop, se as fotos forem do tipo 'treatment', disparamos a sincronização.
    // O this.selectedPhotoType já terá o valor correto ('PRE' ou 'POST')
    // da ação que iniciou o upload.
    //
    if (type === 'treatment' && result.files.length > 0) {
      this.requestPhotoSync(this.selectedPhotoType, 'multiple_photos_upload');
    }

    // Para fotos de produto, o fluxo de salvamento já está correto.
    if (type === 'product' && this.selectedProductId) {
      await this.saveProductPhotos(this.selectedProductId, finalPhotos, 'multiple_photos_upload');
    }
    // ======================== FIM DA CORREÇÃO =========================
  }

  /**
   * Processa resultado de foto única de produto
   */
  async processSinglePhotoResult(result: PhotoCaptureResult, type: 'product'): Promise<void> {
    if (!result) {
      return;
    }

    this.currentUploadFileName = result.file.name;
    this.showSuccess(`Foto selecionada. Iniciando upload...`);

    try {
      // Processa o upload, que atualiza o modelo localmente
      const updatedPhotos = await this.uploadProductPhotoFromResult(result, true);

      this.showSuccess(`Upload completo! Foto adicionada com sucesso.`);

      // Salvar fotos do produto após upload único
      if (this.selectedProductId) {
        // 1. Encontra o produto para obter a lista de fotos atualizada
        const product = this.treatmentPlan.products.find(p => p.id === this.selectedProductId);

        if (product) {
          // 2. Passa a lista de fotos atualizada para o método de salvamento
          await this.saveProductPhotos(this.selectedProductId, updatedPhotos, 'single_photo_upload');
          // Solicita um autosave leve do restante do plano
          this.requestSave(`product_photo_upload:${this.selectedProductId}`, false, 1500);
        } else {
          console.error('Produto não encontrado após upload de foto única, a foto não foi salva.');
        }
      }

      // Fechar modal de upload e limpar variáveis
      this.showUploadOptions = false;
      this.selectedProductId = null;
      this.selectedProductPhotoType = null;

    } catch (error) {
      console.error(`Erro ao fazer upload da foto ${result.file.name}:`, error);
    }

    this.cdr.detectChanges();
  }

  /**
   * Faz upload de foto de tratamento a partir do resultado processado
   */
  private async uploadTreatmentPhotoFromResult(photoResult: PhotoCaptureResult, skipSave: boolean = true): Promise<void> {
    this.selectedPhotoFile = photoResult.file;
    this.rawImageUrl = photoResult.base64;
    this.previewImageUrl = photoResult.preview;

    // A lógica de upload de foto de tratamento não é async, então podemos apenas chamá-la.
    // O setTimeout/Promise original não era necessário e podia causar problemas de timing.
    this.uploadPhoto(skipSave);
  }

  /**
   * Faz upload de foto de produto a partir do resultado processado
   */
  private async uploadProductPhotoFromResult(photoResult: PhotoCaptureResult, skipSave: boolean = true): Promise<IProductPhoto[]> {
    this.selectedProductPhotoFile = photoResult.file;
    this.rawImageUrl = photoResult.base64;
    this.previewImageUrl = photoResult.preview;

    return await this.storeProductPhotoTemporarily(skipSave);
  }

  /**
   * Mostra instruções para captura múltipla da câmera
   */
  private showMultipleCameraInstructions(type: 'treatment' | 'product'): void {
    // Mostrar mensagem explicativa sobre limitações da câmera
    this.showInfo('📱 Dica: Para múltiplas fotos, use "Galeria" e selecione várias imagens. A câmera permite capturar uma foto por vez.');

    // Aguardar um momento e depois abrir a câmera
    setTimeout(() => {
      this.startCamera(type);
    }, 2000);
  }

  /**
   * Mostra mensagem informativa
   */
  private showInfo(message: string): void {
    this.showMessage = true;
    this.messageType = 'success'; // Usar success para info
    this.messageText = message;
    this.messageTimeout = setTimeout(() => {
      this.showMessage = false;
    }, 4000);
    this.cdr.detectChanges();
  }

  /**
   * Sistema de gerenciamento de saves com debounce
   */
  requestSave(reason: string = 'generic', showIndicator: boolean = true, debounceMs?: number): void {
    if (this.treatmentPlan.status === 'CLOSED') {
      return;
    }

    this.hasUnsavedChanges = true; // Garante que a flag esteja correta

    // Se já há um save em andamento, apenas marcamos que há uma alteração pendente e saímos.
    // O fluxo atual se encarregará de salvar isso quando terminar.
    if (this.saving) {
      this.pendingSave = true;
      return;
    }

    // Se não há save em andamento, então aplicamos o debounce.
    // Limpamos qualquer timer anterior para garantir que apenas o último seja executado.
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }



    const delay = typeof debounceMs === 'number' && debounceMs >= 0 ? debounceMs : this.saveDebounceTime;
    this.saveTimeout = setTimeout(() => {
      // Passamos showIndicator para que executeSave possa controlar a UI
      this.executeSave(showIndicator);
    }, delay);
  }

  /**
   * Executa o save efetivamente.
   *
   * <p>Proteção contra flag `saving` travada: além do `finally` (que cobre rejeições/erros
   * normais), usamos um timeout de segurança. Se o save demorar mais que 30s — por promise
   * travada, request pendurado sem resposta, etc. — o flag é liberado à força para não
   * bloquear saves futuros indefinidamente.
   */
  private async executeSave(showIndicator: boolean): Promise<void> {
    // Trava para evitar execuções simultâneas
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.pendingSave = false; // Resetamos a flag, pois estamos processando este save agora.
    this.saveTimeout = null; // Limpa a referência do timeout

    // Fail-safe: se `saveTreatmentPlan` nunca resolver/rejeitar (ex: request pendurado sem
    // timeout no HttpClient), libera o flag depois de 30s para não travar saves subsequentes.
    const SAFETY_TIMEOUT_MS = 30_000;
    const safetyTimer = setTimeout(() => {
      if (this.saving) {
        console.warn('[EXECUTE SAVE] Timeout de segurança disparado — liberando flag `saving`.');
        this.saving = false;
        if (showIndicator) {
          this.saveIndicatorText = 'Tempo esgotado';
          this.cdr.detectChanges();
        }
      }
    }, SAFETY_TIMEOUT_MS);

    if (showIndicator) {
      this.saveIndicatorText = 'Salvando...';
      this.cdr.detectChanges();
    }

    try {
      await this.saveTreatmentPlan(showIndicator);

      if (showIndicator) {
        this.saveIndicatorText = 'Salvo com sucesso!';
      }
    } catch (error) {
      if (showIndicator) {
        this.saveIndicatorText = 'Erro ao salvar';
        console.error('❌ [EXECUTE SAVE] Falha no salvamento.', error);
      }
    } finally {
      clearTimeout(safetyTimer);
      this.saving = false;

      // Se, enquanto salvávamos, uma nova alteração foi solicitada (pendingSave = true)...
      if (this.pendingSave) {
        // Chamamos executeSave diretamente para processar a "fila" sem novo debounce.
        this.executeSave(showIndicator);
      } else {
        // Se não há mais nada pendente, escondemos o indicador.
        this.showSavingIndicator = false;

        if (showIndicator) {
          setTimeout(() => {
            this.showSaveIndicator = false;
            this.cdr.detectChanges();
          }, 2000);
        }
      }
    }
  }

  /**
   * Força um save imediato (usado em casos críticos como mudança de aba)
   */
  forceSave(reason: string = 'force', showIndicator: boolean = true): Promise<void> {

    this.showSavingIndicator = showIndicator;

    // Cancelar timeout se existir
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    // Limpar indicador
    this.showSaveIndicator = false;

    // Executar save imediatamente
    return this.executeSave(true);
  }

  /**
   * Cancela save pendente (usado quando componente é destruído)
   */
  cancelPendingSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    this.showSaveIndicator = false;
    this.saveQueue = [];
  }

  /**
   * Fecha o modal de QR Code
   */
  closeQrCode(): void {
    this.showQrCode = false;
    this.qrCodeConfirmed = false;
    this.qrCodeUrl = '';
    this.selectedQrCodeProductId = null;
    this.selectedQrCodePhotoType = null;
    this.qrCodeType = 'treatment'; // Resetar para o valor padrão
    this.qrCodeExpirationTime = null;
    this.qrCodePhotosFinishedCheckbox = false;
  }

  // Método para verificar se uma região existe na lista padrão
  isRegionInStandardList(region: string): boolean {
    return this.getStandardRegions().includes(region);
  }

  onRegionSelectionChange(productIndex: number, regionIndex: number): void {
    const regionFormGroup = this.getRegionFormGroup(productIndex, regionIndex);
    const regionValue = regionFormGroup.get('region')?.value;

    if (regionValue === 'Outra') {
      // Se "Outra" for selecionada, adicionar validação ao campo custom_region
      regionFormGroup.get('custom_region')?.setValidators([Validators.required]);
    } else {
      // Se outra opção for selecionada, remover validação do campo custom_region
      regionFormGroup.get('custom_region')?.clearValidators();
      // Limpar o valor do campo custom_region
      regionFormGroup.get('custom_region')?.setValue('');
    }

    // Atualizar o status de validação
    regionFormGroup.get('custom_region')?.updateValueAndValidity();

    // Marcar que há alterações não salvas
    this.hasUnsavedChanges = true;

    // Forçar detecção de mudanças
    this.cdr.detectChanges();

    // Autosave após selecionar/alterar região
    this.requestSave(`region_selection_change:${productIndex}:${regionIndex}`, true, 1200);
  }

  initManufacturerProductMap(): void {
    // Dados fornecidos pelo usuário
    const manufacturerProductData = [
      {
        "Merz": [
          "Toxina Botulínica",
          "Ácido Hialurônico",
          "Bioestimulador de Colágeno",
          "Belotero Soft (Ácido Hialurônico)",
          "Belotero Balance (Ácido Hialurônico)",
          "Belotero Intense (Ácido Hialurônico)",
          "Belotero Volume (Ácido Hialurônico)",
          "Belotero Lips (Ácido Hialurônico)",
          "Radiesse (Hidroxiapatita de Cálcio)",
          "Bocouture (Toxina Botulínica)"
        ],
        "Nabota": [
          "Toxina Botulínica"
        ],
        "Milimetric": [
          "Ácido Hialurônico"
        ],
        "Mesoesthetic": [
          "Bioestimuladores de colágeno NCTC",
          "Bioestimuladores de colágeno Silício",
          "Bioestimuladores de colágeno Rendex",
          "Bioestimuladores de colágeno Argibenone"
        ],
        "Radiesse": [
          "Bioestimulador de Colágeno"
        ],
        "Allergan": [
          "Toxina Botulínica",
          "Ácido Hialurônico",
          "Bioestimulador de Colágeno"
        ],
        "Botulim": [
          "Toxina Botulínica"
        ],
        "UP": [
          "Ácido Hialurônico"
        ],
        "Sculptra": [
          "Bioestimulador de Colágeno"
        ],
        "Galderma": [
          "Dysport® 300U",
          "Dysport® 500U",
          "Restylane®",
          "Restylane® Defyne™",
          "Restylane® Kysse™",
          "Restylane® Lidocaine™",
          "Restylane® Lyft™",
          "Restylane® Lyft™ Lido",
          "Restylane® Refyne™",
          "Restylane® Shaype™",
          "Restylane® Skinboosters™ Vital",
          "Restylane® Skinboosters™ Vital Light",
          "Restylane® Volyme™",
          "Sculptra®"
        ],
        "Dysport": [
          "Toxina Botulínica"
        ],
        "Saypha": [
          "Ácido Hialurônico"
        ],
        "Ellansé": [
          "Bioestimulador de Colágeno"
        ],
        "Medytox": [
          "Toxina Botulínica"
        ],
        "Rennova": [
          "Toxina Botulínica",
          "Ácido Hialurônico",
          "Bioestimulador de Colágeno"
        ],
        "Botox": [
          "Toxina Botulínica"
        ],
        "Belotero": [
          "Ácido Hialurônico"
        ],
        "Elleva": [
          "Bioestimulador de Colágeno"
        ],
        "Lanzhou Biological": [
          "Toxina Botulínica"
        ],
        "Pharmaesthetics": [
          "Ácido Hialurônico",
          "Biogelis Fine (Ácido Hialurônico)",
          "Biogelis Global (Ácido Hialurônico)",
          "Biogelis Volume (Ácido Hialurônico)",
          "Biogelis Volume com Lidocaína (Ácido Hialurônico)",
          "Biogelis Deep (Ácido Hialurônico)"
        ],
        "Sinclair": [
          "Bioestimulador de Colágeno",
          "Ácido Hialurônico",
          "Perfectha Fine Lines (Ácido Hialurônico)",
          "Perfectha Derm (Ácido Hialurônico)",
          "Perfectha Deep (Ácido Hialurônico)",
          "Perfectha SubSkin (Ácido Hialurônico)"
        ],
        "Xeomin": [
          "Toxina Botulínica"
        ],
        "Restylane": [
          "Ácido Hialurônico"
        ],
        "Harmonyca": [
          "Bioestimulador de Colágeno"
        ],
        "Hugel": [
          "Toxina Botulínica"
        ],
        "Cimed": [
          "Ácido Hialurônico"
        ],
        "AestheFill": [
          "Bioestimulador de Colágeno"
        ],
        "Evopharma": [
          "EVOTHREAD (fios de sustentação)",
          "EVOEVER (bioimplante)",
          "EVOFILL (preenchedores DVS)",
          "PDRN",
          "Sculpt (Hidroxiapatita de Cálcio)"
        ],
        "I-THREAD": [
          "Fio PDO mono",
          "Fio PDO liso",
          "Fio PDO espiculado",
          "Fio PDO moldado"
        ],
        "Prosigne": [
          "Toxina Botulínica"
        ],
        "Biogelis": [
          "Ácido Hialurônico"
        ],
        "Chroma Pharma": [
          "Ácido Hialurônico",
          "Saypha Filler (Ácido Hialurônico)",
          "Saypha Volume (Ácido Hialurônico)",
          "Saypha Rich (Ácido Hialurônico)",
          "Saypha Princess Volume (Ácido Hialurônico)",
          "Saypha Lips (Ácido Hialurônico)"
        ],
        "Botulift": [
          "Toxina Botulínica"
        ],
        "Ilikia": [
          "Ácido Hialurônico",
          "Bioestimulador Stiim"
        ],
        "EvoPhill": [
          "Ácido Hialurônico"
        ],
        "Juvederm": [
          "Ácido Hialurônico"
        ],
        "Revanese": [
          "Ácido Hialurônico"
        ],
        "Toskani": [
          "Hialuronidase"
        ],
        "Allergan (AbbVie)": [
          "Juvéderm Ultra 3 (Ácido Hialurônico)",
          "Juvéderm Ultra 4 (Ácido Hialurônico)",
          "Juvéderm Voluma (Ácido Hialurônico)",
          "Juvéderm Volift (Ácido Hialurônico)",
          "Juvéderm Volbella (Ácido Hialurônico)",
          "Juvéderm Volux (Ácido Hialurônico)",
          "Juvéderm Volite (Ácido Hialurônico)",
          "HArmonyCa (Híbrido AH+CaHA)"
        ],
        "Rennova (Innovapharma)": [
          "Rennova Fill (Ácido Hialurônico)",
          "Rennova Lift (Ácido Hialurônico)",
          "Rennova Skin (Ácido Hialurônico)",
          "Rennova Deep (Ácido Hialurônico)",
          "Rennova Ultra Deep (Ácido Hialurônico)",
          "Ultra Volume Lido (Ácido Hialurônico)",
          "Diamond (Hidroxiapatita de Cálcio)"
        ],
        "Aqtis Medical": [
          "Ellansé S (Bioestimulador PCL)",
          "Ellansé M (Bioestimulador PCL)",
          "Ellansé L (Bioestimulador PCL)",
          "Ellansé E (Bioestimulador PCL)"
        ],
        "LG Chem": [
          "Yvoire Classic (Ácido Hialurônico)",
          "Yvoire Volume (Ácido Hialurônico)",
          "Yvoire Contour (Ácido Hialurônico)",
          "Yvoire Shape (Ácido Hialurônico)"
        ],
        "Adcos": [
          "Hyalucream GF (Cosmético AH)",
          "Hyalu Serum (Cosmético AH)"
        ],
        "Biomarine": [
          "Hyaluronic Anti Age (Cosmético AH)"
        ],
        "Diversos Nacionais": [
          "Hyalax Various (Ácido Hialurônico)",
          "BioNike Episkin (Cosmético/Teste)"
        ],
        "Letybo": [
          "Toxina Botulínica"
        ]
      }
    ];

    // Extrair o objeto do array (já que é um array com um único objeto)
    const manufacturerMap = manufacturerProductData[0];

    // Armazenar o mapeamento
    this.manufacturerProductMap = manufacturerMap;

    // Extrair a lista de fabricantes
    this.manufacturersList = Object.keys(manufacturerMap).sort();

    // Logs removidos para reduzir ruído no console
  }

  onManufacturerChange(productIndex: number): void {

    // Verificar o estado atual do availableProducts antes da mudança

    // Obter o fabricante selecionado
    const manufacturerControl = this.getProductFormGroup(productIndex).get('manufacturer');
    if (!manufacturerControl) {
      console.error('Controle de fabricante não encontrado para o produto:', productIndex);
      return;
    }

    const selectedManufacturer = manufacturerControl.value;

    // Gerenciar validadores do campo custom_manufacturer.
    // Importante: todas as operações internas em FormControls abaixo usam `{ emitEvent: false }`
    // para que a subscription de `valueChanges` (que marca `hasUnsavedChanges` e dispara
    // autosave) NÃO seja acionada por limpezas programáticas. Sem isso, trocar de fabricante
    // marcava 2-3 mudanças "do usuário" e disparava saves desnecessários.
    const customManufacturerControl = this.getProductFormGroup(productIndex).get('custom_manufacturer');
    if (customManufacturerControl) {
      if (selectedManufacturer === 'Outro') {
        customManufacturerControl.setValidators([Validators.required]);
      } else {
        customManufacturerControl.clearValidators();
        customManufacturerControl.setValue('', { emitEvent: false });
      }
      customManufacturerControl.updateValueAndValidity({ emitEvent: false });
    }

    // Limpar a seleção atual de produto (mudança programática — sem disparar valueChanges)
    const productNameControl = this.getProductFormGroup(productIndex).get('product_name');
    if (productNameControl) {
      productNameControl.setValue('', { emitEvent: false });
    } else {
      console.error('Controle de nome do produto não encontrado para o produto:', productIndex);
    }

    // Limpar o campo custom_product_name e seus validadores também
    const customProductNameControl = this.getProductFormGroup(productIndex).get('custom_product_name');
    if (customProductNameControl) {
      customProductNameControl.clearValidators();
      customProductNameControl.setValue('', { emitEvent: false });
      customProductNameControl.updateValueAndValidity({ emitEvent: false });
    }

    // Atualizar a lista de produtos disponíveis com base no fabricante selecionado
    if (selectedManufacturer && selectedManufacturer !== 'Outro') {
      // Garantir que o array de produtos disponíveis tenha espaço para este índice
      while (this.availableProducts.length <= productIndex) {
        this.availableProducts.push([]);
      }

      // Atribuir os produtos correspondentes ao fabricante
      this.availableProducts[productIndex] = this.manufacturerProductMap[selectedManufacturer] || [];
    } else {
      // Se o fabricante for "Outro" ou não estiver selecionado, limpar a lista
      this.availableProducts[productIndex] = [];
    }

    // Marcar que há alterações não salvas
    this.hasUnsavedChanges = true;

    // Forçar detecção de mudanças para atualizar a view
    this.cdr.detectChanges();

    // Autosave após seleção de fabricante (curto)
    this.requestSave(`manufacturer_change:${productIndex}`, true, 1200);
  }

  // Método para marcar todos os campos como tocados
  markAllTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        this.markAllTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markAllTouched(c);
          } else {
            (c as FormControl).markAsTouched();
          }
        });
      }
    });

    // Se houver alterações mínimas no formulário, como tocar em campos sem alterar valores,
    // vamos garantir que o flag de alterações não salvas seja atualizado se o formulário estiver "dirty"
    if (formGroup.dirty) {
      this.hasUnsavedChanges = true;
    }
  }

  // Método auxiliar para converter AbstractControl para FormGroup
  getProductFormGroup(index: number): FormGroup {
    try {
      if (!this.treatmentPlanForm || !this.productsArray || index >= this.productsArray.length || index < 0) {
        return this.fb.group({});
      }
      const product = this.productsArray.at(index);
      if (!product) {
        return this.fb.group({});
      }
      return product as FormGroup;
    } catch (error) {
      console.error('Erro ao acessar getProductFormGroup:', error);
      return this.fb.group({});
    }
  }

  // Método auxiliar para converter AbstractControl para FormGroup para regiões
  getRegionFormGroup(productIndex: number, regionIndex: number): FormGroup {
    try {
      const regionsArray = this.getRegionsArray(productIndex);
      if (!regionsArray || regionIndex >= regionsArray.length || regionIndex < 0) {
        return this.fb.group({});
      }
      const region = regionsArray.at(regionIndex);
      if (!region) {
        return this.fb.group({});
      }
      return region as FormGroup;
    } catch (error) {
      console.error('Erro ao acessar getRegionFormGroup:', error);
      return this.fb.group({});
    }
  }

  /**
   * Método otimizado para salvamento apenas quando necessário
   * Só salva produtos que realmente foram modificados e estão válidos.
   *
   * <p>Fail-safe: se a promise do service ficar pendurada (request sem timeout),
   * libera `this.saving` após 30s para não bloquear saves futuros — mesmo padrão
   * do `executeSave()`.
   */
  autoSaveProductsOnTabChange(): Promise<void> {

    if (this.saving) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const SAFETY_TIMEOUT_MS = 30_000;
      let safetyTimer: ReturnType<typeof setTimeout> | null = null;

      try {
        // Sincronizar dados do formulário para o modelo
        this.syncFormToModel();

        // ALTERAÇÃO: Todos os produtos já estão salvos no banco
        // Verificar se há mudanças que precisam ser salvas
        if (!this.hasUnsavedChanges) {
          resolve();
          return;
        }


        this.saving = true;

        safetyTimer = setTimeout(() => {
          if (this.saving) {
            console.warn('[AUTO-SAVE] Timeout de segurança — liberando flag `saving`.');
            this.saving = false;
            reject(new Error('Autosave timeout'));
          }
        }, SAFETY_TIMEOUT_MS);

        // Usar o serviço para atualizar o plano de tratamento
        this.treatmentPlanService.updateTreatmentPlan(this.treatmentPlanId, this.treatmentPlan)
          .then(async (updatedPlan) => {

            // Atualizar os dados locais com a resposta do servidor
            this.treatmentPlan = updatedPlan;
            await this.processApiDataForFrontend();

            // Reconstruir o formulário com os novos IDs
            this.updateForm();

            // Marcar como salvo
            this.hasUnsavedChanges = false;
            this.updateCanCloseTreatmentPlan();

            // Limpar fotos temporárias que agora foram persistidas
            this.tempProductPhotos = {};

            // Atualizar a interface
            this.cdr.detectChanges();

            // Mostrar mensagem de sucesso
            this.showSuccess('Alterações salvas automaticamente');

            resolve();
          })
          .catch((error) => {
            console.error('Erro no salvamento automático:', error);
            reject(error);
          })
          .finally(() => {
            if (safetyTimer) clearTimeout(safetyTimer);
            this.saving = false;
          });
      } catch (error) {
        if (safetyTimer) clearTimeout(safetyTimer);
        console.error('Erro ao preparar salvamento automático:', error);
        this.saving = false;
        reject(error);
      }
    });
  }

  // Cache para otimizar validação
  private lastValidationHash: string = '';
  private lastValidationResult: boolean = false;

  // Método para verificar se pode encerrar o plano silenciosamente (sem mostrar erros)
  checkCanCloseTreatmentPlan(): boolean {
    // IMPORTANTE: Esta validação é aplicada apenas para ENCERRAMENTO do plano
    // Para salvamento parcial, produtos incompletos são permitidos

    let isValid = true;
    const validationErrors: string[] = [];

    try {
      // Usar validação simplificada e otimizada para verificação de encerramento

      // Verificar aba de informações gerais
      const patientAge = this.treatmentPlanForm.get('patient_age');
      if (!patientAge?.value || patientAge.value <= 0) {
        isValid = false;
        validationErrors.push('Idade do paciente é obrigatória');
      }

      // Verificar aba de produtos - ALTERADO: produtos não são mais obrigatórios
      // O plano pode ser encerrado sem produtos
      if (this.productsArray.length > 0) {
        // Se há produtos, todos devem estar válidos
        // VALIDAÇÃO ULTRA OTIMIZADA - SEM LOOPS
        // Usar validação simples baseada no FormArray sem loops aninhados
        const productsArrayValue = this.productsArray.value;

        // Validação básica usando map/filter que são mais eficientes
        const invalidProducts = productsArrayValue.filter((product: any, index: number) => {
          const hasBasicFields = !!(product.manufacturer && product.product_name &&
            product.application_date);
          const hasRegions = !!(product.application_regions && product.application_regions.length > 0);
          const hasValidRegions = hasRegions && product.application_regions.every((region: any) => {
            if (!region.region) return false;
            // Se a região for "Outra", precisa ter custom_region preenchida
            if (region.region === 'Outra' && !region.custom_region) return false;
            return true;
          });

          return !hasBasicFields || !hasValidRegions;
        });

        if (invalidProducts.length > 0) {
          isValid = false;
          validationErrors.push(`${invalidProducts.length} produto(s) com dados incompletos`);
        }

        // Verificar fotos dos produtos - pelo menos uma foto é obrigatória por produto
        const productsWithoutPhotos = this.treatmentPlan.products?.filter(product => {
          const hasAnyPhoto = this.hasProductPhoto(product, 'bottle') ||
            this.hasProductPhoto(product, 'box') ||
            this.hasProductPhoto(product, 'label');
          return !hasAnyPhoto;
        }) || [];

        if (productsWithoutPhotos.length > 0) {
          isValid = false;
          validationErrors.push(`${productsWithoutPhotos.length} produto(s) sem fotos`);
        }
      }

    } catch (error) {
      console.error('Erro ao verificar se o plano pode ser encerrado:', error);
      return false;
    }

    // Log para debug
    if (!isValid) {
    } else {
    }

    return isValid;
  }

  // Método para obter os erros de validação para o tooltip
  getValidationErrorsForTooltip(): string[] {
    const errors: string[] = [];

    try {
      // Verificar se há alterações não salvas
      if (this.hasUnsavedChanges) {
        errors.push('Há alterações não salvas. Salve antes de encerrar.');
        return errors;
      }

      // Verificar aba de informações gerais
      const patientAge = this.treatmentPlanForm.get('patient_age');
      if (!patientAge?.value || patientAge.value <= 0) {
        errors.push('Idade do paciente é obrigatória');
      }

      // Verificar aba de produtos - ALTERADO: produtos não são mais obrigatórios
      // O plano pode ser encerrado sem produtos
      if (this.productsArray.length > 0) {
        // Se há produtos, todos devem estar válidos
        const productsArrayValue = this.productsArray.value;

        // Contar produtos com dados incompletos
        const invalidProducts = productsArrayValue.filter((product: any) => {
          const hasBasicFields = !!(product.manufacturer && product.product_name &&
            product.application_date);
          const hasRegions = !!(product.application_regions && product.application_regions.length > 0);
          const hasValidRegions = hasRegions && product.application_regions.every((region: any) => {
            if (!region.region) return false;
            if (region.region === 'Outra' && !region.custom_region) return false;
            return true;
          });

          return !hasBasicFields || !hasValidRegions;
        });

        if (invalidProducts.length > 0) {
          if (invalidProducts.length === 1) {
            errors.push('1 produto com dados incompletos');
          } else {
            errors.push(`${invalidProducts.length} produtos com dados incompletos`);
          }
        }

        // Verificar fotos dos produtos - usando validação robusta
        const productsWithoutPhotos = this.treatmentPlan.products?.filter(product => {
          // Verificar se há pelo menos uma foto válida usando o método hasProductPhoto
          const hasAnyPhoto = this.hasProductPhoto(product, 'bottle') ||
            this.hasProductPhoto(product, 'box') ||
            this.hasProductPhoto(product, 'label');

          return !hasAnyPhoto; // Retorna true se NÃO tem nenhuma foto válida
        }) || [];

        if (productsWithoutPhotos.length > 0) {
          if (productsWithoutPhotos.length === 1) {
            errors.push('1 produto sem fotos');
          } else {
            errors.push(`${productsWithoutPhotos.length} produtos sem fotos`);
          }
        }
      }

    } catch (error) {
      console.error('Erro ao obter erros de validação para tooltip:', error);
      errors.push('Erro ao verificar validação');
    }

    return errors;
  }

  // Método para exibir o modal de ajuda sobre campos obrigatórios
  showHelpModal(): void {
    // Implementaremos isso no HTML
  }

  // Métodos para controlar o tooltip do botão de encerrar
  showCloseButtonTooltip(): void {
    if (!this.canCloseTreatmentPlan) {
      this.closeTooltipErrors = this.getValidationErrorsForTooltip();
      this.showCloseTooltip = true;

      // Calcular posição após o tooltip aparecer
      setTimeout(() => {
        this.calculateTooltipPosition();
      }, 0);
    }
  }

  private calculateTooltipPosition(): void {
    if (this.closeButton && this.closeTooltip) {
      const buttonRect = this.closeButton.nativeElement.getBoundingClientRect();
      const tooltipElement = this.closeTooltip.nativeElement;
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Posição padrão: acima do botão, centralizado
      let top = buttonRect.top - tooltipRect.height - 8; // 8px de margem
      let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);

      // Ajustar se sair da tela pela direita
      if (left + tooltipRect.width > viewportWidth - 16) { // 16px de margem da borda
        left = viewportWidth - tooltipRect.width - 16;
      }

      // Ajustar se sair da tela pela esquerda
      if (left < 16) {
        left = 16;
      }

      // Se não couber acima, colocar abaixo
      if (top < 16) {
        top = buttonRect.bottom + 8;
      }

      // Se ainda assim não couber na altura, ajustar
      if (top + tooltipRect.height > viewportHeight - 16) {
        top = viewportHeight - tooltipRect.height - 16;
      }

      this.tooltipPosition = {
        top: `${top}px`,
        left: `${left}px`
      };
    }
  }

  hideCloseButtonTooltip(): void {
    this.showCloseTooltip = false;
    this.closeTooltipErrors = [];
  }

  // Handler para o evento beforeunload - versão otimizada
  private handleBeforeUnload = (event: BeforeUnloadEvent) => {

    if (this.hasUnsavedChanges) {
      // Mensagem padrão para navegadores modernos
      const message = 'Há alterações não salvas. Se sair agora, essas alterações serão perdidas.';


      // Usar apenas returnValue para compatibilidade moderna
      event.preventDefault();
      event.returnValue = message;
      return message;
    }

    return undefined;
  }

  // Método para configurar detecção de navegação/fechamento da página - versão moderna
  setupBeforeUnloadListener(): void {
    // Remover qualquer listener existente primeiro
    this.removeBeforeUnloadListener();


    // Usar addEventListener em vez de onbeforeunload para evitar o aviso de deprecação
    this.beforeUnloadListener = this.handleBeforeUnload;
    window.addEventListener('beforeunload', this.beforeUnloadListener, { passive: false });

  }

  // Método para remover o listener de beforeunload - versão otimizada
  removeBeforeUnloadListener(): void {
    // Limpar abordagem com addEventListener
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }

    // Limpar abordagem direta apenas se necessário
    if (window.onbeforeunload === this.handleBeforeUnload) {
      window.onbeforeunload = null;
    }

  }

  // Implementação da interface CanComponentDeactivate
  canDeactivate(): boolean {

    // NOVO: Verificar se há fotos sendo enviadas
    if (this.hasAnyProductPhotoUploading()) {
      const result = confirm('⚠️ ATENÇÃO: Há fotos sendo enviadas no momento. Se sair agora, o upload pode ser interrompido e as fotos podem não ser salvas. Deseja continuar mesmo assim?');
      return result;
    }

    if (this.hasUnsavedChanges) {
      const result = confirm('Há alterações não salvas. Se sair agora, essas alterações serão perdidas. Deseja continuar?');
      return result;
    }
    return true;
  }

  // Handler para evento de tecla
  private handleKeyDown = (event: KeyboardEvent) => {
    // Tecla F5 (código 116)
    if (event.key === 'F5' || event.keyCode === 116) {

      if (this.hasUnsavedChanges) {
        const message = 'Há alterações não salvas. Se recarregar agora, essas alterações serão perdidas.';

        // Mostrar mensagem personalizada usando confirm
        if (!confirm(message)) {
          event.preventDefault();
          return false;
        }
      }
    }
    return true;
  }

  // Método para validar campos obrigatórios antes de salvar (não para encerramento)
  validateRequiredFieldsForSaving(): { isValid: boolean; messages: string[] } {

    switch (this.activeTab) {
      case 'info':
        return this.validateInfoTabFields();
      case 'products':
        return this.validateProductsTabFields();
      case 'photos':
        return this.validatePhotosTabFields();
      case 'intercorrencias':
        return { isValid: true, messages: [] };
      default:
        return { isValid: true, messages: [] };
    }
  }

  // Validação específica da aba de informações gerais
  validateInfoTabFields(): { isValid: boolean; messages: string[] } {

    let isValid = true;
    const messages: string[] = [];
    let firstInvalidElement: HTMLElement | null = null;

    // Marcar campos da aba info como tocados
    this.markInfoFieldsAsTouched();

    // Verificar idade do paciente
    const patientAge = this.treatmentPlanForm.get('patient_age');
    if (!patientAge?.value) {
      isValid = false;
      messages.push('A idade do paciente é obrigatória');
      if (!firstInvalidElement) {
        firstInvalidElement = document.getElementById('patient-age-field');
      }
    }

    // Focar no primeiro campo inválido se houver
    if (firstInvalidElement) {
      setTimeout(() => {
        firstInvalidElement?.focus();
        firstInvalidElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    return { isValid, messages };
  }

  // Validação específica da aba de produtos
  // IMPORTANTE: Esta validação é aplicada apenas para PUBLICAÇÃO/ENCERRAMENTO do plano
  // Para salvamento parcial, produtos incompletos são permitidos
  validateProductsTabFields(): { isValid: boolean; messages: string[] } {

    let isValid = true;
    const messages: string[] = [];
    let firstInvalidElement: HTMLElement | null = null;

    // Marcar campos da aba produtos como tocados
    this.markProductsFieldsAsTouched();

    // Verificar se há produtos - ALTERADO: produtos não são mais obrigatórios
    // O plano pode ser encerrado sem produtos
    if (this.productsArray.length === 0) {
      return { isValid: true, messages: [] };
    }

    // Validar cada produto
    for (let i = 0; i < this.productsArray.length; i++) {
      const productGroup = this.getProductFormGroup(i);
      const productId = productGroup.get('id')?.value;

      // Validar campos básicos do produto
      const fieldsToValidate = [
        { control: 'application_date', name: 'Data da Aplicação', id: `product-${i}-application_date` },
        { control: 'manufacturer', name: 'Fabricante', id: `product-${i}-manufacturer` },
        { control: 'product_name', name: 'Produto', id: `product-${i}-product_name` }
      ];

      for (const field of fieldsToValidate) {
        const control = productGroup.get(field.control);
        if (!control?.value) {
          isValid = false;
          messages.push(`${field.name} é obrigatório no Produto ${i + 1}`);
          if (!firstInvalidElement) {
            firstInvalidElement = document.getElementById(field.id);
          }
        }
      }

      // Validar regiões de aplicação
      const regionsArray = this.getRegionsArray(i);
      if (regionsArray.length === 0) {
        isValid = false;
        messages.push(`É necessário adicionar pelo menos uma região de aplicação no Produto ${i + 1}`);
      } else {
        // Validar cada região
        for (let j = 0; j < regionsArray.length; j++) {
          const regionGroup = this.getRegionFormGroup(i, j);

          // Validar região
          const region = regionGroup.get('region');
          if (!region?.value) {
            isValid = false;
            messages.push(`Região de aplicação é obrigatória no Produto ${i + 1}`);
            if (!firstInvalidElement) {
              firstInvalidElement = document.getElementById(`product-${i}-region-${j}`);
            }
          } else if (region.value === 'Outra') {
            const customRegion = regionGroup.get('custom_region');
            if (!customRegion?.value) {
              isValid = false;
              messages.push(`É necessário especificar a região personalizada no Produto ${i + 1}`);
              if (!firstInvalidElement) {
                firstInvalidElement = document.getElementById(`product-${i}-custom-region-${j}`);
              }
            }
          }
        }
      }

      // Validar se há pelo menos uma foto do produto
      const hasAnyPhoto = this.hasProductPhoto(productGroup.value, 'bottle') ||
        this.hasProductPhoto(productGroup.value, 'box') ||
        this.hasProductPhoto(productGroup.value, 'label');

      if (!hasAnyPhoto) {
        isValid = false;
        messages.push(`É necessário adicionar pelo menos uma foto do Produto ${i + 1}`);
        if (!firstInvalidElement) {
          firstInvalidElement = document.getElementById(`product-${i}-photo-bottle`);
        }
      }
    }

    // Focar no primeiro campo inválido se houver
    if (firstInvalidElement) {
      setTimeout(() => {
        firstInvalidElement?.focus();
        firstInvalidElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    return { isValid, messages };
  }

  // Validação específica da aba de fotos
  validatePhotosTabFields(): { isValid: boolean; messages: string[] } {
    // Log removido para reduzir ruído

    let isValid = true;
    const messages: string[] = [];

    // Para a aba de fotos, não há campos obrigatórios para salvamento
    // As fotos de antes e depois só são obrigatórias para encerramento
    // Aqui podemos adicionar validações específicas se necessário no futuro

    return { isValid, messages };
  }

  // Método para marcar campos da aba info como tocados
  markInfoFieldsAsTouched(): void {
    const patientAge = this.treatmentPlanForm.get('patient_age');
    if (patientAge) {
      patientAge.markAsTouched();
    }
  }

  // Método para marcar campos da aba produtos como tocados
  markProductsFieldsAsTouched(): void {
    for (let i = 0; i < this.productsArray.length; i++) {
      const productGroup = this.getProductFormGroup(i);

      // Marcar campos básicos do produto como tocados
      const basicFields = ['application_date', 'manufacturer', 'product_name'];
      basicFields.forEach(fieldName => {
        const control = productGroup.get(fieldName);
        if (control) {
          control.markAsTouched();
        }
      });

      // Marcar campos das regiões como tocados
      const regionsArray = this.getRegionsArray(i);
      for (let j = 0; j < regionsArray.length; j++) {
        const regionGroup = this.getRegionFormGroup(i, j);
        const regionFields = ['region', 'custom_region'];
        regionFields.forEach(fieldName => {
          const control = regionGroup.get(fieldName);
          if (control) {
            control.markAsTouched();
          }
        });
      }
    }
  }

  // Método para exibir mensagem de validação (não como erro crítico)
  showValidationMessage(message: string): void {
    // Usar console.warn ao invés de console.error para não afetar métricas de erro
    console.warn('Validação de formulário:', message);

    this.messageType = 'error';
    this.messageText = message;
    this.showMessage = true;

    // Limpar qualquer timeout existente
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    // Esconder a mensagem após 5 segundos
    this.messageTimeout = setTimeout(() => {
      this.showMessage = false;
    }, 5000);
  }

  // Modificar o método startCamera para usar a nova implementação de câmera
  async startCamera(type: 'treatment' | 'product'): Promise<void> {

    // Usar a nova implementação de câmera
    this.useDesktopCamera(type);
  }



  // Função auxiliar para melhorar a legibilidade
  private getPhotoTypeDisplayName(photoType: string): string {
    const typeMap: { [key: string]: string } = {
      'bottle': 'Frasco',
      'box': 'Caixa',
      'label': 'Etiqueta'
    };
    return typeMap[photoType] || photoType;
  }

  onProductNameChange(productIndex: number): void {

    // Obter o produto selecionado
    const productNameControl = this.getProductFormGroup(productIndex).get('product_name');
    if (!productNameControl) {
      console.error('Controle de nome do produto não encontrado para o produto:', productIndex);
      return;
    }

    const selectedProductName = productNameControl.value;

    // Gerenciar validadores do campo custom_product_name
    const customProductNameControl = this.getProductFormGroup(productIndex).get('custom_product_name');
    if (customProductNameControl) {
      if (selectedProductName === 'Outro') {
        customProductNameControl.setValidators([Validators.required]);
      } else {
        customProductNameControl.clearValidators();
        customProductNameControl.setValue('');
      }
      customProductNameControl.updateValueAndValidity();
    }

    // Marcar que há alterações não salvas
    this.hasUnsavedChanges = true;

    // Forçar detecção de mudanças para atualizar a view
    this.cdr.detectChanges();

    // Autosave após seleção de produto (curto)
    this.requestSave(`product_name_change:${productIndex}`, true, 1200);
  }

  // Funções para controlar o dropdown customizado de fabricantes
  openCloseManufacturerSelect(productIndex: number): void {
    const selectId = `manufacturer-${productIndex}`;
    const dropdownButton = document.getElementById(`dropdown-button-${selectId}`);
    const dropdownMenu = document.getElementById(`dropdown-menu-${selectId}`);
    const searchInput = document.getElementById(`search-input-${selectId}`) as HTMLInputElement;
    const background = document.getElementById(`dropdown-background-${selectId}`);

    // Fechar outros dropdowns abertos
    this.openedManufacturerDropdowns.forEach((openedSelectId) => {
      if (openedSelectId !== selectId) {
        const openedDropdownMenu = document.getElementById(`dropdown-menu-${openedSelectId}`);
        const openedBackground = document.getElementById(`dropdown-background-${openedSelectId}`);
        openedDropdownMenu?.classList.add('hidden');
        openedBackground?.classList.add('hidden');
      }
    });

    if (dropdownMenu?.classList.contains('hidden')) {
      // Abrir dropdown
      dropdownMenu?.classList.remove('hidden');
      background?.classList.remove('hidden');
      searchInput?.focus();

      // Limpar busca anterior
      if (searchInput) {
        searchInput.value = '';
        this.onManufacturerSearch({ target: searchInput } as any, selectId);
      }

      this.openedManufacturerDropdowns.push(selectId);
    } else {
      // Fechar dropdown
      dropdownMenu?.classList.add('hidden');
      background?.classList.add('hidden');

      // Marcar como tocado ao fechar para exibir validação
      const manufacturerControl = this.getProductFormGroup(productIndex).get('manufacturer');
      if (manufacturerControl) {
        manufacturerControl.markAsTouched();
      }

      // Remover da lista de abertos
      this.openedManufacturerDropdowns = this.openedManufacturerDropdowns.filter(id => id !== selectId);
    }
  }

  onManufacturerSearch(event: Event, selectId: string): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const optionsList = document.getElementById(`options-list-${selectId}`);
    const items = optionsList?.querySelectorAll('.manufacturer-option') || [];

    items.forEach(item => {
      const labelElement = item.querySelector('a');
      if (labelElement) {
        const label = labelElement.textContent?.toLowerCase() || '';
        if (label.includes(searchTerm)) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      }
    });
  }

  onManufacturerSelect(manufacturer: string, productIndex: number): void {
    // Limpar destaque de validação ao interagir com o campo
    this.clearCurrentHighlight();

    // Atualizar o valor do formulário
    const manufacturerControl = this.getProductFormGroup(productIndex).get('manufacturer');
    if (manufacturerControl) {
      manufacturerControl.setValue(manufacturer);
    }

    // Fechar o dropdown
    this.openCloseManufacturerSelect(productIndex);

    // Chamar a função existente para processar a mudança
    this.onManufacturerChange(productIndex);

    // Autosave curto após seleção via dropdown customizado
    this.requestSave(`manufacturer_select:${productIndex}`, true, 1200);
  }

  getSelectedManufacturerLabel(productIndex: number): string {
    const manufacturerControl = this.getProductFormGroup(productIndex).get('manufacturer');
    const value = manufacturerControl?.value;

    if (!value) {
      return 'Selecione um fabricante';
    }

    return value;
  }

  // Funções para controlar o dropdown customizado de unidade
  openCloseUnitSelect(productIndex: number): void {
    const selectId = `unit-${productIndex}`;
    const dropdownMenu = document.getElementById(`dropdown-menu-${selectId}`);
    const background = document.getElementById(`dropdown-background-${selectId}`);

    // Fechar outros dropdowns abertos
    this.openedUnitDropdowns.forEach((openedSelectId) => {
      if (openedSelectId !== selectId) {
        const openedDropdownMenu = document.getElementById(`dropdown-menu-${openedSelectId}`);
        const openedBackground = document.getElementById(`dropdown-background-${openedSelectId}`);
        openedDropdownMenu?.classList.add('hidden');
        openedBackground?.classList.add('hidden');
      }
    });

    if (dropdownMenu?.classList.contains('hidden')) {
      dropdownMenu?.classList.remove('hidden');
      background?.classList.remove('hidden');
      this.openedUnitDropdowns.push(selectId);
    } else {
      dropdownMenu?.classList.add('hidden');
      background?.classList.add('hidden');
      this.openedUnitDropdowns = this.openedUnitDropdowns.filter(id => id !== selectId);
    }
  }

  onUnitSelect(unit: string, productIndex: number): void {
    const unitControl = this.getProductFormGroup(productIndex).get('total_unit');
    if (unitControl) {
      unitControl.setValue(unit);
    }
    this.openCloseUnitSelect(productIndex);
    // Reformat the quantity according to the new unit and then trigger blur/save
    this.formatTotalQuantity(productIndex);
    this.onFieldBlur('total_unit');
    this.requestSave(`unit_select:${productIndex}`, true, 1200);
  }

  getSelectedUnitLabel(productIndex: number): string {
    const unitControl = this.getProductFormGroup(productIndex).get('total_unit');
    const value = unitControl?.value;
    if (!value) {
      return 'ml';
    }
    return value;
  }

  /**
   * Aplica máscara de quantidade no formato xx.x (da direita para a esquerda).
   * Ex.: "5" → "0.5", "45" → "4.5", "345" → "34.5"
   */
  private applyQuantityMask(digits: string): string {
    if (digits.length === 0) return '';
    // Pad with leading zero if only one digit (e.g. "5" → "05")
    const padded = digits.padStart(2, '0');
    const intPart = padded.slice(0, padded.length - 1);
    const decPart = padded.slice(padded.length - 1);
    // Remove unnecessary leading zeros from integer part (but keep at least "0")
    const intClean = parseInt(intPart, 10).toString();
    return `${intClean}.${decPart}`;
  }

  /**
   * Formata o valor do controle total_quantity para o índice de produto informado.
   * Garante que o valor armazenado sempre tenha exatamente 1 casa decimal (máscara xx.x).
   */
  formatTotalQuantity(productIndex: number): void {
    const fg = this.getProductFormGroup(productIndex);
    if (!fg) return;
    const qtyCtrl = fg.get('total_quantity');
    if (!qtyCtrl) return;

    const raw = qtyCtrl.value;
    if (raw === null || raw === undefined || raw === '') return;

    const rawStr = String(raw).trim();
    if (rawStr.length === 0) return;

    let formatted: string;

    // If the value is already a valid number (e.g. loaded from backend as 10
    // or a previously masked string like "10.0" / "34.5"), format it directly
    // with exactly 1 decimal place. We must NOT run it through the
    // right-to-left digit mask, because that mask is only meaningful while the
    // user is typing — reapplying it to "10" would yield "1.0" (bug).
    const numericMatch = /^-?\d+(?:[.,]\d+)?$/.test(rawStr);
    if (numericMatch) {
      const n = Number(rawStr.replace(',', '.'));
      if (!Number.isFinite(n)) return;
      formatted = (Math.round(n * 10) / 10).toFixed(1);
    } else {
      // Fallback: extract only digits and apply right-to-left mask
      const digits = rawStr.replace(/[^0-9]/g, '');
      if (digits.length === 0) return;
      formatted = this.applyQuantityMask(digits);
    }

    if (rawStr !== formatted) {
      qtyCtrl.setValue(formatted);
    }
  }

  /**
   * Wrapper utilizado pelos templates: formata o valor e chama onFieldBlur genérico.
   */
  onTotalQuantityBlur(productIndex: number): void {
    this.formatTotalQuantity(productIndex);
    this.onFieldBlur('total_quantity');
  }

  /**
   * Handler de input com máscara da direita para a esquerda (máscara: xx.x).
   * O usuário digita e os dígitos preenchem a partir da casa decimal para cima:
   *   digita "5"   → "0.5"
   *   digita "4"   → "4.5"
   *   digita "3"   → "34.5"
   *   backspace     → "4.5" → "0.5" → ""
   */
  onTotalQuantityInput(event: Event, productIndex: number): void {
    const input = event.target as HTMLInputElement;

    // Remove tudo exceto dígitos
    const digits = input.value.replace(/[^0-9]/g, '');

    if (digits.length === 0) {
      input.value = '';
      return;
    }

    const formatted = this.applyQuantityMask(digits);
    input.value = formatted;

    // Sincroniza o valor formatado com o FormControl
    const fg = this.getProductFormGroup(productIndex);
    const qtyCtrl = fg?.get('total_quantity');
    if (qtyCtrl) {
      qtyCtrl.setValue(formatted, { emitEvent: false });
    }

    // Posiciona o cursor no final
    setTimeout(() => {
      input.setSelectionRange(formatted.length, formatted.length);
    });
  }

  // Funções para controlar o dropdown customizado de produtos
  openCloseProductSelect(productIndex: number): void {
    // Verificar se o select deve estar habilitado
    if (!this.isProductSelectEnabled(productIndex)) {
      return;
    }

    const selectId = `product-${productIndex}`;
    const dropdownButton = document.getElementById(`dropdown-button-${selectId}`);
    const dropdownMenu = document.getElementById(`dropdown-menu-${selectId}`);
    const searchInput = document.getElementById(`search-input-${selectId}`) as HTMLInputElement;
    const background = document.getElementById(`dropdown-background-${selectId}`);

    // Fechar outros dropdowns abertos
    this.openedProductDropdowns.forEach((openedSelectId) => {
      if (openedSelectId !== selectId) {
        const openedDropdownMenu = document.getElementById(`dropdown-menu-${openedSelectId}`);
        const openedBackground = document.getElementById(`dropdown-background-${openedSelectId}`);
        openedDropdownMenu?.classList.add('hidden');
        openedBackground?.classList.add('hidden');
      }
    });

    if (dropdownMenu?.classList.contains('hidden')) {
      // Abrir dropdown
      dropdownMenu?.classList.remove('hidden');
      background?.classList.remove('hidden');
      searchInput?.focus();

      // Limpar busca anterior
      if (searchInput) {
        searchInput.value = '';
        this.onProductSearch({ target: searchInput } as any, selectId);
      }

      this.openedProductDropdowns.push(selectId);
    } else {
      // Fechar dropdown
      dropdownMenu?.classList.add('hidden');
      background?.classList.add('hidden');

      // Marcar como tocado ao fechar para exibir validação
      const productControl = this.getProductFormGroup(productIndex).get('product_name');
      if (productControl) {
        productControl.markAsTouched();
      }

      // Remover da lista de abertos
      this.openedProductDropdowns = this.openedProductDropdowns.filter(id => id !== selectId);
    }
  }

  onProductSearch(event: Event, selectId: string): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const optionsList = document.getElementById(`options-list-${selectId}`);
    const items = optionsList?.querySelectorAll('.product-option') || [];

    items.forEach(item => {
      const labelElement = item.querySelector('a');
      if (labelElement) {
        const label = labelElement.textContent?.toLowerCase() || '';
        if (label.includes(searchTerm)) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      }
    });
  }

  onProductSelect(product: string, productIndex: number): void {
    // Limpar destaque de validação ao interagir com o campo
    this.clearCurrentHighlight();

    // Atualizar o valor do formulário
    const productControl = this.getProductFormGroup(productIndex).get('product_name');
    if (productControl) {
      productControl.setValue(product);
    }

    // Fechar o dropdown
    this.openCloseProductSelect(productIndex);

    // Chamar a função existente para processar a mudança
    this.onProductNameChange(productIndex);

    // Autosave curto após seleção via dropdown customizado
    this.requestSave(`product_select:${productIndex}`, true, 1200);
  }

  getSelectedProductLabel(productIndex: number): string {
    const productControl = this.getProductFormGroup(productIndex).get('product_name');
    const value = productControl?.value;

    if (!value) {
      return 'Selecione um produto';
    }

    return value;
  }

  // Método para verificar se o select de produto deve estar habilitado
  isProductSelectEnabled(productIndex: number): boolean {
    if (this.treatmentPlan.status === 'CLOSED') {
      return false;
    }

    // Verificar se há um fabricante selecionado
    const manufacturerControl = this.getProductFormGroup(productIndex).get('manufacturer');
    const manufacturer = manufacturerControl?.value;

    // Habilitado apenas se há fabricante selecionado e não está vazio
    return !!(manufacturer && manufacturer.trim() !== '');
  }

  // Funções para controlar o dropdown customizado de regiões
  openCloseRegionSelect(productIndex: number, regionIndex: number): void {
    const selectId = `region-${productIndex}-${regionIndex}`;

    // Buscar os elementos do dropdown (agora com IDs únicos)
    const dropdownButton = document.getElementById(`dropdown-button-${selectId}`);
    const dropdownMenu = document.getElementById(`dropdown-menu-${selectId}`);
    const searchInput = document.getElementById(`search-input-${selectId}`) as HTMLInputElement;
    const background = document.getElementById(`dropdown-background-${selectId}`);


    // Fechar outros dropdowns abertos
    this.openedRegionDropdowns.forEach((openedSelectId) => {
      if (openedSelectId !== selectId) {
        const openedDropdownMenu = document.getElementById(`dropdown-menu-${openedSelectId}`);
        const openedBackground = document.getElementById(`dropdown-background-${openedSelectId}`);
        openedDropdownMenu?.classList.add('hidden');
        openedBackground?.classList.add('hidden');
      }
    });

    if (dropdownMenu?.classList.contains('hidden')) {
      // Abrir dropdown
      dropdownMenu?.classList.remove('hidden');
      background?.classList.remove('hidden');
      searchInput?.focus();

      // Limpar busca anterior
      if (searchInput) {
        searchInput.value = '';
        this.onRegionSearch({ target: searchInput } as any, selectId);
      }

      this.openedRegionDropdowns.push(selectId);
    } else {
      // Fechar dropdown
      dropdownMenu?.classList.add('hidden');
      background?.classList.add('hidden');

      // Remover da lista de abertos
      this.openedRegionDropdowns = this.openedRegionDropdowns.filter(id => id !== selectId);
    }
  }

  onRegionSearch(event: Event, selectId: string): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const optionsList = document.getElementById(`options-list-${selectId}`);
    const items = optionsList?.querySelectorAll('.region-option') || [];

    items.forEach(item => {
      const labelElement = item.querySelector('a');
      if (labelElement) {
        const label = labelElement.textContent?.toLowerCase() || '';
        if (label.includes(searchTerm)) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      }
    });
  }

  onRegionSelect(region: string, productIndex: number, regionIndex: number): void {
    // Atualizar o valor do formulário
    const regionFormGroup = this.getRegionFormGroup(productIndex, regionIndex);
    const regionControl = regionFormGroup.get('region');
    if (regionControl) {
      regionControl.setValue(region);
    }

    // Fechar o dropdown
    this.openCloseRegionSelect(productIndex, regionIndex);

    // Chamar a função existente para processar a mudança
    this.onRegionSelectionChange(productIndex, regionIndex);

    // Autosave curto após seleção via dropdown customizado
    this.requestSave(`region_select:${productIndex}:${regionIndex}`, true, 1200);
  }

  getSelectedRegionLabel(productIndex: number, regionIndex: number): string {
    const regionFormGroup = this.getRegionFormGroup(productIndex, regionIndex);
    const regionControl = regionFormGroup.get('region');
    const value = regionControl?.value;

    if (!value) {
      return 'Selecionar região';
    }

    return value;
  }

  // Lista de regiões padronizadas (todas, para compatibilidade)
  getStandardRegions(): string[] {
    return [...this.getFacialRegions(), ...this.getCorporalRegions()];
  }

  /**
   * Carrega regiões da API e SÓ ENTÃO carrega o plano de tratamento.
   * Evita race condition em que regiões padrão eram classificadas como "Outra".
   */
  private async loadApplicationRegionsThenPlan(): Promise<void> {
    try {
      const categories = await firstValueFrom(this.productService.getApplicationRegions());
      this.applyApplicationRegionCategories(categories);
    } catch (err) {
      console.error('Erro ao carregar regiões de aplicação:', err);
      this.handleApplicationRegionsLoadFailure(err);
    }
    this.loadTreatmentPlan();
  }

  /**
   * Carrega as regiões de aplicação da API (versão fire-and-forget para casos sem plano)
   */
  loadApplicationRegions(): void {
    this.productService.getApplicationRegions().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (categories) => {
        this.applyApplicationRegionCategories(categories);
      },
      error: (err: any) => {
        console.error('Erro ao carregar regiões de aplicação:', err);
        this.handleApplicationRegionsLoadFailure(err);
      }
    });
  }

  /**
   * Centraliza a aplicação do resultado — fácil de garantir que SEMPRE validamos
   * que a lista veio preenchida. Antes, categorias vazias eram aceitas silenciosamente
   * e o usuário via popup de Regiões em branco sem saber o motivo.
   */
  private applyApplicationRegionCategories(categories: IApplicationRegionCategory[] | null): void {
    if (!categories || categories.length === 0) {
      console.warn('[REGIÕES] API retornou lista vazia de regiões — UI ficará sem opções padrão.');
      this.showError('Lista de regiões de aplicação indisponível. Adicione regiões como personalizadas ou recarregue a página.');
    }
    this.applicationRegionCategories = categories ?? [];
    const facial = this.applicationRegionCategories.find(c => c.category === 'Facial');
    const corporal = this.applicationRegionCategories.find(c => c.category === 'Corporal');
    this.facialRegionsFromApi = facial ? facial.regions.map(r => r.value) : [];
    this.corporalRegionsFromApi = corporal ? corporal.regions.map(r => r.value) : [];
    this.regionsLoaded = true;

    this.refreshIncidentRegionSelectorConfig();
  }

  /**
   * Tratamento unificado de falha: mantém arrays vazios, libera flag de loading
   * e NOTIFICA o usuário — não mais silencioso. Sem isso, a UI ficava parecendo
   * funcional mas sem regiões disponíveis, e o usuário não entendia o problema.
   */
  private handleApplicationRegionsLoadFailure(err: unknown): void {
    this.applicationRegionCategories = [];
    this.facialRegionsFromApi = [];
    this.corporalRegionsFromApi = [];
    this.regionsLoaded = true;
    this.showError('Não foi possível carregar as regiões de aplicação. Verifique sua conexão e recarregue a página.');
  }

  // Regiões faciais (carregadas da API)
  getFacialRegions(): string[] {
    return this.facialRegionsFromApi;
  }

  // Regiões corporais (carregadas da API)
  getCorporalRegions(): string[] {
    return this.corporalRegionsFromApi;
  }

  /**
   * Retorna regiões filtradas pelo termo de busca para uma categoria específica
   */
  getFilteredRegionsByCategory(productIndex: number, category: string): string[] {
    const filter = (this.regionSearchFilters[productIndex] || '').toLowerCase().trim();
    const cat = this.applicationRegionCategories.find(c => c.category === category);
    const regions = cat ? cat.regions.map(r => r.value) : [];
    if (!filter) return regions;
    return regions.filter(r => r.toLowerCase().includes(filter));
  }

  /**
   * Retorna regiões faciais filtradas pelo termo de busca
   */
  getFilteredFacialRegions(productIndex: number): string[] {
    const filter = (this.regionSearchFilters[productIndex] || '').toLowerCase().trim();
    if (!filter) return this.getFacialRegions();
    return this.getFacialRegions().filter(r => r.toLowerCase().includes(filter));
  }

  /**
   * Retorna regiões corporais filtradas pelo termo de busca
   */
  getFilteredCorporalRegions(productIndex: number): string[] {
    const filter = (this.regionSearchFilters[productIndex] || '').toLowerCase().trim();
    if (!filter) return this.getCorporalRegions();
    return this.getCorporalRegions().filter(r => r.toLowerCase().includes(filter));
  }

  // ============================================================
  // Métodos para checkbox multi-select de regiões
  // ============================================================

  /**
   * Abre/fecha o painel de seleção de regiões de um produto
   */
  toggleRegionPanel(productIndex: number): void {
    if (this.treatmentPlan.status === 'CLOSED') return;
    this.regionPanelOpen[productIndex] = !this.regionPanelOpen[productIndex];
    // Limpar filtro ao abrir
    if (this.regionPanelOpen[productIndex]) {
      this.regionSearchFilters[productIndex] = '';
    }
  }

  /**
   * Abre o modal de seleção de regiões (v2)
   */
  openRegionModal(productIndex: number): void {
    if (this.treatmentPlan.status === 'CLOSED') return;
    this.regionModalProductIndex = productIndex;
    this.regionModalOpen[productIndex] = true;
    this.regionSearchFilters[productIndex] = '';
    document.body.style.overflow = 'hidden';
  }

  /**
   * Fecha o modal de seleção de regiões (v2)
   */
  closeRegionModal(productIndex: number): void {
    this.regionModalOpen[productIndex] = false;
    this.regionSearchFilters[productIndex] = '';
    document.body.style.overflow = '';
  }

  /**
   * Verifica se uma região padrão está selecionada para um produto
   */
  isRegionSelected(productIndex: number, regionName: string): boolean {
    const regionsArray = this.getRegionsArray(productIndex);
    for (let j = 0; j < regionsArray.length; j++) {
      const regionGroup = regionsArray.at(j) as FormGroup;
      if (regionGroup.get('region')?.value === regionName) {
        return true;
      }
    }
    return false;
  }

  /**
   * Toggle de uma região padrão (adiciona ou remove da FormArray)
   */
  toggleRegionCheckbox(productIndex: number, regionName: string): void {
    if (this.treatmentPlan.status === 'CLOSED') return;

    // Limpar destaque de validação ao interagir com regiões
    this.clearCurrentHighlight();

    const regionsArray = this.getRegionsArray(productIndex);

    // Verificar se já está selecionada
    const existingIndex = this.findRegionIndex(productIndex, regionName);

    if (existingIndex >= 0) {
      // Remover
      regionsArray.removeAt(existingIndex);
    } else {
      // Adicionar
      regionsArray.push(this.fb.group({
        id: [''],
        region: [regionName, Validators.required],
        custom_region: ['']
      }));
    }

    // Marcar alterações
    this.hasUnsavedChanges = true;
    this.updateCanCloseTreatmentPlan();
    this.cdr.detectChanges();
    this.requestSave(`region_toggle:${productIndex}:${regionName}`, true, 1200);
  }

  /**
   * Encontra o índice de uma região pelo nome na FormArray
   */
  private findRegionIndex(productIndex: number, regionName: string): number {
    const regionsArray = this.getRegionsArray(productIndex);
    for (let j = 0; j < regionsArray.length; j++) {
      const regionGroup = regionsArray.at(j) as FormGroup;
      if (regionGroup.get('region')?.value === regionName) {
        return j;
      }
    }
    return -1;
  }

  /**
   * Retorna a lista de regiões padrão filtradas pelo termo de busca
   */
  getFilteredStandardRegions(productIndex: number): string[] {
    const filter = (this.regionSearchFilters[productIndex] || '').toLowerCase().trim();
    if (!filter) return this.getStandardRegions();
    return this.getStandardRegions().filter(r => r.toLowerCase().includes(filter));
  }

  /**
   * Callback de filtro de busca de regiões
   */
  onRegionFilterChange(productIndex: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.regionSearchFilters[productIndex] = input.value;
  }

  /**
   * Retorna labels das regiões selecionadas (padrão + customizadas)
   */
  getSelectedRegionsList(productIndex: number): { label: string; index: number; isCustom: boolean }[] {
    const regionsArray = this.getRegionsArray(productIndex);
    const result: { label: string; index: number; isCustom: boolean }[] = [];
    for (let j = 0; j < regionsArray.length; j++) {
      const regionGroup = regionsArray.at(j) as FormGroup;
      const regionValue = regionGroup.get('region')?.value;
      const customValue = regionGroup.get('custom_region')?.value;
      if (regionValue === 'Outra') {
        result.push({ label: customValue || 'Região personalizada', index: j, isCustom: true });
      } else if (regionValue) {
        result.push({ label: regionValue, index: j, isCustom: false });
      }
    }
    return result;
  }

  /**
   * Retorna apenas as regiões personalizadas ("Outra") já adicionadas, com seu índice
   * no FormArray, para listagem/remoção dentro do popup.
   */
  getCustomRegionsList(productIndex: number): { label: string; index: number; isCustom: boolean }[] {
    return this.getSelectedRegionsList(productIndex).filter(r => r.isCustom);
  }

  /**
   * Texto concatenado das regiões selecionadas
   */
  getSelectedRegionsText(productIndex: number): string {
    const list = this.getSelectedRegionsList(productIndex);
    if (list.length === 0) return '';
    if (list.length === 1) return list[0].label;
    const labels = list.map(r => r.label);
    return labels.slice(0, -1).join(', ') + ' e ' + labels[labels.length - 1];
  }

  /**
   * Validator que rejeita strings vazias mesmo quando contêm só espaços.
   * `Validators.required` considera "   " como preenchido — aqui exigimos
   * pelo menos 1 caractere não-whitespace. Use junto (ou em vez) de required
   * em campos de texto livre como `custom_region`.
   */
  private nonBlankValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined) return { required: true };
    if (typeof value !== 'string') return null;
    return value.trim().length === 0 ? { required: true } : null;
  }

  /**
   * Retorna a "chave" normalizada de uma região para comparação de duplicatas:
   * trim + lowercase + collapse de whitespace interno. Assim "Braço",
   * "  braço  " e "BRAÇO" geram a mesma chave e são tratados como a mesma região.
   */
  private normalizeRegionKey(name: string | null | undefined): string {
    if (!name) return '';
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Verifica se existe foto de tratamento vinculada a uma região específica.
   * Hoje o front NÃO persiste `applicationRegion` nas fotos (gap reportado no
   * review), então por ora o método procura por menção textual da região no
   * campo `legend` das fotos. Quando o backend passar a persistir a FK de
   * região, refatorar para comparar por ID.
   */
  private hasTreatmentPhotosForRegion(productId: string | undefined, regionLabel: string): boolean {
    if (!this.treatmentPlan?.photos || !regionLabel) return false;
    const key = this.normalizeRegionKey(regionLabel);
    return this.treatmentPlan.photos.some(photo => {
      const legend = (photo as any).legend ?? (photo as any).caption ?? '';
      return legend && this.normalizeRegionKey(String(legend)).includes(key);
    });
  }

  /**
   * Remove uma região pelo índice (usado no chip X).
   *
   * <p>Antes da correção, remover uma região não checava se havia fotos de
   * tratamento vinculadas, deixando fotos órfãs sem contexto de região.
   * Agora pedimos confirmação explícita quando há vínculo.
   */
  removeRegionByIndex(productIndex: number, regionIndex: number): void {
    if (this.treatmentPlan.status === 'CLOSED') return;
    this.clearCurrentHighlight();
    const regions = this.getRegionsArray(productIndex);
    if (regionIndex >= 0 && regionIndex < regions.length) {
      // Identifica o rótulo humano da região para mostrar no confirm.
      const regionGroup = regions.at(regionIndex);
      const rawLabel = regionGroup?.get('region')?.value === 'Outra'
        ? regionGroup?.get('custom_region')?.value
        : regionGroup?.get('region')?.value;
      const label = String(rawLabel ?? '').trim();

      const productId = this.treatmentPlan.products?.[productIndex]?.id;
      if (label && this.hasTreatmentPhotosForRegion(productId, label)) {
        const confirmed = window.confirm(
          `Existem fotos de tratamento associadas à região "${label}". Remover a região fará com que essas fotos fiquem sem contexto. Deseja continuar?`
        );
        if (!confirmed) {
          return;
        }
      }

      regions.removeAt(regionIndex);
      this.hasUnsavedChanges = true;
      this.updateCanCloseTreatmentPlan();
      this.cdr.detectChanges();
      this.requestSave(`region_chip_removed:${productIndex}:${regionIndex}`, true, 1200);
    }
  }

  /**
   * Adiciona uma região personalizada ("Outra") com dedupe case-insensitive
   * e whitespace-normalizado. Antes, "Braço", "braço" e "Braço " eram
   * tratados como regiões distintas — gerando duplicatas no banco, relatórios
   * inconsistentes e UX confusa.
   */
  addCustomRegion(productIndex: number): void {
    if (this.treatmentPlan.status === 'CLOSED') return;
    this.clearCurrentHighlight();

    const rawInput = this.customRegionInput[productIndex] || '';
    const customValue = rawInput.trim().replace(/\s+/g, ' ');
    if (!customValue) return;

    const normalizedKey = this.normalizeRegionKey(customValue);
    const regionsArray = this.getRegionsArray(productIndex);

    // Dedup: comparar tanto contra regiões padrão quanto contra outras "Outra" já adicionadas.
    const existing = regionsArray.controls.some(ctrl => {
      const region = ctrl.get('region')?.value;
      const custom = ctrl.get('custom_region')?.value;
      const compareAgainst = region === 'Outra' ? custom : region;
      return this.normalizeRegionKey(compareAgainst) === normalizedKey;
    });

    if (existing) {
      this.showError('Esta região já foi adicionada ao produto.');
      // Limpa o input mesmo assim para não ficar em loop
      this.customRegionInput[productIndex] = '';
      return;
    }

    regionsArray.push(this.fb.group({
      id: [''],
      region: ['Outra', Validators.required],
      // Validator composto: required tradicional + nonBlankValidator para bloquear "   ".
      custom_region: [customValue, [Validators.required, this.nonBlankValidator.bind(this)]]
    }));

    // Limpar input
    this.customRegionInput[productIndex] = '';

    this.hasUnsavedChanges = true;
    this.updateCanCloseTreatmentPlan();
    this.cdr.detectChanges();
    this.requestSave(`custom_region_added:${productIndex}`, true, 1200);
  }

  /**
   * Reindexa estados locais (como {@link #customRegionInput}) após a remoção
   * do produto de índice `removedIndex`. Todos os índices > removedIndex são
   * deslocados uma posição para baixo; a última chave é descartada.
   *
   * <p>Motivação: o FormArray `productsArray` faz shift automaticamente no
   * `removeAt`, mas estados locais em objetos `{ [index: number]: ... }` NÃO.
   * Sem isso, após deletar o produto 1, a entrada 2 do array passa a ocupar
   * a posição 1 no FormArray, mas o valor em `customRegionInput[1]` continua
   * sendo a digitação abandonada do produto antigo de índice 1.
   */
  private reindexProductLocalState(removedIndex: number): void {
    const newCustomRegion: { [productIndex: number]: string } = {};
    for (const key of Object.keys(this.customRegionInput)) {
      const idx = Number(key);
      if (!Number.isFinite(idx)) continue;
      if (idx < removedIndex) {
        newCustomRegion[idx] = this.customRegionInput[idx];
      } else if (idx > removedIndex) {
        // desloca para baixo
        newCustomRegion[idx - 1] = this.customRegionInput[idx];
      }
      // idx === removedIndex: descarta (era o próprio produto deletado)
    }
    this.customRegionInput = newCustomRegion;
  }

  /**
   * Salva as fotos de um produto específico usando o endpoint uploadProductPhotos
   * @param productId ID do produto
   * @param reason Razão do salvamento para logs
   */
  private async saveProductPhotos(productId: string, photosToUpload: any[], reason: string): Promise<void> {
    try {

      // O array de fotos agora é recebido diretamente como parâmetro
      if (!photosToUpload) {
        console.error(`Array de fotos para o produto ${productId} é nulo ou indefinido.`);
        return;
      }


      // Preparar o payload (se o backend espera um formato específico)
      const payload = photosToUpload.map(photo => ({
        photo: photo.photo,
        photo_order: photo.photo_order
      }));

      // Chamar o endpoint específico para fotos de produtos
      await this.treatmentPlanService.uploadProductPhotos(
        this.treatmentPlan.id,
        productId,
        payload // Enviando o payload preparado
      );


      // Marcar que não há mais alterações não salvas
      this.hasUnsavedChanges = false;
      this.updateCanCloseTreatmentPlan();

    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        return;
      }
      console.error(`❌ [SAVE_PRODUCT_PHOTOS] Erro ao salvar fotos do produto ${productId}:`, error);
    }
  }
}

